/* Label & Leaf — personalised story book studio.
   Photo-only: the customer uploads their child's photo. Until they do, the
   live preview shows a per-story demo character (the first sample's pose
   cuts, badged "Sample preview") so the book never opens empty; the demo is
   not a choice and can never be ordered. Every upload is checked in the
   browser first (ll-photo-check.js: a face is present, large enough, in
   frame) before it counts as chosen. Generated character poses are never
   shown before purchase; the original upload remains local to the
   personalisation controls until the customer adds the book. */
(function () {
  'use strict';

  /* Exact Latin glyph coverage carried by the bundled Crimson Pro production
     face. Keep these ranges byte-for-byte aligned with storybook_order.py: an
     unsupported script must be refused before purchase, never silently dropped
     by the compositor after the customer approved a preview. */
  var STORYBOOK_SUPPORTED_LATIN_RANGES = [
    [0x0041, 0x005a], [0x0061, 0x007a], [0x00c0, 0x00d6],
    [0x00d8, 0x00f6], [0x00f8, 0x017e], [0x018f, 0x018f],
    [0x0192, 0x0192], [0x01a0, 0x01a1], [0x01af, 0x01b0],
    [0x01c4, 0x01cc], [0x01cf, 0x01cf], [0x01e6, 0x01e7],
    [0x01ea, 0x01eb], [0x01fa, 0x021b], [0x0226, 0x0227],
    [0x022a, 0x022d], [0x0230, 0x0233], [0x0237, 0x0237],
    [0x0259, 0x0259], [0x1e08, 0x1e09], [0x1e0c, 0x1e0f],
    [0x1e14, 0x1e17], [0x1e1c, 0x1e1d], [0x1e20, 0x1e21],
    [0x1e24, 0x1e25], [0x1e2a, 0x1e2b], [0x1e2e, 0x1e2f],
    [0x1e36, 0x1e37], [0x1e3a, 0x1e3b], [0x1e42, 0x1e49],
    [0x1e4c, 0x1e53], [0x1e5a, 0x1e5b], [0x1e5e, 0x1e69],
    [0x1e6c, 0x1e6f], [0x1e78, 0x1e7b], [0x1e80, 0x1e85],
    [0x1e8e, 0x1e8f], [0x1e92, 0x1e93], [0x1e97, 0x1e97],
    [0x1e9e, 0x1e9e], [0x1ea0, 0x1ef9]
  ];
  var STORYBOOK_SUPPORTED_COMBINING_RANGES = [
    [0x0300, 0x0304], [0x0306, 0x030c], [0x030f, 0x030f],
    [0x0311, 0x0312], [0x031b, 0x031b], [0x0323, 0x0324],
    [0x0326, 0x0328], [0x032e, 0x032e], [0x0331, 0x0331],
    [0x0335, 0x0338]
  ];
  var STORYBOOK_NAME_SEPARATORS = " \u00a0'’ʼ-‐";
  var STORYBOOK_DEDICATION_PUNCTUATION = " \r\n\u00a0'’‘ʼ-‐.,!?;:&()[]/\"“”–—…$`%+@#";
  var STORYBOOK_NAME_GLYPH_ERROR = 'Please use Latin letters, spaces, apostrophes and hyphens for the child’s name.';
  var STORYBOOK_DEDICATION_GLYPH_ERROR = 'Please use Latin letters, numbers and standard punctuation in the dedication.';

  function storybookCodepointInRanges(codepoint, ranges) {
    for (var rangeIndex = 0; rangeIndex < ranges.length; rangeIndex++) {
      if (codepoint >= ranges[rangeIndex][0] && codepoint <= ranges[rangeIndex][1]) return true;
    }
    return false;
  }

  function storybookPersonalisationGlyphError(value, dedication) {
    if (typeof value !== 'string') return dedication ? STORYBOOK_DEDICATION_GLYPH_ERROR : STORYBOOK_NAME_GLYPH_ERROR;
    var combiningBase = false;
    var sawLatin = false;
    var characters = Array.from(value);
    for (var characterIndex = 0; characterIndex < characters.length; characterIndex++) {
      var character = characters[characterIndex];
      var codepoint = character.codePointAt(0);
      if (storybookCodepointInRanges(codepoint, STORYBOOK_SUPPORTED_LATIN_RANGES)) {
        combiningBase = true;
        sawLatin = true;
        continue;
      }
      if (storybookCodepointInRanges(codepoint, STORYBOOK_SUPPORTED_COMBINING_RANGES)) {
        if (!combiningBase) return dedication ? STORYBOOK_DEDICATION_GLYPH_ERROR : STORYBOOK_NAME_GLYPH_ERROR;
        continue;
      }
      combiningBase = false;
      if (STORYBOOK_NAME_SEPARATORS.indexOf(character) >= 0) continue;
      if (dedication && (
        (character >= '0' && character <= '9')
        || STORYBOOK_DEDICATION_PUNCTUATION.indexOf(character) >= 0
      )) continue;
      return dedication ? STORYBOOK_DEDICATION_GLYPH_ERROR : STORYBOOK_NAME_GLYPH_ERROR;
    }
    return dedication || sawLatin ? '' : STORYBOOK_NAME_GLYPH_ERROR;
  }

  function fillStorybookTemplate(template, childName, dedication) {
    return String(template)
      .replace(/\[DEDICATION\]/gi, function () { return dedication; })
      .replace(/\[(?:NAME|CHILD(?:['’]S)?(?: NAME)?)\]/gi, function () { return childName; });
  }

  // Runs are validated by applyStorybookPreview. Text nodes keep names literal,
  // including markup-like input, while print's span presets retain their ink.
  function renderStorybookRuns(target, runs, styles, fill) {
    var doc = target.ownerDocument;
    target.textContent = '';
    runs.forEach(function (line, lineIndex) {
      if (lineIndex) target.appendChild(doc.createTextNode('\n'));
      line.forEach(function (run) {
        var text = fill(run.t);
        var st = run.s ? styles[run.s] : null;
        if (!st) { target.appendChild(doc.createTextNode(text)); return; }
        var span = doc.createElement('span');
        span.style.fontFamily = "'" + st.family + "', Georgia, serif";
        span.style.fontWeight = String(st.weight);
        span.style.fontStyle = st.italic ? 'italic' : 'normal';
        span.style.color = st.color;
        span.style.fontSize = st.scale + 'em';
        span.style.letterSpacing = st.tracking + 'em';
        if (st.stroke) span.style.webkitTextStroke = st.strokeWidth + 'em ' + st.stroke;
        if (st.letterDy || st.climb) {
          Array.from(text).forEach(function (letter, index, letters) {
            var glyph = doc.createElement('span');
            glyph.textContent = letter;
            var dy = st.letterDy && st.letterDy.length ? st.letterDy[index % st.letterDy.length] : 0;
            dy -= st.climb && letters.length > 1 ? st.climb * index / (letters.length - 1) : 0;
            glyph.style.position = 'relative';
            glyph.style.top = dy + 'em';
            span.appendChild(glyph);
          });
        } else span.textContent = text;
        target.appendChild(span);
      });
    });
  }

  function storybookCoverNameInitialScale(kind, enteredName, previewDefaultName) {
    return kind === 'cover_child_name'
      && !enteredName
      && String(previewDefaultName || '').trim() === 'Your child'
      ? 0.55
      : 1;
  }

  function storybookCoverNameLengthScale(kind, value, fullSizeMaxChars) {
    var limit = Number(fullSizeMaxChars);
    if (
      kind !== 'cover_child_name'
      || !Number.isInteger(limit)
      || limit < 1
    ) return 1;
    var name = String(value || '');
    if (String.prototype.normalize) name = name.normalize('NFC');
    var length = Array.from(name).length;
    return length > limit ? limit / length : 1;
  }

  var STORYBOOK_CANVA_WIDTH = 1557;
  var STORYBOOK_CANVA_HEIGHT = 779;

  function storybookTextBoxGeometry(box, style) {
    var paddingCanva = style && typeof style.paddingCanva === 'number'
      ? style.paddingCanva
      : 0;
    var insetX = paddingCanva / STORYBOOK_CANVA_WIDTH * 100;
    var insetY = paddingCanva / STORYBOOK_CANVA_HEIGHT * 100;
    return {
      x: box.x + insetX,
      y: box.y + insetY,
      w: box.w - insetX * 2,
      h: box.h - insetY * 2
    };
  }

  /* Keep the browser-only upload preview aligned with the production
     compositor's Black Oak dedication policy. Coordinates use logical book
     units: a UK spread is 200 × 100 and a US single page is 100 × 100. The
     original image is always contained without cropping; only true panoramas
     or unusually narrow screenshots receive a small mount inside the frame. */
  function storybookDedicationPhotoLayout(layout, sourceWidth, sourceHeight, withStoryRange) {
    var singlePage = layout === 'single';
    var canvasWidth = singlePage ? 100 : 200;
    var available = singlePage && withStoryRange
      ? { left: 25.1, top: 4, right: 74.9, bottom: 36 }
      : singlePage
      ? { left: 25.1, top: 10, right: 74.9, bottom: 46 }
      : { left: 125.2, top: 25.2, right: 174.8, bottom: 74.8 };
    /* The preview's single page is 800 x 806 (ll-storybook.css), so 1% of
       its height is a little more than 1% of its width. Measure heights in
       width units, or a square photo's box comes out 0.75% too tall and a
       cream band shows under it. The spread is exactly 2:1. */
    var heightUnit = singlePage ? 806 / 800 : 1;
    var availableTop = available.top * heightUnit;
    var availableBottom = available.bottom * heightUnit;
    var rail = 1.9;
    var openingMaxWidth = available.right - available.left - rail * 2;
    var openingMaxHeight = availableBottom - availableTop - rail * 2;
    var sourceAspect = Math.max(1, sourceWidth) / Math.max(1, sourceHeight);
    var openingAspect = Math.min(2.4, Math.max(0.42, sourceAspect));
    var openingWidth;
    var openingHeight;
    if (openingMaxWidth / openingMaxHeight > openingAspect) {
      openingHeight = openingMaxHeight;
      openingWidth = openingHeight * openingAspect;
    } else {
      openingWidth = openingMaxWidth;
      openingHeight = openingWidth / openingAspect;
    }
    var outerWidth = openingWidth + rail * 2;
    var outerHeight = openingHeight + rail * 2;
    var outerLeft = available.left
      + (available.right - available.left - outerWidth) / 2;
    var outerTop = availableTop
      + (availableBottom - availableTop - outerHeight) / 2;
    return {
      x: outerLeft / canvasWidth * 100,
      y: outerTop / heightUnit,
      w: outerWidth / canvasWidth * 100,
      h: outerHeight / heightUnit,
      // The rail as CSS padding: a percentage of the frame's width on every
      // side, so the photo sits in the opening exactly as print mounts it.
      rail: rail / canvasWidth * 100,
      // The frame's own box in book units, for storybookBlackOakFrameSvg.
      frame: { width: outerWidth, height: outerHeight, rail: rail },
      fit: 'contain'
    };
  }

  /* Print's Black Oak frame (black_oak_frame_svg, build_storybook_canva_box_
     proof.py), drawn for this photo's own box rather than one square picture
     stretched over it (2026-10-20): four mitred rails of one thickness,
     shaded per side, with grain, and the highlights, outlines and mitre lines
     print's raster adds (_render_black_oak_frame, at its 46 px rail). Units are
     book units, the frame box's own (print draws about 24 px to one), so the
     rails stay even whatever the photo's shape. The grain is seeded like
     print's, though not print's exact lines, and each line is clipped to its
     rail here, so the markup needs no ids and survives being cloned. */
  function storybookBlackOakFrameSvg(width, height, rail) {
    var px = 1 / 24;
    var seed = 58;
    function random() {
      seed = (seed + 0x6D2B79F5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }
    function n(value) { return Math.round(value * 1000) / 1000; }
    var right = width - rail, bottom = height - rail;
    var polygons = [
      ['#35312f', [0, 0, width, 0, right, rail, rail, rail]],
      ['#252220', [width, 0, width, height, right, bottom, right, rail]],
      ['#1e1b1a', [0, height, rail, bottom, right, bottom, width, height]],
      ['#2e2a28', [0, 0, rail, rail, rail, bottom, 0, height]]
    ];
    var count = Math.max(6, Math.floor(rail / px / 2));
    var lines = [];
    // A grain line at this depth into its rail runs between the two mitres.
    function grain(horizontal, start) {
      for (var i = 0; i < count; i++) {
        var depth = random() * rail;
        var wobble = (random() * 4 - 2) * px;
        var strokeWidth = (random() < 2 / 3 ? 1 : 2) * px;
        var at = start + (start ? -depth : depth);
        var inset = depth, length = (horizontal ? width : height) - depth * 2;
        if (length <= 0) continue;
        lines.push(horizontal
          ? [inset, at, inset + length, at + wobble, strokeWidth]
          : [at, inset, at + wobble, inset + length, strokeWidth]);
      }
    }
    grain(true, 0); grain(true, height); grain(false, 0); grain(false, width);
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + n(width) + ' ' + n(height) + '" preserveAspectRatio="none" aria-hidden="true" focusable="false">'
      + polygons.map(function (polygon) {
        var p = polygon[1];
        return '<polygon fill="' + polygon[0] + '" points="' + n(p[0]) + ',' + n(p[1]) + ' ' + n(p[2]) + ',' + n(p[3]) + ' ' + n(p[4]) + ',' + n(p[5]) + ' ' + n(p[6]) + ',' + n(p[7]) + '"/>';
      }).join('')
      + '<g stroke="#100d0b" stroke-opacity="0.46">' + lines.map(function (line) {
        return '<line x1="' + n(line[0]) + '" y1="' + n(line[1]) + '" x2="' + n(line[2]) + '" y2="' + n(line[3]) + '" stroke-width="' + n(line[4]) + '"/>';
      }).join('') + '</g>'
      + '<line x1="0" y1="0" x2="' + n(width) + '" y2="0" stroke="#5c554e" stroke-opacity="0.51" stroke-width="' + n(2 * px) + '"/>'
      + '<line x1="0" y1="0" x2="0" y2="' + n(height) + '" stroke="#5c554e" stroke-opacity="0.37" stroke-width="' + n(2 * px) + '"/>'
      + '<rect x="0" y="0" width="' + n(width) + '" height="' + n(height) + '" fill="none" stroke="#0d0b0a" stroke-opacity="0.86" stroke-width="' + n(3 * px) + '"/>'
      + '<rect x="' + n(rail) + '" y="' + n(rail) + '" width="' + n(width - rail * 2) + '" height="' + n(height - rail * 2) + '" fill="none" stroke="#0d0b0a" stroke-opacity="0.82" stroke-width="' + n(4 * px) + '"/>'
      + '<g stroke="#0d0b0a" stroke-opacity="0.59" stroke-width="' + n(2 * px) + '">'
      + [[0, 0, rail, rail], [width, 0, right, rail], [0, height, rail, bottom], [width, height, right, bottom]].map(function (m) {
        return '<line x1="' + n(m[0]) + '" y1="' + n(m[1]) + '" x2="' + n(m[2]) + '" y2="' + n(m[3]) + '"/>';
      }).join('') + '</g>'
      + '</svg>';
  }

  /* The product/Creator payload keeps the complete print sequence so its
     edition contract is still validated before the book can be sold. The
     customer-facing viewer shows the cover, the dedication page (owner,
     2026-10-18, after Wonderbly: our dedication is the sample, and the
     shopper may write their own there) and the story. Return a new array so
     omitting the back cover never mutates the parsed Shopify payload or the
     fulfilment data submitted with the order. */
  function storybookViewerSpreads(spreads) {
    if (!Array.isArray(spreads)) return [];
    return spreads.filter(function (spread) {
      return spread && spread.id !== 'back-cover';
    });
  }

  /* US page one carries print's range row (compose_intro_book_mockups,
     build_storybook_canva_box_proof.py, layout us_page_one): three
     straight-on hardcover scenes centred 29/50/71% across, their tops 70%
     down, each 22% of the page wide. UK print puts its row on the shared
     intro page, which this preview leaves out (isSharedIntroSpread).
     With the QR card the row is four slots (range_qr.RANGE_ROW_QR_CENTRES):
     the three books then the card, at the same top and size. */
  var INTRO_RANGE_CENTRES = [0.29, 0.50, 0.71];
  var INTRO_RANGE_QR_CENTRES = [0.198, 0.3993, 0.6007, 0.802];
  var INTRO_RANGE_TOP = 0.70;
  var INTRO_RANGE_SIDE = 0.22;
  /* order_pipeline._intro_book_story_ids: the first three, never this book. */
  var INTRO_RANGE_STORIES = ['luna_the_shy_dragon', 'rainbow_kingdom', 'dino_herd', 'finding_me'];

  function introRangePercent(fraction) {
    return Math.round(fraction * 10000) / 100;
  }

  /* Three books with their places on a US single dedication page, or none
     (any other page, or any art missing). They take the first three of
     print's four slots when the QR card follows (qr.src), else today's. */
  function introRangeBooks(spread, region, covers, storyId, qr) {
    var dedication = spread && (spread.id === 'dedication' || spread.id === 'dedication-page');
    if (!dedication || spread.layout !== 'single' || region !== 'US' || !covers) return [];
    var centres = qr && qr.src ? INTRO_RANGE_QR_CENTRES : INTRO_RANGE_CENTRES;
    var books = INTRO_RANGE_STORIES.filter(function (id) {
      return id !== storyId;
    }).slice(0, 3).map(function (id, index) {
      var cover = covers[id];
      return cover && cover.src ? {
        storyId: id,
        title: cover.title,
        src: cover.src,
        left: introRangePercent(centres[index] - INTRO_RANGE_SIDE / 2),
        top: introRangePercent(INTRO_RANGE_TOP),
        width: introRangePercent(INTRO_RANGE_SIDE)
      } : null;
    });
    return books.every(Boolean) ? books : [];
  }

  /* The QR card in the row's fourth slot, or null: only beside three books,
     and only with its art. */
  function introRangeQrCard(spread, region, covers, storyId, qr) {
    if (!qr || !qr.src) return null;
    if (introRangeBooks(spread, region, covers, storyId, qr).length !== 3) return null;
    return {
      src: qr.src,
      alt: qr.alt,
      left: introRangePercent(INTRO_RANGE_QR_CENTRES[3] - INTRO_RANGE_SIDE / 2),
      top: introRangePercent(INTRO_RANGE_TOP),
      width: introRangePercent(INTRO_RANGE_SIDE)
    };
  }

  var STORYBOOK_ADD_RECONCILE_DELAYS_MS = [250, 750, 1500, 3000];
  /* A pending add older than this has long since either committed or been
     dropped by Shopify. On a later visit, one confirmed basket read may then
     release the safety lock instead of trapping the browser in "unknown". */
  var STORYBOOK_ADD_SETTLE_MS = 10 * 60 * 1000;

  function settledStorybookPendingAdd(record, now) {
    return Boolean(
      record
      && validStorybookAddToken(record.token)
      && typeof record.createdAt === 'number'
      && Number.isFinite(record.createdAt)
      && now - record.createdAt > STORYBOOK_ADD_SETTLE_MS
    );
  }

  function pollForStorybookAddToken(checkToken, wait, delays) {
    var retryDelays = Array.isArray(delays)
      ? delays.slice(0, 8)
      : STORYBOOK_ADD_RECONCILE_DELAYS_MS.slice();
    var retryIndex = 0;

    function check() {
      return Promise.resolve().then(checkToken).catch(function () {
        return false;
      }).then(function (found) {
        if (found) return true;
        if (retryIndex >= retryDelays.length) return false;
        var delay = retryDelays[retryIndex++];
        return Promise.resolve().then(function () {
          return wait(delay);
        }).catch(function () {
          return null;
        }).then(check);
      });
    }

    return check();
  }

  function validStorybookAddToken(value) {
    return typeof value === 'string'
      && value.length >= 16
      && value.length <= 160
      && /^[a-z0-9-]+$/i.test(value);
  }

  function readStorybookPendingAdd(storage, key) {
    try {
      if (!storage || typeof storage.getItem !== 'function') return null;
      var parsed = JSON.parse(storage.getItem(key) || 'null');
      if (
        !parsed
        || !validStorybookAddToken(parsed.token)
        || (parsed.state !== 'posting' && parsed.state !== 'unknown')
        || typeof parsed.createdAt !== 'number'
        || !Number.isFinite(parsed.createdAt)
        || parsed.createdAt <= 0
      ) return null;
      return {
        token: parsed.token,
        state: parsed.state,
        createdAt: parsed.createdAt
      };
    } catch (error) {
      return null;
    }
  }

  function persistStorybookPendingAdd(storage, key, record) {
    try {
      if (!storage || typeof storage.setItem !== 'function') return false;
      var safeRecord = {
        token: record && record.token,
        state: record && record.state,
        createdAt: record && record.createdAt
      };
      if (
        !validStorybookAddToken(safeRecord.token)
        || (safeRecord.state !== 'posting' && safeRecord.state !== 'unknown')
        || typeof safeRecord.createdAt !== 'number'
        || !Number.isFinite(safeRecord.createdAt)
        || safeRecord.createdAt <= 0
      ) return false;
      storage.setItem(key, JSON.stringify(safeRecord));
      var persisted = readStorybookPendingAdd(storage, key);
      return Boolean(
        persisted
        && persisted.token === safeRecord.token
        && persisted.state === safeRecord.state
        && persisted.createdAt === safeRecord.createdAt
      );
    } catch (error) {
      return false;
    }
  }

  function clearStorybookPendingAdd(storage, key) {
    try {
      if (storage && typeof storage.removeItem === 'function') storage.removeItem(key);
    } catch (error) { /* keep the in-memory safety lock */ }
  }

  var root = document.getElementById('llsb');
  if (!root) return;
  if (!window.LLBooksOffers || typeof window.LLBooksOffers.requiresSingleChild !== 'function') {
    document.querySelectorAll('[data-llsb-add], [data-llsb-add-sticky]').forEach(function (button) { button.disabled = true; button.textContent = 'Please refresh'; });
    var refreshStatus = root.querySelector('[data-llsb-add-status]');
    if (refreshStatus) { refreshStatus.hidden = false; refreshStatus.textContent = 'Please refresh to load the latest book options.'; }
    return;
  }
  var guidedEnabled = root.getAttribute('data-llsb-guided') === 'true' && Boolean(window.LLStorybookJourney && window.LLBooksOffers && window.LLBundlePage && window.LLBundlePage.createChildEditor);
  var journey = null;

  /* The gallery is sticky under the site header, which is sticky itself;
     it needs the header's height as its offset (.llsb__gallery reads
     --llp-sticky-top) — measured, because the header's height depends on
     the announcement bar and the viewport. */
  var siteHeader = document.querySelector('.section-llp-header');
  function syncStickyTop() {
    if (!siteHeader) return;
    root.style.setProperty('--llp-sticky-top', Math.round(siteHeader.getBoundingClientRect().height) + 'px');
  }
  syncStickyTop();
  window.addEventListener('resize', syncStickyTop);

  /* A Creator-authored preview is deliberately an overlay on the known-good
     theme configuration, not a second storefront implementation. The whole
     override is rejected if any required style, spread, text box or pose box
     is malformed. Luna keeps its established inline fallback; every newer
     story fails closed so it can never display or sell the wrong book. */
  function applyStorybookPreview(baseConfig, preview) {
    var LUNA_STORY_ID = 'luna_the_shy_dragon';
    var baseStoryId = typeof baseConfig.storyId === 'string' ? baseConfig.storyId.trim() : '';

    function rejectedPreview() {
      if (baseStoryId === LUNA_STORY_ID) return baseConfig;
      var unavailable = JSON.parse(JSON.stringify(baseConfig));
      unavailable.styles = {};
      unavailable.spreads = [];
      unavailable.characters = [];
      unavailable.samples = [];
      unavailable.storybookPreviewUnavailable = true;
      unavailable.storybookPreviewMessage = 'This story preview is temporarily unavailable. Please try again later.';
      if (unavailable.product) {
        unavailable.product.available = false;
        unavailable.product.anyAvailable = false;
      }
      return unavailable;
    }

    try {
      if (!preview || preview.enabled !== true || preview.schemaVersion !== 1 || preview.productType !== 'storybook') return rejectedPreview();
      var previewStoryId = typeof preview.storyId === 'string' ? preview.storyId.trim() : '';
      // v1 Luna previews already published before story IDs existed remain
      // valid. All new stories must carry an exact product/preview match.
      if (baseStoryId === LUNA_STORY_ID) {
        if (previewStoryId && previewStoryId !== LUNA_STORY_ID) return rejectedPreview();
      } else if (!baseStoryId || !previewStoryId || previewStoryId !== baseStoryId) {
        return rejectedPreview();
      }
      if (!preview.styles || typeof preview.styles !== 'object' || Array.isArray(preview.styles)) return rejectedPreview();
      if (!Array.isArray(preview.spreads) || preview.spreads.length < 3 || preview.spreads.length > 64) return rejectedPreview();

      var allowedFamilies = [
        'Archivo Black', 'Bodoni Moda', 'Cormorant', 'Courier Prime', 'Crimson Pro',
        'EB Garamond', 'Fraunces', 'Great Vibes', 'Mrs Saint Delafield',
        'Pinyon Script', 'Playfair Display', 'Sacramento', 'Slopes',
        'Bubblegum Sans', 'Cheering Section', 'Luckiest Guy', 'Special Elite',
        // Christmas Hush cover name (Google Fonts family "Emilys Candy").
        'Emilys Candy',
        // Activity pages' body lines: the storefront's own body face.
        'Hanken Grotesk'
      ];
      var styleKeys = Object.keys(preview.styles);
      if (!styleKeys.length || styleKeys.length > 128) return rejectedPreview();
      var cleanStyles = {};
      var compositorRainbow = baseStoryId === 'rainbow_kingdom' && preview.contract === 'rainbow-compositor-preview-v1';
      /* Dino Herd's compositor preview carries the same inline-span runs
         (the coloured name, BOOM, Stamp...) without Rainbow's shape contract. */
      var compositorDino = baseStoryId === 'dino_herd' && preview.contract === 'dino-compositor-preview-v1';
      var compositorRuns = compositorRainbow || compositorDino;
      if (preview.contract != null && !compositorRuns) return rejectedPreview();
      var cleanSpanStyles = Object.create(null);

      function numberBetween(value, min, max) {
        var n = Number(value);
        return Number.isFinite(n) && n >= min && n <= max ? n : null;
      }
      function colour(value) {
        return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : null;
      }
      function familyFrom(style) {
        var value = typeof style.family === 'string' ? style.family : style.font;
        if (typeof value !== 'string') return null;
        if (value === 'crimson') return 'Crimson Pro';
        if (value === 'script') return 'Bubblegum Sans';
        var match = value.match(/^\s*['"]?([^,'"]+)/);
        var family = match ? match[1].trim() : '';
        return allowedFamilies.indexOf(family) >= 0 ? family : null;
      }
      function fontStack(family) {
        if (family === 'Bubblegum Sans') return "'Bubblegum Sans', 'Luckiest Guy', cursive";
        if (family === 'Cheering Section') return "'Cheering Section', 'Luckiest Guy', cursive";
        if (family === 'Luckiest Guy') return "'Luckiest Guy', sans-serif";
        if (family === 'Emilys Candy') return "'Emilys Candy', 'Luckiest Guy', cursive";
        if (family === 'Slopes') return "'Slopes', 'Caveat', cursive";
        if (['Great Vibes', 'Mrs Saint Delafield', 'Pinyon Script', 'Sacramento'].indexOf(family) >= 0) return "'" + family + "', cursive";
        if (family === 'Archivo Black') return "'Archivo Black', sans-serif";
        if (['Courier Prime', 'Special Elite'].indexOf(family) >= 0) return "'" + family + "', monospace";
        return "'" + family + "', Georgia, serif";
      }

      for (var styleIndex = 0; styleIndex < styleKeys.length; styleIndex++) {
        var styleKey = styleKeys[styleIndex];
        var sourceStyle = preview.styles[styleKey];
        if (!/^[a-z0-9][a-z0-9_:-]{0,79}$/i.test(styleKey) || !sourceStyle || typeof sourceStyle !== 'object' || Array.isArray(sourceStyle)) return rejectedPreview();
        var family = familyFrom(sourceStyle);
        var weight = numberBetween(sourceStyle.weight == null ? 400 : sourceStyle.weight, 100, 900);
        var size = numberBetween(sourceStyle.size, 6, 160);
        var leading = numberBetween(sourceStyle.leading == null ? 1.05 : sourceStyle.leading, 0.7, 3);
        var tracking = numberBetween(sourceStyle.tracking == null ? 0 : sourceStyle.tracking, -0.1, 1);
        var fill = colour(sourceStyle.fill || sourceStyle.color);
        var stroke = sourceStyle.stroke == null ? null : colour(sourceStyle.stroke);
        /* Explicit stroke widths are CSS em units. Keep the historical 0.028em
           rendering only for older payloads that omit this optional field;
           any present value must be a bounded JSON number attached to a
           validated stroke colour. */
        var strokeWidth = sourceStyle.strokeWidth == null
          ? null
          : typeof sourceStyle.strokeWidth === 'number'
            ? numberBetween(sourceStyle.strokeWidth, 0.001, 0.1)
            : null;
        var strokeAlpha = sourceStyle.strokeAlpha == null
          ? null
          : typeof sourceStyle.strokeAlpha === 'number'
            && Number.isInteger(sourceStyle.strokeAlpha)
            && sourceStyle.strokeAlpha >= 0
            && sourceStyle.strokeAlpha <= 255
            ? sourceStyle.strokeAlpha
            : null;
        /* Luna's fallback also carries the reviewed one-line title policy.
           Older Creator payloads inherit it so a stale metafield cannot make
           the child's name wrap instead of shrinking to the checked-in box. */
        var inheritedPreserveLineBreaks = baseStoryId === LUNA_STORY_ID
          && baseConfig.styles
          && baseConfig.styles[styleKey]
          ? baseConfig.styles[styleKey].preserveLineBreaks
          : null;
        var rawPreserveLineBreaks = sourceStyle.preserveLineBreaks == null
          ? inheritedPreserveLineBreaks
          : sourceStyle.preserveLineBreaks;
        var preserveLineBreaks = rawPreserveLineBreaks == null
          ? null
          : typeof rawPreserveLineBreaks === 'boolean'
            ? rawPreserveLineBreaks
            : null;
        var paddingCanva = sourceStyle.paddingCanva == null
          ? null
          : typeof sourceStyle.paddingCanva === 'number'
            ? numberBetween(sourceStyle.paddingCanva, 0, 50)
            : null;
        /* Optional shrink floor, in the same canva units as `size`. A published
           layout may omit it; a present-but-invalid one is a malformed payload. */
        var min = sourceStyle.min == null ? null : numberBetween(sourceStyle.min, 1, 160);
        /* Luna's checked-in fallback carries the approved point where its
           cover name starts shrinking. Older published Luna preview payloads
           inherit that value so activating a metafield cannot silently restore
           the later, box-only fit. Other stories opt in explicitly. */
        var inheritedFullSizeMaxChars = baseStoryId === LUNA_STORY_ID
          && baseConfig.styles
          && baseConfig.styles[styleKey]
          ? baseConfig.styles[styleKey].fullSizeMaxChars
          : null;
        var rawFullSizeMaxChars = sourceStyle.fullSizeMaxChars == null
          ? inheritedFullSizeMaxChars
          : sourceStyle.fullSizeMaxChars;
        var fullSizeMaxChars = rawFullSizeMaxChars == null
          ? null
          : typeof rawFullSizeMaxChars === 'number'
            && Number.isInteger(rawFullSizeMaxChars)
            ? numberBetween(rawFullSizeMaxChars, 1, 32)
            : null;
        if (!family || weight == null || size == null || leading == null || tracking == null || !fill || (sourceStyle.stroke != null && !stroke) || (sourceStyle.strokeWidth != null && (strokeWidth == null || !stroke)) || (sourceStyle.strokeAlpha != null && (strokeAlpha == null || !stroke)) || (rawPreserveLineBreaks != null && preserveLineBreaks == null) || (sourceStyle.paddingCanva != null && paddingCanva == null) || (baseStoryId === 'rainbow_kingdom' && paddingCanva == null) || (sourceStyle.min != null && (min == null || min > size)) || (rawFullSizeMaxChars != null && fullSizeMaxChars == null)) return rejectedPreview();
        cleanStyles[styleKey] = {
          font: fontStack(family),
          family: family,
          weight: weight,
          size: size,
          leading: leading,
          tracking: tracking,
          italic: sourceStyle.italic === true,
          fill: fill
        };
        if (stroke) cleanStyles[styleKey].stroke = stroke;
        if (strokeWidth != null) cleanStyles[styleKey].strokeWidth = strokeWidth;
        if (strokeAlpha != null) cleanStyles[styleKey].strokeAlpha = strokeAlpha;
        if (preserveLineBreaks != null) cleanStyles[styleKey].preserveLineBreaks = preserveLineBreaks;
        if (paddingCanva != null) cleanStyles[styleKey].paddingCanva = paddingCanva;
        if (min != null) cleanStyles[styleKey].min = min;
        if (fullSizeMaxChars != null) cleanStyles[styleKey].fullSizeMaxChars = fullSizeMaxChars;
      }

      if (compositorRuns) {
        if (!preview.spanStyles || typeof preview.spanStyles !== 'object' || Array.isArray(preview.spanStyles)) return rejectedPreview();
        var spanKeys = Object.keys(preview.spanStyles);
        if (spanKeys.length > 64) return rejectedPreview();
        for (var spanIndex = 0; spanIndex < spanKeys.length; spanIndex++) {
          var spanKey = spanKeys[spanIndex], ss = preview.spanStyles[spanKey];
          if (!/^[a-z0-9_]{1,60}$/.test(spanKey) || !ss || typeof ss !== 'object' || Array.isArray(ss)) return rejectedPreview();
          function boundedSpan(value, lo, hi) { return typeof value === 'number' && numberBetween(value, lo, hi) != null; }
          if (['Crimson Pro', 'Bubblegum Sans', 'Luckiest Guy'].indexOf(ss.family) < 0 || !boundedSpan(ss.weight, 100, 900)
            || typeof ss.italic !== 'boolean' || !colour(ss.color) || !boundedSpan(ss.scale, 0.5, 2)
            || !boundedSpan(ss.tracking, -0.1, 1)) return rejectedPreview();
          if ((ss.stroke != null || ss.strokeWidth != null) && (!colour(ss.stroke) || !boundedSpan(ss.strokeWidth, 0.001, 0.1))) return rejectedPreview();
          if (ss.letterDy != null && (!Array.isArray(ss.letterDy) || ss.letterDy.length > 16 || ss.letterDy.some(function (v) { return !boundedSpan(v, -0.3, 0.3); }))) return rejectedPreview();
          if (ss.climb != null && !boundedSpan(ss.climb, 0, 1)) return rejectedPreview();
          cleanSpanStyles[spanKey] = { family: ss.family, weight: ss.weight, italic: ss.italic, color: colour(ss.color), scale: ss.scale, tracking: ss.tracking };
          if (ss.stroke != null) { cleanSpanStyles[spanKey].stroke = colour(ss.stroke); cleanSpanStyles[spanKey].strokeWidth = ss.strokeWidth; }
          if (ss.letterDy != null) cleanSpanStyles[spanKey].letterDy = ss.letterDy.slice();
          if (ss.climb != null) cleanSpanStyles[spanKey].climb = ss.climb;
        }
      }

      var seenSpreadIds = {};
      var lunaCoverNameX = null;
      var lunaCoverNameY = null;
      var lunaCoverNameW = null;
      var lunaCoverNameH = null;
      var lunaCoverNameAlign = null;
      var lunaCoverNameValign = null;
      var lunaCoverCharY = null;
      var lunaCoverCharH = null;
      if (
        baseStoryId === LUNA_STORY_ID
        && Array.isArray(baseConfig.spreads)
      ) {
        for (var baseSpreadIndex = 0; baseSpreadIndex < baseConfig.spreads.length; baseSpreadIndex++) {
          var baseSpread = baseConfig.spreads[baseSpreadIndex];
          if (!baseSpread || baseSpread.id !== 'cover') continue;
          if (Array.isArray(baseSpread.texts)) {
            for (var baseTextIndex = 0; baseTextIndex < baseSpread.texts.length; baseTextIndex++) {
              var baseText = baseSpread.texts[baseTextIndex];
              if (!baseText || baseText.kind !== 'cover_child_name') continue;
              lunaCoverNameX = numberBetween(baseText.x, 0, 100);
              lunaCoverNameY = numberBetween(baseText.y, 0, 100);
              lunaCoverNameW = numberBetween(baseText.w, 0.1, 100);
              lunaCoverNameH = numberBetween(baseText.h, 0.1, 100);
              lunaCoverNameAlign = ['left', 'center', 'right'].indexOf(baseText.align) >= 0
                ? baseText.align
                : null;
              lunaCoverNameValign = ['top', 'middle', 'bottom'].indexOf(baseText.valign) >= 0
                ? baseText.valign
                : null;
              break;
            }
          }
          if (baseSpread.char && String(baseSpread.char.pose || baseSpread.char.poseId || '') === '14') {
            var baseCoverCharY = numberBetween(baseSpread.char.y, 0, 100);
            var baseCoverCharH = numberBetween(baseSpread.char.h, 0.1, 100);
            if (
              baseCoverCharY != null
              && baseCoverCharH != null
              && baseCoverCharY + baseCoverCharH <= 100.001
            ) {
              lunaCoverCharY = baseCoverCharY;
              lunaCoverCharH = baseCoverCharH;
            }
          }
          break;
        }
      }
      var cleanSpreads = preview.spreads.map(function (spread) {
        if (!spread || typeof spread !== 'object' || Array.isArray(spread)) throw new Error('invalid spread');
        if (typeof spread.id !== 'string' || !/^[a-z0-9][a-z0-9_-]{0,79}$/i.test(spread.id) || seenSpreadIds[spread.id]) throw new Error('invalid spread id');
        seenSpreadIds[spread.id] = true;
        if (typeof spread.label !== 'string' || !spread.label.trim() || spread.label.length > 100) throw new Error('invalid spread label');
        var description = typeof spread.description === 'string' ? spread.description.trim() : '';
        if (spread.description != null && (
          description.length < 12
          || description.length > 300
          || /\[(?:child|name|dedication)/i.test(description)
          || /\briu\b/i.test(description)
        )) throw new Error('invalid spread description');
        /* Rainbow's artwork descriptions are Creator-authored scene copy.
           Requiring them prevents a new story from silently falling back to
           generic "Spread 5" alt text or leaking sample/customer tokens. */
        if (baseStoryId === 'rainbow_kingdom' && !description) throw new Error('missing spread description');
        if (typeof spread.bg !== 'string' || !/^(https?:\/\/|\/\/|\/|assets\/)/i.test(spread.bg)) throw new Error('invalid spread image');
        if (!Array.isArray(spread.texts) || spread.texts.length > 64) throw new Error('invalid text boxes');

        function box(value, requirePose) {
          if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('invalid box');
          var x = numberBetween(value.x, 0, 100);
          var y = numberBetween(value.y, 0, 100);
          var w = numberBetween(value.w, 0.1, 100);
          var h = numberBetween(value.h, 0.1, 100);
          var rotation = numberBetween(value.rotation == null ? (value.rot || 0) : value.rotation, -180, 180);
          if (x == null || y == null || w == null || h == null || rotation == null || x + w > 100.001 || y + h > 100.001) throw new Error('box outside spread');
          var clean = { x: x, y: y, w: w, h: h, rot: rotation };
          if (requirePose) {
            var pose = String(value.pose || value.poseId || '');
            if (!/^\d{2}$/.test(pose)) throw new Error('invalid pose');
            clean.pose = pose;
            clean.binding = 'pose_transfer';
            if (value.backdrop != null) {
              var backdrop = colour(value.backdrop);
              if (!backdrop) throw new Error('invalid backdrop');
              clean.backdrop = backdrop;
            }
          }
          return clean;
        }

        var texts = spread.texts.map(function (text) {
          var cleanBox = box(text, false);
          if (typeof text.style !== 'string' || !cleanStyles[text.style]) throw new Error('unknown style');
          if (typeof text.template !== 'string' || text.template.length > 2000) throw new Error('invalid text');
          cleanBox.id = typeof text.id === 'string' ? text.id.slice(0, 80) : '';
          cleanBox.kind = typeof text.kind === 'string' ? text.kind.slice(0, 40) : 'dialogue';
          /* Keep Luna's cover-wrap safety position authoritative when an older
             Creator metafield is active. A new Creator export carries the same
             x/y/width/height/alignment values, while legacy payloads inherit the checked-in fallback so
             deploying the source fix cannot be masked by stale metadata. */
          if (
            lunaCoverNameX != null
            && lunaCoverNameY != null
            && lunaCoverNameW != null
            && lunaCoverNameH != null
            && spread.id === 'cover'
            && cleanBox.kind === 'cover_child_name'
          ) {
            cleanBox.x = lunaCoverNameX;
            cleanBox.y = lunaCoverNameY;
            cleanBox.w = lunaCoverNameW;
            cleanBox.h = lunaCoverNameH;
            if (
              cleanBox.x + cleanBox.w > 100.001
              || cleanBox.y + cleanBox.h > 100.001
            ) throw new Error('cover name box outside spread');
          }
          cleanBox.binding = typeof text.binding === 'string' ? text.binding.slice(0, 40) : 'static_text';
          cleanBox.style = text.style;
          /* Optional per-element reviewed ink. The compositor emits this for
             spreads whose verse is authored in a colour other than the style
             default (cream on dark plates); print takes the same value from
             print_fill_rgba. Validated through the same #rrggbb check as
             style fills, and silently ignored if malformed so a bad payload
             falls back to the style rather than rejecting the preview. */
          var textFill = text.fill == null ? null : colour(text.fill);
          if (textFill) cleanBox.fill = textFill;
          cleanBox.align = ['left', 'center', 'right'].indexOf(text.align) >= 0 ? text.align : 'left';
          if (
            lunaCoverNameAlign != null
            && spread.id === 'cover'
            && cleanBox.kind === 'cover_child_name'
          ) cleanBox.align = lunaCoverNameAlign;
          cleanBox.valign = ['top', 'middle', 'bottom'].indexOf(text.valign) >= 0 ? text.valign : 'top';
          if (
            lunaCoverNameValign != null
            && spread.id === 'cover'
            && cleanBox.kind === 'cover_child_name'
          ) cleanBox.valign = lunaCoverNameValign;
          cleanBox.template = text.template;
          if (compositorRuns) {
            var lines = text.template.split('\n');
            if (!Array.isArray(text.runs) || text.runs.length !== lines.length) throw new Error('missing text runs');
            cleanBox.runs = text.runs.map(function (runs, index) {
              if (!Array.isArray(runs) || runs.length > 200) throw new Error('invalid runs');
              var clean = runs.map(function (run) {
                if (!run || typeof run !== 'object' || Array.isArray(run) || Object.keys(run).some(function (key) { return key !== 't' && key !== 's'; })
                  || typeof run.t !== 'string' || run.t.length > 200 || (run.s != null && !Object.prototype.hasOwnProperty.call(cleanSpanStyles, run.s))) throw new Error('invalid run');
                return run.s == null ? { t: run.t } : { t: run.t, s: run.s };
              });
              if (clean.map(function (run) { return run.t; }).join('') !== lines[index]) throw new Error('runs differ from text');
              return clean;
            });
          }

          var innerBox = storybookTextBoxGeometry(cleanBox, cleanStyles[text.style]);
          if (innerBox.w <= 0 || innerBox.h <= 0) throw new Error('text padding consumes box');
          return cleanBox;
        });

        var cleanChar = spread.char == null ? null : box(spread.char, true);
        /* The Luna pose-14 box moved inside the cover-wrap safe line. Keep the
           checked-in fallback authoritative over an older Creator metafield,
           just as the cover-name x position is above. */
        if (
          lunaCoverCharY != null
          && lunaCoverCharH != null
          && spread.id === 'cover'
          && cleanChar
          && cleanChar.pose === '14'
        ) {
          cleanChar.y = lunaCoverCharY;
          cleanChar.h = lunaCoverCharH;
        }
        var result = {
          id: spread.id,
          label: spread.label.trim(),
          description: description || spread.label.trim(),
          layout: spread.layout === 'single' ? 'single' : 'spread',
          bg: spread.bg,
          texts: texts,
          char: cleanChar
        };
        return result;
      });

      if (baseStoryId === 'rainbow_kingdom') {
        /* Two sealed Rainbow editions exist: the UK 17-canvas book
           (16 preview spreads, Creator-authored) and the US 24-page book
           (15 preview spreads, compositor-derived — the departure
           transition is cut and the closing beat is a square single). Each
           length gets its exact contract; any other shape fails closed. */
        var rainbowContractValid = false;
        if (compositorRainbow) {
          var uk = /^rainbow-kingdom-v[0-9]+-[a-z0-9-]+$/.test(preview.bundleId || '');
          var us = /^rainbow-kingdom-us-v[0-9]+-[a-z0-9-]+$/.test(preview.bundleId || '');
          var expectedIds = ['cover', uk ? 'dedication' : 'dedication-page'];
          var expectedPoses = ['14', null];
          for (var scene = 1; scene <= (uk ? 13 : 11); scene++) {
            expectedIds.push('sp' + String(scene).padStart(2, '0'));
            expectedPoses.push(String(uk || scene < 3 ? scene : scene + 1).padStart(2, '0'));
          }
          expectedIds.push('closing-page', 'back-cover');
          expectedPoses.push(uk ? null : '13', null);
          var liveKinds = ['cover_child_name', 'cover_title_ampersand', 'cover_title_story', 'dedication_greeting', 'dialogue', 'activity_name_line'];
          rainbowContractValid = (uk || us) && cleanSpreads.length === expectedIds.length && cleanSpreads.every(function (spread, index) {
            var count = spread.texts.length;
            var expectedLayout = index === 0 || spread.id === 'back-cover' || (us && (index === 1 || spread.id === 'closing-page')) ? 'single' : 'spread';
            return spread.id === expectedIds[index] && spread.layout === expectedLayout
              && (spread.char ? spread.char.pose : null) === expectedPoses[index]
              && (index === 0 ? count === 3 : spread.id === 'back-cover' ? count === 0 : count <= 4)
              && spread.texts.every(function (text) { return liveKinds.indexOf(text.kind) >= 0; });
          });
        } else if (cleanSpreads.length === 16) {
          var rainbowIds = ['cover', 'dedication'];
          for (var rainbowIndex = 1; rainbowIndex <= 13; rainbowIndex++) {
            rainbowIds.push('sp' + String(rainbowIndex).padStart(2, '0'));
          }
          rainbowIds.push('back-cover');
          rainbowContractValid = cleanSpreads.every(function (spread, index) {
            if (spread.id !== rainbowIds[index]) return false;
            var expectedTexts = spread.id === 'back-cover'
              ? 0
              : spread.id === 'cover'
                ? 3
                : spread.id === 'sp13'
                  ? 2
                  : 1;
            if (spread.texts.length !== expectedTexts) return false;
            var expectedPose = index === 0
              ? '14'
              : index >= 2 && index <= 14
                ? String(index - 1).padStart(2, '0')
                : null;
            return expectedPose ? spread.char && spread.char.pose === expectedPose : spread.char == null;
          });
        } else if (cleanSpreads.length === 15) {
          var rainbowUsIds = ['cover', 'dedication-page'];
          for (var rainbowUsIndex = 1; rainbowUsIndex <= 12; rainbowUsIndex++) {
            rainbowUsIds.push('sp' + String(rainbowUsIndex).padStart(2, '0'));
          }
          rainbowUsIds.push('back-cover');
          // The shortened edition drops UK story spread 3. Canvas numbers
          // include front matter and must never be used as sample pose IDs.
          var rainbowUsPoses = ['01', '02', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13'];
          rainbowContractValid = cleanSpreads.every(function (spread, index) {
            if (spread.id !== rainbowUsIds[index]) return false;
            var expectedTexts = spread.id === 'back-cover'
              ? 0
              : spread.id === 'cover'
                ? 3
                : 1;
            if (spread.texts.length !== expectedTexts) return false;
            if (index === 0) return Boolean(spread.char && spread.char.pose === '14');
            if (index >= 2 && index <= 13) return Boolean(spread.char && spread.char.pose === rainbowUsPoses[index - 2]);
            return spread.char == null;
          });
        }
        if (!rainbowContractValid) return rejectedPreview();
      }

      /* Page 2 of every printed book is the shared Label & Leaf introduction
         ("We don't just write books for kids…") over the site URL. It earns
         its place in the printed copy; in the storefront preview it hands the
         shopper a page of our own marketing at the exact moment they are
         looking for their child. Dino Herd UK publishes it as a
         `welcome-page` spread, so it is dropped here rather than in each
         story's metafield: a republish, or a new story built from the same
         Creator front matter, cannot put it back. The intro_* styles are what
         define this page (nothing else in a book uses them), so a spread
         whose text is built entirely from them is the page — matched on the
         styles the compositor names, never on the copy itself. The cover is
         never a candidate, and a payload that somehow matched everything is
         left untouched so the preview can never empty itself. This runs after
         validation, so a malformed preview still fails closed above. */
      var INTRO_STYLE_KINDS = ['intro_body', 'intro_emphasis', 'intro_url'];
      function isSharedIntroSpread(spread, index) {
        if (index === 0 || spread.char || !spread.texts.length) return false;
        return spread.texts.every(function (text) {
          return INTRO_STYLE_KINDS.indexOf(text.style) >= 0
            || INTRO_STYLE_KINDS.indexOf(text.kind) >= 0;
        });
      }
      var withoutIntro = cleanSpreads.filter(function (spread, index) {
        return !isSharedIntroSpread(spread, index);
      });
      if (withoutIntro.length >= 3) cleanSpreads = withoutIntro;

      var defaults = preview.defaults || {};
      if (defaults && (typeof defaults !== 'object' || Array.isArray(defaults))) return rejectedPreview();
      var nextConfig = JSON.parse(JSON.stringify(baseConfig));
      nextConfig.styles = cleanStyles;
      if (compositorRuns) nextConfig.spanStyles = cleanSpanStyles;
      nextConfig.spreads = cleanSpreads;
      nextConfig.previewDefaults = {
        childName: typeof defaults.childName === 'string' ? defaults.childName.trim().slice(0, 32) : ''
      };
      nextConfig.storybookPreviewActive = true;
      return nextConfig;
    } catch (error) {
      return rejectedPreview();
    }
  }

  var parsedConfig = JSON.parse(root.querySelector('[data-llsb-config]').textContent);
  var config = applyStorybookPreview(parsedConfig, parsedConfig.storybookPreview);
  /* One source of truth for bundle eligibility: the section's story list
     (llsb_bundle_story). A story the bundles do not sell (Christmas Hush)
     gets no bundle data at all, so neither this page nor the journey can
     select, price or hand off a bundle. */
  if (config.bundlesEnabled === false) config.bundles = [];
  if (config.storybookPreviewUnavailable) {
    root.setAttribute('data-llsb-preview-unavailable', 'true');
    var unavailablePreview = root.querySelector('[data-llsb-preview]');
    if (unavailablePreview) {
      unavailablePreview.setAttribute('aria-label', 'Personalisation sample unavailable');
      var unavailableNotice = document.createElement('p');
      unavailableNotice.className = 'llsb__upload-status is-bad';
      unavailableNotice.setAttribute('role', 'status');
      unavailableNotice.textContent = config.storybookPreviewMessage;
      unavailablePreview.appendChild(unavailableNotice);
    }
    /* document-scoped, not root-scoped: [data-llsb-add-sticky] moves out to
       document.body on init (task 11c item 1) so it is not guaranteed to be
       inside root's subtree by the time this runs. */
    document.querySelectorAll('[data-llsb-add], [data-llsb-add-sticky]').forEach(function (button) {
      button.disabled = true;
      button.textContent = 'Temporarily unavailable';
    });
    var unavailableStatus = root.querySelector('[data-llsb-add-status]');
    if (unavailableStatus) {
      unavailableStatus.hidden = false;
      unavailableStatus.className = 'llsb__upload-status is-bad';
      unavailableStatus.textContent = config.storybookPreviewMessage;
    }
    return;
  }
  config = Object.assign({}, config, {
    spreads: storybookViewerSpreads(config.spreads)
  });
  var isShopify = typeof config.cartUrl === 'string' && config.cartUrl.indexOf('/cart') === 0;
  var storeKey = 'llsb:' + config.product.handle;

  /* The single hardcover is config.offers[0]; the bundles are built through
     the configurator module and post their own variant. Pin
     product.variantId to the single offer explicitly here rather than
     relying on both having been resolved from the same Liquid variable
     upstream (see theme/sections/ll-storybook.liquid). */
  if (config.offers && config.offers[0] && config.offers[0].variantId) {
    config.product.variantId = config.offers[0].variantId;
  }

  /* Photo rules, kept byte-identical to the colouring page's
     (ll-coloring-book.js) so one product family cannot quietly accept a file
     the other refuses; the section's own accept="" attribute lists the same
     three types. MAX_NAME_LENGTH mirrors the name input's maxlength — typing
     is capped by the browser, but the homepage handoff below fills the field
     programmatically and used to walk straight past it. */
  var ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  var MAX_PHOTO_BYTES = 10 * 1024 * 1024;
  var MAX_NAME_LENGTH = 12;

  var state = {
    photoFile: null,        // the customer's own upload, once it has passed the photo check
    photoDataUrl: null,
    photoWidth: null,
    photoHeight: null,
    name: '',
    dedication: '',         // the shopper's own dedication, in memory only ('' keeps the story's)
    offer: 'single'         // the selector's choice: 'single' | 'two-stories' | 'complete-collection'
  };

  /* The shopper's own dedication (owner, 2026-10-18). The worker prints a
     line's Dedication property in place of the story's fixed copy on these
     four stories' single books (StorybookJob.dedication); Finding books and
     sets keep their own. The journey switches it on only while a single
     book's sample is on screen, so a set's review never shows it. Five of
     the eight printed dedication boxes never wrap a line, so the words are
     wrapped here, at spaces, into lines of at most 56 characters (our own
     dedications run to 59); 8 such lines and 250 characters fit every
     story's box
     (services/storybook-production/tests/test_customer_dedication_fit.py),
     inside the worker's Rainbow limit. The characters are the worker's own
     dedication set. The sample shows exactly the lines that print. */
  var CUSTOM_DEDICATION_STORIES = ['luna_the_shy_dragon', 'rainbow_kingdom', 'dino_herd', 'christmas_hush'];
  var DEDICATION_MAX_CHARS = 250;
  var DEDICATION_MAX_LINES = 8;
  var DEDICATION_LINE_CHARS = 56;
  var dedicationActive = false;
  function wrapDedicationLine(line) {
    var lines = [], current = [];
    line.split(' ').forEach(function (word) {
      var letters = Array.from(word);
      // A word longer than a line is broken where it reaches the edge.
      while (letters.length > DEDICATION_LINE_CHARS) {
        if (current.length) { lines.push(current.join('')); current = []; }
        lines.push(letters.slice(0, DEDICATION_LINE_CHARS).join('')); letters = letters.slice(DEDICATION_LINE_CHARS);
      }
      var next = current.length ? current.concat([' '], letters) : letters;
      if (current.length && next.length > DEDICATION_LINE_CHARS) { lines.push(current.join('')); current = letters; }
      else current = next;
    });
    lines.push(current.join(''));
    return lines;
  }
  function normaliseDedication(value) {
    var lines = String(value || '').replace(/\r\n?/g, '\n').replace(/\ufe0f/g, '').split('\n').map(function (line) { return line.replace(/[ \t\u00a0]+/g, ' ').trim(); });
    while (lines.length && !lines[0]) lines.shift();
    while (lines.length && !lines[lines.length - 1]) lines.pop();
    return lines.reduce(function (all, line) { return all.concat(wrapDedicationLine(line)); }, []).join('\n');
  }
  function dedicationError(value) {
    var text = normaliseDedication(value);
    if (Array.from(text).length > DEDICATION_MAX_CHARS) return 'Please keep the dedication to ' + DEDICATION_MAX_CHARS + ' characters or fewer.';
    if (text.split('\n').length > DEDICATION_MAX_LINES) return 'That’s too long for the page. Please keep it to ' + DEDICATION_MAX_LINES + ' short lines.';
    return text ? storybookPersonalisationGlyphError(text, true) : '';
  }
  function dedicationLines(value) { var text = normaliseDedication(value); return text ? text.split('\n').length : 0; }
  function activeDedication() { return dedicationActive ? state.dedication : ''; }
  // The greeting on the story's own dedication page (the text the worker replaces).
  function ownGreeting() {
    var page = (config.spreads || []).filter(function (spread) { return spread && (spread.id === 'dedication' || spread.id === 'dedication-page'); })[0];
    return page && (page.texts || []).filter(function (text) { return text && text.kind === 'dedication_greeting' && typeof text.template === 'string'; })[0] || null;
  }
  function standardDedication() {
    var greeting = ownGreeting();
    return greeting ? fillStorybookTemplate(greeting.template, state.name || defaultChildName(), fixedDedication()) : '';
  }

  /* ================= preview rendering ================= */

  var viewConfig = config;
  var viewChild = null;
  function previewChild() { return viewChild || state; }
  var preview = root.querySelector('[data-llsb-preview]');
  var spreadEls = [];
  var currentSpreadIndex = 0;
  var bookViewport;
  var bookLabel;
  var bookCounter;
  var bookProgress;
  var previousPageButton;
  var nextPageButton;

  /* ================= gallery stage ================= */
  var galleryStage = root.querySelector('.llsb__stage');
  var galleryStageLive = root.querySelector('[data-llsb-stage-live]');
  var galleryStageMedia = root.querySelector('[data-llsb-stage-media]');
  var galleryStageVideo = root.querySelector('[data-llsb-stage-video]');
  var galleryStageTag = root.querySelector('[data-llsb-stage-tag]');
  var galleryStagePause = root.querySelector('[data-llsb-stage-pause]');
  var galleryThumbs = Array.prototype.slice.call(root.querySelectorAll('[data-llsb-gthumb]'));

  var galleryModes = root.querySelector('[data-llsb-gallery-modes]');
  var photoNav = root.querySelector('[data-llsb-photo-nav]');
  var photoPrev = root.querySelector('[data-llsb-photo-prev]');
  var photoNext = root.querySelector('[data-llsb-photo-next]');
  var photoCounter = root.querySelector('[data-llsb-photo-counter]');
  var photoRail = root.querySelector('[data-llsb-photo-rail]');
  var photoThumbs = root.querySelector('[data-llsb-gthumbs]');
  var morePhotos = root.querySelector('[data-llsb-more-photos]');
  var galleryMode = galleryThumbs.length ? 'photos' : 'preview';
  var selectedPhotoId = galleryThumbs.length ? galleryThumbs[0].getAttribute('data-llsb-gthumb') : null;

  function isMobileGalleryViewport() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  function updatePhotoOverflow() {
    if (!morePhotos || !photoThumbs) return;
    var horizontal = isMobileGalleryViewport();
    var remaining = horizontal
      ? photoThumbs.scrollWidth - photoThumbs.clientWidth - photoThumbs.scrollLeft
      : photoThumbs.scrollHeight - photoThumbs.clientHeight - photoThumbs.scrollTop;
    morePhotos.hidden = galleryMode !== 'photos' || (state.offer !== 'single' && !guidedEnabled) || remaining < 4;
  }

  function syncGalleryControls() {
    var isPhotos = galleryMode === 'photos';
    var isBundle = state.offer !== 'single' && !guidedEnabled;
    if (galleryModes) {
      galleryModes.hidden = isBundle || !galleryThumbs.length;
      galleryModes.querySelectorAll('[data-llsb-mode]').forEach(function (button) {
        button.setAttribute('aria-pressed', button.getAttribute('data-llsb-mode') === galleryMode ? 'true' : 'false');
      });
    }
    if (photoNav) photoNav.hidden = isBundle || !isPhotos;
    if (photoRail) photoRail.hidden = isBundle || !isPhotos;
    // The guided journey owns its moved preview during these steps. Gallery
    // resize/settle events must not make that visible preview inert again.
    var journeyOwnsPreview = guidedEnabled && /^(sample|review|look)$/.test(root.getAttribute('data-llsj-step') || '');
    if (galleryStageLive && !journeyOwnsPreview) {
      galleryStageLive.setAttribute('aria-hidden', isPhotos || isBundle ? 'true' : 'false');
      galleryStageLive.inert = isPhotos || isBundle;
    }
    var index = currentGallerySlideIndex();
    if (photoPrev) photoPrev.disabled = !mobilePhotoTrack() && index <= 0;
    if (photoNext) photoNext.disabled = !mobilePhotoTrack() && index >= galleryThumbs.length - 1;
    if (photoCounter) photoCounter.textContent = (photoCounter.getAttribute('data-format') || 'Photo [current] of [total]')
      .replace('[current]', index + 1).replace('[total]', galleryThumbs.length);
    updatePhotoOverflow();
  }

  // Filmed sample-book loops play like a moving photo: muted, looped and
  // started when their slide shows. A shopper's own pause holds until they
  // leave the slide; reduced motion and Save-Data leave the poster showing
  // with the usual controls.
  var loopPausedByGallery = false, loopHeldByShopper = false;
  function hideGalleryVideo() {
    if (!galleryStageVideo) return;
    if (!galleryStageVideo.paused) loopPausedByGallery = true;
    galleryStageVideo.pause(); galleryStageVideo.hidden = true;
    loopHeldByShopper = false;
    if (galleryStageTag) galleryStageTag.hidden = true;
    if (galleryStagePause) galleryStagePause.hidden = true;
  }
  function syncLoopButton() {
    if (!galleryStagePause) return;
    var paused = galleryStageVideo.paused;
    galleryStagePause.setAttribute('data-paused', paused ? 'true' : 'false');
    galleryStagePause.setAttribute('aria-label', paused ? 'Play the filmed sample' : 'Pause the filmed sample');
  }
  function loopsMayAutoplay() {
    var connection = navigator.connection;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !(connection && connection.saveData);
  }
  function syncGalleryVideo() {
    if (!galleryStageVideo) return;
    var media = (config.media || []).filter(function (item) { return item.id === selectedPhotoId; })[0];
    var isVideo = galleryMode === 'photos' && media && media.type === 'video';
    var step = root.getAttribute('data-llsj-step');
    galleryStage.classList.toggle('llsb__stage--video', !!isVideo);
    galleryStage.classList.toggle('llsb__stage--loop', !!(isVideo && media.loop));
    if (!isVideo || (step && step !== 'product') || gallerySettleTimer || (stagePointerStart && stagePointerStart.dragging)) {
      hideGalleryVideo(); return;
    }
    if (galleryStageVideo.getAttribute('data-media-id') !== media.id) {
      hideGalleryVideo();
      galleryStageVideo.replaceChildren();
      galleryStageVideo.poster = media.src;
      galleryStageVideo.setAttribute('aria-label', media.alt || '');
      galleryStageVideo.loop = !!media.loop;
      galleryStageVideo.muted = !!media.loop;
      galleryStageVideo.controls = !media.loop;
      (media.sources || []).slice().sort(function (a, b) { return (b.height || 0) - (a.height || 0); }).forEach(function (source) {
        var node = document.createElement('source'); node.src = source.url; node.type = source.mime_type;
        galleryStageVideo.appendChild(node);
      });
      galleryStageVideo.setAttribute('data-media-id', media.id);
      galleryStageVideo.load();
    }
    galleryStageVideo.hidden = false;
    if (galleryStageTag) galleryStageTag.hidden = !media.loop;
    if (galleryStagePause) galleryStagePause.hidden = !media.loop;
    if (media.loop && galleryStageVideo.paused && !loopHeldByShopper && loopsMayAutoplay()) {
      var playing = galleryStageVideo.play();
      if (playing && playing.catch) playing.catch(function () {});
    }
    syncLoopButton();
  }
  if (galleryStageVideo) {
    galleryStageVideo.addEventListener('pause', function () {
      if (loopPausedByGallery) loopPausedByGallery = false;
      else if (galleryStageVideo.loop) loopHeldByShopper = true;
    });
    galleryStageVideo.addEventListener('play', function () { loopHeldByShopper = false; syncLoopButton(); });
    galleryStageVideo.addEventListener('pause', syncLoopButton);
    if (galleryStagePause) galleryStagePause.addEventListener('click', function () {
      // The shopper's choice holds at once, before the media 'pause' event.
      if (!galleryStageVideo.paused) { loopHeldByShopper = true; galleryStageVideo.pause(); }
      else {
        loopHeldByShopper = false;
        var playing = galleryStageVideo.play();
        if (playing && playing.catch) playing.catch(syncLoopButton);
      }
      syncLoopButton();
    });
    new MutationObserver(syncGalleryVideo).observe(root, { attributes: true, attributeFilter: ['data-llsj-step'] });
    document.addEventListener('visibilitychange', function () { if (document.hidden) hideGalleryVideo(); else syncGalleryVideo(); });
    window.addEventListener('pagehide', hideGalleryVideo);
  }

  // A continuous strip keeps both neighbouring photos under the finger.
  // The original image remains the accessible/current-photo source for the
  // desktop gallery; the mobile strip contains decorative copies only.
  var galleryTrack = null, galleryTrackPosition = 1, gallerySettleTimer = null;
  var galleryDragOffset = 0, gallerySuppressClickUntil = 0;
  function mobilePhotoTrack() { return guidedEnabled && isMobileGalleryViewport() && galleryThumbs.length > 1; }
  function galleryDuration() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300; }
  /* Shopify's image CDN resizes by the URL's width= parameter, so a phone
     takes a copy sized to its screen instead of the 1400px desktop one. The
     stage image's srcset in ll-storybook.liquid lists the same widths, so the
     first slide shares its download. */
  var GALLERY_WIDTHS = [600, 900, 1200, 1400];
  function gallerySrcset(src) {
    if (!/[?&]width=\d+/.test(src || '')) return '';
    return GALLERY_WIDTHS.map(function (width) { return src.replace(/([?&]width=)\d+/, '$1' + width) + ' ' + width + 'w'; }).join(', ');
  }
  function ensureGalleryTrack() {
    if (galleryTrack || !mobilePhotoTrack() || !galleryStageMedia) return;
    galleryTrack = document.createElement('div'); galleryTrack.className = 'llsb__gallery-track';
    galleryTrack.setAttribute('aria-hidden', 'true');
    var ids = galleryThumbs.map(function (thumb) { return thumb.getAttribute('data-llsb-gthumb'); });
    [ids[ids.length - 1]].concat(ids, [ids[0]]).forEach(function (id) {
      var media = (config.media || []).filter(function (item) { return item.id === id; })[0];
      // Lazy: only the slides near the one on screen download on arrival.
      var image = document.createElement('img'); image.loading = 'lazy'; image.decoding = 'async';
      var srcset = gallerySrcset(media.src);
      if (srcset) { image.sizes = '100vw'; image.srcset = srcset; }
      image.src = media.src; image.alt = ''; image.draggable = false;
      image.className = 'llsb__gallery-slide'; galleryTrack.appendChild(image);
    });
    galleryStage.appendChild(galleryTrack);
    settleGalleryTrack(currentGallerySlideIndex() + 1, false);
  }
  function trackOffset() {
    if (!galleryTrack) return 0;
    return new DOMMatrixReadOnly(getComputedStyle(galleryTrack).transform).m41;
  }
  function setTrackOffset(offset, duration) {
    galleryTrack.style.transition = duration ? 'transform ' + duration + 'ms ease-out' : 'none';
    galleryTrack.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
  }
  function cancelGalleryMotion() {
    clearTimeout(gallerySettleTimer); gallerySettleTimer = null;
    if (galleryTrack) setTrackOffset(trackOffset(), 0);
    galleryDragOffset = 0;
  }
  function settleGalleryTrack(position, animate) {
    if (!galleryTrack) return;
    if (animate) hideGalleryVideo();
    cancelGalleryMotion(); galleryTrackPosition = position;
    var duration = animate ? galleryDuration() : 0;
    setTrackOffset(-position * galleryStage.clientWidth, duration);
    function normalise() {
      gallerySettleTimer = null;
      if (position === 0 || position === galleryThumbs.length + 1) {
        galleryTrackPosition = position === 0 ? galleryThumbs.length : 1;
        setTrackOffset(-galleryTrackPosition * galleryStage.clientWidth, 0);
      }
      syncGalleryVideo();
    }
    if (duration) gallerySettleTimer = setTimeout(normalise, duration + 20);
    else normalise();
  }
  function setGalleryPhoto(media, direction) {
    ensureGalleryTrack();
    galleryStageMedia.draggable = false;
    // The srcset outranks src, so it has to follow the chosen photo too.
    var stageSrcset = gallerySrcset(media.src);
    function apply() {
      if (stageSrcset) galleryStageMedia.srcset = stageSrcset; else galleryStageMedia.removeAttribute('srcset');
      galleryStageMedia.src = media.src; galleryStageMedia.alt = media.alt || '';
    }
    // Desktop slides between photos; phones already drag the strip below.
    var journey = window.LLStorybookJourney;
    if (!mobilePhotoTrack() && journey && journey.slidePhoto) journey.slidePhoto(galleryStage, galleryStageMedia, direction, apply, { src: media.src, srcset: stageSrcset, sizes: galleryStageMedia.sizes });
    else apply();
    if (mobilePhotoTrack() && galleryTrack) {
      var index = galleryThumbs.map(function (thumb) { return thumb.getAttribute('data-llsb-gthumb'); }).indexOf(media.id);
      var position = index + 1;
      if (direction === 1 && currentGallerySlideIndex() === galleryThumbs.length - 1 && index === 0) position = galleryThumbs.length + 1;
      if (direction === -1 && currentGallerySlideIndex() === 0 && index === galleryThumbs.length - 1) position = 0;
      settleGalleryTrack(position, !!direction);
    }
  }
  function showGallerySlide(id, moveDirection) {
    var isLive = id === 'live';
    if (id !== selectedPhotoId) hideGalleryVideo();
    if (!isLive) {
      var m = (config.media || []).filter(function (x) { return x.id === id; })[0];
      if (!m || !galleryStageMedia) return;
      var nextIndex = galleryThumbs.map(function (thumb) { return thumb.getAttribute('data-llsb-gthumb'); }).indexOf(id);
      var direction = id === selectedPhotoId ? 0 : nextIndex > currentGallerySlideIndex() ? 1 : -1;
      setGalleryPhoto(m, moveDirection || direction);
      selectedPhotoId = id;
    }
    if (isLive) cancelGalleryMotion();
    galleryMode = isLive ? 'preview' : 'photos';
    if (galleryStage) galleryStage.classList.toggle('llsb__stage--media', !isLive);
    if (galleryStageMedia) galleryStageMedia.hidden = isLive;
    galleryThumbs.forEach(function (thumb) {
      thumb.setAttribute('aria-pressed', thumb.getAttribute('data-llsb-gthumb') === selectedPhotoId ? 'true' : 'false');
    });
    syncGalleryControls();
    syncGalleryVideo();
  }

  galleryThumbs.forEach(function (thumb) {
    thumb.addEventListener('click', function () { showGallerySlide(thumb.getAttribute('data-llsb-gthumb')); });
  });
  var watchBook = root.querySelector('[data-llsb-watch]');
  if (watchBook) {
    watchBook.hidden = false;
    watchBook.addEventListener('click', function () {
      showGallerySlide(watchBook.getAttribute('data-llsb-watch'));
      var gallery = root.querySelector('[data-llsb-gallery]');
      gallery.focus({ preventScroll: true });
      gallery.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
  }
  if (galleryModes) galleryModes.querySelectorAll('[data-llsb-mode]').forEach(function (button) {
    button.addEventListener('click', function () {
      showGallerySlide(button.getAttribute('data-llsb-mode') === 'photos' ? selectedPhotoId : 'live');
    });
  });

  function currentGallerySlideIndex() {
    for (var i = 0; i < galleryThumbs.length; i++) {
      if (galleryThumbs[i].getAttribute('data-llsb-gthumb') === selectedPhotoId) return i;
    }
    return 0;
  }

  function moveGallerySlide(delta) {
    if (galleryMode !== 'photos' || (state.offer !== 'single' && !guidedEnabled)) return;
    var nextIndex = currentGallerySlideIndex() + delta;
    if (mobilePhotoTrack()) nextIndex = (nextIndex + galleryThumbs.length) % galleryThumbs.length;
    if (nextIndex < 0 || nextIndex >= galleryThumbs.length) return;
    var thumb = galleryThumbs[nextIndex];
    showGallerySlide(thumb.getAttribute('data-llsb-gthumb'), delta);
    if (photoThumbs) {
      var railRect = photoThumbs.getBoundingClientRect();
      var rect = thumb.getBoundingClientRect();
      if (isMobileGalleryViewport()) {
        if (rect.left < railRect.left) photoThumbs.scrollLeft -= railRect.left - rect.left;
        else if (rect.right > railRect.right) photoThumbs.scrollLeft += rect.right - railRect.right;
      } else {
        if (rect.top < railRect.top) photoThumbs.scrollTop -= railRect.top - rect.top;
        else if (rect.bottom > railRect.bottom) photoThumbs.scrollTop += rect.bottom - railRect.bottom;
      }
    }
  }
  if (photoPrev) photoPrev.addEventListener('click', function () { moveGallerySlide(-1); });
  if (photoNext) photoNext.addEventListener('click', function () { moveGallerySlide(1); });
  if (photoThumbs) photoThumbs.addEventListener('scroll', updatePhotoOverflow, { passive: true });
  if (morePhotos) morePhotos.addEventListener('click', function () {
    var horizontal = isMobileGalleryViewport();
    if (horizontal) photoThumbs.scrollLeft += photoThumbs.clientWidth * 0.8;
    else photoThumbs.scrollTop += photoThumbs.clientHeight * 0.8;
    updatePhotoOverflow();
  });
  window.addEventListener('resize', function () {
    stagePointerStart = null; ensureGalleryTrack();
    settleGalleryTrack(currentGallerySlideIndex() + 1, false); syncGalleryControls();
    syncGalleryVideo();
  });

  if (window.ResizeObserver && galleryStage) {
    var galleryWidth = galleryStage.clientWidth;
    new ResizeObserver(function () {
      var width = galleryStage.clientWidth;
      if (width && width !== galleryWidth) {
        galleryWidth = width; stagePointerStart = null; ensureGalleryTrack();
        settleGalleryTrack(currentGallerySlideIndex() + 1, false);
      }
    }).observe(galleryStage);
  }

  var STAGE_SWIPE_THRESHOLD = 40;
  var stagePointerStart = null;
  var gallerySwipeSurface = guidedEnabled ? root.querySelector('[data-llsb-gallery]') : galleryStage;
  function resetGalleryDrag() { settleGalleryTrack(currentGallerySlideIndex() + 1, true); }
  if (galleryStage) {
    galleryStage.addEventListener('keydown', function (event) {
      if (event.target === galleryStageVideo) return;
      if (galleryMode !== 'photos' || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); moveGallerySlide(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });
    gallerySwipeSurface.addEventListener('click', function (event) {
      if (Date.now() < gallerySuppressClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, true);
    gallerySwipeSurface.addEventListener('pointerdown', function (event) {
      gallerySuppressClickUntil = 0;
      if (galleryMode !== 'photos' || (state.offer !== 'single' && !guidedEnabled)) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea, label') && !event.target.closest('[data-llsb-gthumb]')) return;
      // Leave the native playback/scrubbing controls to the browser.
      if (event.target === galleryStageVideo && galleryStageVideo.controls && event.clientY > galleryStageVideo.getBoundingClientRect().bottom - 56) return;
      ensureGalleryTrack(); cancelGalleryMotion();
      // Rebase a completed loop before a fresh drag. Interrupted movement
      // starts at its rendered position, so rapid swipes never jump.
      if (galleryTrack && (galleryTrackPosition === 0 || galleryTrackPosition === galleryThumbs.length + 1)) {
        var shift = galleryTrackPosition === 0 ? -galleryThumbs.length : galleryThumbs.length;
        setTrackOffset(trackOffset() + shift * galleryStage.clientWidth, 0);
        galleryTrackPosition = currentGallerySlideIndex() + 1;
      }
      stagePointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY, offset: trackOffset(), dragging: false };
    });
    gallerySwipeSurface.addEventListener('pointermove', function (event) {
      if (!stagePointerStart || event.pointerId !== stagePointerStart.id || !isMobileGalleryViewport()) return;
      var dx = event.clientX - stagePointerStart.x, dy = event.clientY - stagePointerStart.y;
      if (!stagePointerStart.dragging && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { stagePointerStart = null; resetGalleryDrag(); return; }
      if (!stagePointerStart.dragging && (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy))) return;
      stagePointerStart.dragging = true;
      hideGalleryVideo();
      if (gallerySwipeSurface.setPointerCapture && event.isTrusted) gallerySwipeSurface.setPointerCapture(event.pointerId);
      galleryDragOffset = Math.max(-galleryStage.clientWidth, Math.min(galleryStage.clientWidth, dx * 0.5));
      if (galleryTrack && mobilePhotoTrack()) setTrackOffset(stagePointerStart.offset + galleryDragOffset, 0);
    });
    gallerySwipeSurface.addEventListener('pointerup', function (event) {
      if (!stagePointerStart || event.pointerId !== stagePointerStart.id) return;
      var start = stagePointerStart; stagePointerStart = null;
      var dx = event.clientX - start.x, dy = event.clientY - start.y;
      if (start.dragging) gallerySuppressClickUntil = Date.now() + 400;
      if (!start.dragging && Math.abs(dx) < STAGE_SWIPE_THRESHOLD) { syncGalleryVideo(); return; }
      if (Math.abs(dx) < STAGE_SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) { resetGalleryDrag(); return; }
      moveGallerySlide(dx < 0 ? 1 : -1);
    });
    gallerySwipeSurface.addEventListener('pointercancel', function () { stagePointerStart = null; resetGalleryDrag(); });
    gallerySwipeSurface.addEventListener('lostpointercapture', function (event) {
      // Moving implicit touch capture from an image/dot to the gallery
      // bubbles a loss event from that child; the drag is still active.
      if (event.target !== gallerySwipeSurface) return;
      if (stagePointerStart) { stagePointerStart = null; resetGalleryDrag(); }
    });
  }

  function defaultChildName() {
    return (viewConfig.previewDefaults && viewConfig.previewDefaults.childName) || 'Your child';
  }

  function fixedDedication() {
    var policy = viewConfig.dedicationPolicy;
    if (policy && policy.mode === 'fixed' && typeof policy.text === 'string' && policy.text.trim()) {
      return policy.text;
    }
    /* Kept in sync with the Liquid dedicationPolicy default (task 11d item
       3d) even though the section always emits a policy today, so this
       fallback never actually runs in production. */
    return 'For [NAME], this special story was made just for you,\nwith so much love and care.\nAlways remember how loved you are,\nand how much joy you bring to everyone around you.';
  }

  function fillTemplate(tpl) {
    return fillStorybookTemplate(
      tpl,
      previewChild().name || defaultChildName(),
      fixedDedication()
    );
  }

  /* Bubblegum Sans carries the selected cover lockup. Luckiest Guy remains a
     deterministic fallback for unsupported customer-name glyphs. */
  var SCRIPT_CHARSET = ' !"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~';
  var LATIN_EXPANSIONS = {
    'æ': 'ae', 'Æ': 'AE', 'œ': 'oe', 'Œ': 'OE', 'ß': 'ss',
    'ø': 'o', 'Ø': 'O', 'đ': 'd', 'Đ': 'D', 'ł': 'l', 'Ł': 'L',
    'þ': 'th', 'Þ': 'Th', 'ð': 'd', 'Ð': 'D'
  };
  var PUNCTUATION_FOLD = {
    '’': "'", '‘': "'", '‚': ',', '“': '"', '”': '"', '„': '"',
    '–': '-', '—': '-', '‑': '-', '…': '...', ' ': ' '
  };

  function foldToCharset(text, charset) {
    var out = '';
    for (var i = 0; i < text.length; i++) {
      var ch = text.charAt(i);
      if (ch === '\n' || charset.indexOf(ch) >= 0) { out += ch; continue; }
      var replacement = PUNCTUATION_FOLD[ch] || LATIN_EXPANSIONS[ch];
      if (replacement == null && String.prototype.normalize) {
        /* strip combining marks: é -> e, ñ -> n */
        replacement = ch.normalize('NFKD').replace(/[̀-ͯ]/g, '');
        if (replacement === ch) replacement = '';
      }
      if (!replacement) continue;
      for (var j = 0; j < replacement.length; j++) {
        var folded = replacement.charAt(j);
        if (charset.indexOf(folded) >= 0) out += folded;
      }
    }
    return out;
  }

  /* Text as the press will set it: template filled, then folded onto the face's
     own repertoire. Only the script face is limited; Crimson Pro covers Latin. */
  function styleText(template, isScriptFace) {
    var filled = fillTemplate(template);
    return isScriptFace ? foldToCharset(filled, SCRIPT_CHARSET) : filled;
  }

  function styleFontFamily(style) {
    if (!style) return "'Crimson Pro', Georgia, serif";
    if (style.font === 'script' || style.family === 'Bubblegum Sans') return "'Bubblegum Sans', 'Luckiest Guy', cursive";
    if (style.family === 'Cheering Section') return "'Cheering Section', 'Luckiest Guy', cursive";
    if (style.font === 'crimson') return "'Crimson Pro', Georgia, serif";
    return style.font || (style.family ? "'" + style.family + "', Georgia, serif" : "'Crimson Pro', Georgia, serif");
  }

  function isCoverDisplayFace(style) {
    return !!style && (style.font === 'script' || style.family === 'Bubblegum Sans' || style.family === 'Cheering Section');
  }

  function styleStrokeColour(style) {
    if (!style || !style.stroke) return null;
    if (style.strokeAlpha == null || style.strokeAlpha === 255) return style.stroke;
    return 'rgba(' +
      parseInt(style.stroke.slice(1, 3), 16) + ', ' +
      parseInt(style.stroke.slice(3, 5), 16) + ', ' +
      parseInt(style.stroke.slice(5, 7), 16) + ', ' +
      String(style.strokeAlpha / 255) + ')';
  }

  /* Shallow-clone a style object with one field overridden, without
     mutating the shared viewConfig.styles entry other spreads/renders still
     read. Used below to cap the cover-name placeholder's fit ceiling on the
     canvas proof path — task 11c item 4. */
  function cloneStyleWithSize(style, size) {
    var copy = {};
    for (var key in style) {
      if (Object.prototype.hasOwnProperty.call(style, key)) copy[key] = style[key];
    }
    copy.size = size;
    return copy;
  }

  /* Canvas mirror of the compositor's fit_text_block (see
     scripts/storybook_build/build_storybook_canva_box_proof.py): preserve the
     authored verse lines when requested, otherwise wrap on word boundaries,
     then step the size down until the block fits the authored box in both axes,
     bottoming out at the style's own floor. The proof travels with the order,
     so it has to agree with what the press will set. */
  function fitCanvasText(ctx, text, style, boxWidthPx, boxHeightPx, pageHeightPx) {
    var authored = style.size || 84;
    var floor = style.min != null ? style.min : authored * 0.55;
    var leading = style.leading || 1.05;
    var prefix = (style.italic ? 'italic ' : '') + (style.weight || 400) + ' ';
    var suffix = 'px ' + styleFontFamily(style);
    var best = null;
    var bestCanvaSize = null;
    var fitted = false;

    function measureAt(canvaSize) {
      var fontPx = pageHeightPx * (canvaSize / 779);
      ctx.font = prefix + fontPx + suffix;
      var lines = [];
      var paragraphs = String(text).split(/\r?\n/);
      for (var p = 0; p < paragraphs.length; p++) {
        if (style.preserveLineBreaks === true) {
          lines.push(paragraphs[p].trim());
          continue;
        }
        var words = paragraphs[p].split(/\s+/).filter(Boolean);
        if (!words.length) { lines.push(''); continue; }
        var current = '';
        for (var w = 0; w < words.length; w++) {
          var trial = current ? current + ' ' + words[w] : words[w];
          if (!current || ctx.measureText(trial).width <= boxWidthPx) current = trial;
          else { lines.push(current); current = words[w]; }
        }
        lines.push(current);
      }
      var widest = 0;
      for (var i = 0; i < lines.length; i++) widest = Math.max(widest, ctx.measureText(lines[i]).width);
      var lineHeightPx = fontPx * leading;
      return {
        result: { lines: lines, fontPx: fontPx, lineHeightPx: lineHeightPx },
        fits: lineHeightPx * lines.length <= boxHeightPx && widest <= boxWidthPx
      };
    }

    for (var canvaSize = authored; canvaSize >= floor; canvaSize -= 2) {
      var measured = measureAt(canvaSize);
      best = measured.result;
      bestCanvaSize = canvaSize;
      if (measured.fits) {
        fitted = true;
        break;
      }
    }
    /* The compositor decrements by two, then explicitly renders its exact
       minimum when that stepping skips over it. Without this final measure a
       53→26 style bottoms out at 27 and can disagree with the printed cover. */
    if (!fitted && (bestCanvaSize == null || Math.abs(bestCanvaSize - floor) > 0.000001)) {
      best = measureAt(floor).result;
    }
    ctx.font = prefix + best.fontPx + suffix;
    return best;
  }

  function createBookNavButton(direction) {
    var isPrevious = direction === 'previous';
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'llsb-book__nav llsb-book__nav--' + direction;
    button.setAttribute('data-llsb-book-' + (isPrevious ? 'prev' : 'next'), '');
    button.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' +
      (isPrevious ? 'm15 18-6-6 6-6' : 'm9 6 6 6-6 6') +
      '" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '<span class="llsb-book__nav-text">' + (isPrevious ? 'Previous' : 'Next') + '</span>';
    return button;
  }

  function buildBookViewer() {
    if (!preview.getAttribute('aria-label')) preview.setAttribute('aria-label', 'Story sample');
    preview.setAttribute('tabindex', '0');

    var meta = document.createElement('div');
    meta.className = 'llsb-book__meta';
    bookLabel = document.createElement('p');
    bookLabel.className = 'llsb-book__label';
    bookLabel.setAttribute('role', 'status');
    bookLabel.setAttribute('aria-live', 'polite');
    bookCounter = document.createElement('p');
    bookCounter.className = 'llsb-book__counter';
    meta.appendChild(bookLabel);
    meta.appendChild(bookCounter);

    var progressTrack = document.createElement('div');
    progressTrack.className = 'llsb-book__progress-track';
    progressTrack.setAttribute('aria-hidden', 'true');
    bookProgress = document.createElement('span');
    bookProgress.className = 'llsb-book__progress';
    progressTrack.appendChild(bookProgress);

    var stage = document.createElement('div');
    stage.className = 'llsb-book__stage';
    previousPageButton = createBookNavButton('previous');
    nextPageButton = createBookNavButton('next');
    bookViewport = document.createElement('div');
    bookViewport.className = 'llsb-book__viewport';
    bookViewport.id = 'llsb-book-pages';
    stage.appendChild(previousPageButton);
    stage.appendChild(bookViewport);
    stage.appendChild(nextPageButton);

    /* Round 7 (owner): the "Click or tap the left side..." / "Tap the left
       or right side..." caption is gone — its own text height plus
       .llsb__preview's padding-bottom (previously sized to clear it) were
       what pushed .llsb__gthumbs (the gallery thumb rail, a sibling of
       .llsb__stage in the markup — see theme/sections/ll-storybook.liquid)
       visually far down below the book stage on desktop; on mobile the
       caption was already sr-only (see the round-3/round-5 mobile
       .llsb-book__help rule, also removed) so this is a no-op there. Page-
       turn affordance is unchanged: the overlay Previous/Next arrows are
       always visible, and turnBookAt()/the pointerup swipe handling below
       still claim left/right taps and horizontal swipes exactly as before —
       nothing here depended on the caption's text or its
       isMobileGalleryViewport() breakpoint switch. */
    [meta, progressTrack, stage].forEach(function (node) {
      preview.appendChild(node);
    });

    previousPageButton.addEventListener('click', function () { moveBook(-1); });
    nextPageButton.addEventListener('click', function () { moveBook(1); });
    if (!preview.hasAttribute('data-llsb-keyboard-bound')) {
    preview.setAttribute('data-llsb-keyboard-bound', 'true');
    preview.addEventListener('keydown', function (event) {
      if (event.altKey || event.ctrlKey || event.metaKey || isScrollingBookReader()) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveBook(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveBook(1);
      }
    });

    }

    function activeBookFrame() {
      return bookViewport.querySelector('.llsb-spread:not([hidden]) .llsb-spread__frame');
    }

    function pointIsInside(rect, x, y) {
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    }

    function turnBookAt(x, y) {
      var frame = activeBookFrame();
      if (!frame) return;
      var rect = frame.getBoundingClientRect();
      if (!pointIsInside(rect, x, y)) return;
      moveBook(x < rect.left + rect.width / 2 ? -1 : 1);
    }

    var pointerStart = null;
    bookViewport.addEventListener('pointerdown', function (event) {
      if (isScrollingBookReader()) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea, label')) return;
      var frame = activeBookFrame();
      if (!frame || !pointIsInside(frame.getBoundingClientRect(), event.clientX, event.clientY)) return;
      pointerStart = { id: event.pointerId, type: event.pointerType, x: event.clientX, y: event.clientY };
    });
    bookViewport.addEventListener('pointerup', function (event) {
      if (!pointerStart || event.pointerId !== pointerStart.id) return;
      var start = pointerStart;
      pointerStart = null;
      var dx = event.clientX - start.x;
      var dy = event.clientY - start.y;

      if ((guidedEnabled || (galleryMode === 'preview' && state.offer === 'single')) && start.type !== 'mouse' && Math.abs(dx) >= 48 && Math.abs(dx) >= Math.abs(dy) * 1.2) {
        moveBook(dx < 0 ? 1 : -1);
        return;
      }
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) return;

      var selection = window.getSelection && window.getSelection();
      if (selection && !selection.isCollapsed) return;
      turnBookAt(event.clientX, event.clientY);
    });
    bookViewport.addEventListener('pointercancel', function () { pointerStart = null; });

    /* Phones list each spread at the screen's width, where its words are
       small: a tap opens that spread larger in a full-screen layer to pan
       across. A reading aid, not a page mode (the list stays two-page). */
    bookViewport.addEventListener('click', function (event) {
      if (!isScrollingBookReader() || !isMobileGalleryViewport()) return;
      if (event.target.closest('a, button, input, select, textarea, label')) return;
      var frame = event.target.closest('.llsb-spread:not(.llsb-spread--single) .llsb-spread__frame');
      if (frame) openSpreadZoom(frame, event.clientX);
    });
  }

  var spreadZoom = null;
  function closeSpreadZoom() {
    if (!spreadZoom) return;
    var layer = spreadZoom;
    spreadZoom = null;
    document.removeEventListener('keydown', layer.llsbKeys, true);
    document.documentElement.style.removeProperty('overflow');
    layer.remove();
    if (layer.llsbReturn && document.contains(layer.llsbReturn)) layer.llsbReturn.focus({ preventScroll: true });
  }
  function openSpreadZoom(frame, tapX) {
    closeSpreadZoom();
    var rect = frame.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var scale = Math.max(1.5, Math.min(2.6, (window.innerHeight - 140) / rect.height));
    var spread = frame.closest('.llsb-spread');
    var label = spread.getAttribute('aria-label') || '';
    var layer = document.createElement('div');
    layer.className = 'llsb-zoom';
    layer.setAttribute('role', 'dialog');
    layer.setAttribute('aria-modal', 'true');
    layer.setAttribute('aria-label', label ? label + ', enlarged' : 'Enlarged page');
    var bar = document.createElement('div');
    bar.className = 'llsb-zoom__bar';
    var hint = document.createElement('p');
    hint.className = 'llsb-zoom__hint';
    hint.textContent = 'Drag to look around';
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'llsb-zoom__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
    close.addEventListener('click', closeSpreadZoom);
    bar.appendChild(hint);
    bar.appendChild(close);
    var scroller = document.createElement('div');
    scroller.className = 'llsb-zoom__scroller';
    var sizer = document.createElement('div');
    sizer.className = 'llsb-zoom__sizer';
    sizer.style.width = Math.round(rect.width * scale) + 'px';
    sizer.style.height = Math.round(rect.height * scale) + 'px';
    /* The copy keeps the fitted text's pixel sizes, so a uniform scale
       enlarges the page exactly; the art asks for a sharper copy. */
    var copyFrame = frame.cloneNode(true);
    copyFrame.removeAttribute('id');
    copyFrame.style.width = rect.width + 'px';
    copyFrame.style.height = rect.height + 'px';
    copyFrame.style.transform = 'scale(' + scale + ')';
    copyFrame.querySelectorAll('img').forEach(function (image) {
      image.loading = 'eager';
      if (image.srcset) image.sizes = Math.round(rect.width * scale) + 'px';
    });
    sizer.appendChild(copyFrame);
    scroller.appendChild(sizer);
    scroller.addEventListener('click', function (event) { if (event.target === scroller) closeSpreadZoom(); });
    layer.appendChild(bar);
    layer.appendChild(scroller);
    layer.llsbReturn = preview;
    layer.llsbKeys = function (event) {
      if (event.key === 'Escape') { event.preventDefault(); closeSpreadZoom(); }
      else if (event.key === 'Tab') { event.preventDefault(); close.focus(); }
    };
    document.addEventListener('keydown', layer.llsbKeys, true);
    document.documentElement.style.overflow = 'hidden';
    document.body.appendChild(layer);
    spreadZoom = layer;
    /* Open on the page the shopper tapped, centred on its words (else on
       the child's place), so a sentence is never cut at the edge. */
    var tapped = typeof tapX === 'number' ? Math.max(0, Math.min(1, (tapX - rect.left) / rect.width)) : 0.5;
    var targets = Array.prototype.map.call(frame.querySelectorAll('[data-llsb-tpl], .llsb-char-zone'), function (node) {
      var box = node.getBoundingClientRect();
      return { words: node.hasAttribute('data-llsb-tpl'), centre: box.width ? (box.left + box.width / 2 - rect.left) / rect.width : -1 };
    }).filter(function (target) { return target.centre >= 0 && (target.centre < 0.5) === (tapped < 0.5); });
    targets.sort(function (a, b) { return (b.words ? 1 : 0) - (a.words ? 1 : 0); });
    var centre = targets.length ? targets[0].centre : tapped;
    scroller.scrollLeft = Math.max(0, centre * rect.width * scale - scroller.clientWidth / 2);
    close.focus({ preventScroll: true });
  }
  window.addEventListener('resize', function () { if (spreadZoom && !isMobileGalleryViewport()) closeSpreadZoom(); });
  window.addEventListener('popstate', closeSpreadZoom);

  /* Round 5 item A: the viewer's Previous/Next controls used to move every
     time a page turn swapped a square single (tall) for a 2:1 spread
     (short) — the owner's exact complaint. .llsb-book__viewport's own
     width stays the full grid track (unclamped) at every breakpoint
     because narrowing it to the single page's width left a spread with
     excessive horizontal whitespace, so a fixed height that still equals
     the single page's height at that unclamped width isn't reachable with
     CSS aspect-ratio alone (aspect-ratio only relates a box to its own
     width, not a narrower sibling's). This reproduces
     .llsb-spread--single .llsb-spread__frame's own
     width: clamp(360px, 58%, 700px) / aspect-ratio: 800 / 806 math
     (ll-storybook.css) against the measured track and sets the result as
     an inline height, so the viewport is always exactly as tall as a
     single page and CSS (align-items: center, on the viewport; the flex
     rules there) centres a shorter spread frame inside it. Below 990px
     the CSS media query gives the viewport its own aspect-ratio directly
     (its width already equals the single page's there), so this clears
     the inline height and steps aside. Recompute on boot, window resize
     and observed track-width changes after gallery reflow. The gallery's
     fixed grid track keeps this independent of which page is showing. */
  /* The journey's preview steps list every page one under another at every
     width, Wonderbly-style (owner, 2026-10-13); phones did first. Without
     the journey (a plain page, or one whose journey failed to mount) desktop
     keeps the paged reader. */
  function isScrollingBookReader() {
    return guidedEnabled && (Boolean(journey) || isMobileGalleryViewport());
  }

  function syncBookReaderLayout() {
    if (!guidedEnabled || !bookViewport) return;
    var scrolling = isScrollingBookReader();
    preview.classList.toggle('llsb__preview--scrolling', scrolling);
    spreadEls.forEach(function (spread, index) {
      spread.hidden = !scrolling && index !== currentSpreadIndex;
      spread.setAttribute('aria-hidden', spread.hidden ? 'true' : 'false');
    });
  }

  function syncBookViewportHeight() {
    if (!bookViewport) return;
    syncBookReaderLayout();
    if (isMobileGalleryViewport() || isScrollingBookReader()) {
      bookViewport.style.removeProperty('height');
    } else {
      var trackWidth = bookViewport.clientWidth;
      if (trackWidth) {
        var singleWidth = Math.min(trackWidth, 700, Math.max(360, trackWidth * 0.58));
        bookViewport.style.height = (singleWidth * (806 / 800)) + 'px';
      }
    }
    syncBackgroundSizes();
    syncSpreadPages();
  }

  /* Shopify's image CDN (cdn.shopify.com files and /cdn/shop/ theme assets)
     serves any smaller width of an image on request and never enlarges one.
     So each page's single master gives every size: a phone fetches a lighter
     copy, and a 2,400px master stays sharp across a large desktop list. Any
     other host (the local fixtures) keeps its one source. */
  var SPREAD_IMAGE_WIDTHS = [700, 1000, 1400, 1800, 2400];
  var SINGLE_IMAGE_WIDTHS = [450, 700, 900, 1200];
  function shopifyImageSrcset(url, widths) {
    url = String(url || '');
    if (!/^(?:(?:https?:)?\/\/(?:cdn\.shopify\.com\/|[^/]+\/cdn\/shop\/)|\/cdn\/shop\/)/.test(url) || /[?&]width=/.test(url)) return '';
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    return widths.map(function (width) { return url + separator + 'width=' + width + ' ' + width + 'w'; }).join(', ');
  }
  /* sizes follows each page's drawn width, so the browser picks the copy
     the screen needs; a page not drawn yet borrows the viewer's width. */
  function syncBackgroundSizes() {
    var track = bookViewport && bookViewport.clientWidth;
    if (!track) return;
    /* Past 2x density a sharper copy looks no sharper, so a 3x phone, which
       draws each spread twice the screen's width, fetches the 2x copy. */
    var density = Math.min(1, 2 / (window.devicePixelRatio || 1));
    spreadEls.forEach(function (spread) {
      var image = spread.querySelector('img.llsb-spread__bg[srcset]');
      if (!image) return;
      var width = image.parentNode.clientWidth || track * (spread.classList.contains('llsb-spread--single') ? 0.6 : 1);
      var sizes = Math.max(1, Math.round(width * density)) + 'px';
      if (image.sizes !== sizes) image.sizes = sizes;
    });
  }

  /* Guided pages show the book only on the journey's sample and review steps
     (showJourneyPreview). Until the journey opens it, and whenever the viewer
     is hidden, no page downloads, which keeps the product page light on
     phones; showBookPage and the viewport observer warm the pages once shown. */
  var journeyPreviewShown = false;
  function warmSpread(index) {
    var spread = spreadEls[index];
    if (!spread || !bookViewport || (guidedEnabled && !journeyPreviewShown) || !bookViewport.getClientRects().length) return;
    spread.querySelectorAll('img').forEach(function (image) {
      image.loading = 'eager';
      if (index === currentSpreadIndex) image.fetchPriority = 'high';
    });
  }

  function showBookPage(index, direction) {
    if (index < 0 || index >= spreadEls.length) return;
    currentSpreadIndex = index;
    spreadEls.forEach(function (spread, spreadIndex) {
      spread.hidden = spreadIndex !== index;
      spread.classList.remove('is-active', 'is-turning-forward', 'is-turning-back');
      spread.setAttribute('aria-hidden', spreadIndex === index ? 'false' : 'true');
    });

    var activeSpread = spreadEls[index];
    activeSpread.classList.add('is-active');
    if (direction) {
      activeSpread.classList.add(direction === 'forward' ? 'is-turning-forward' : 'is-turning-back');
      window.setTimeout(function () {
        activeSpread.classList.remove('is-turning-forward', 'is-turning-back');
      }, 320);
    }

    var spread = viewConfig.spreads[index];
    bookLabel.textContent = spread.label;
    bookCounter.textContent = (index + 1) + ' / ' + spreadEls.length;
    bookProgress.style.transform = 'scaleX(' + ((index + 1) / spreadEls.length) + ')';
    previousPageButton.disabled = index === 0;
    nextPageButton.disabled = index === spreadEls.length - 1;
    previousPageButton.setAttribute('aria-label', index > 0 ? 'Previous page: ' + viewConfig.spreads[index - 1].label : 'Previous page');
    nextPageButton.setAttribute('aria-label', index < spreadEls.length - 1 ? 'Next page: ' + viewConfig.spreads[index + 1].label : 'Next page');

    warmSpread(index);
    warmSpread(index + 1);
    syncBookReaderLayout();
    refreshText();
  }

  function moveBook(delta) {
    var nextIndex = currentSpreadIndex + delta;
    if (nextIndex < 0 || nextIndex >= spreadEls.length) return;
    showBookPage(nextIndex, delta > 0 ? 'forward' : 'back');
  }

  function renderSpreads() {
    buildBookViewer();
    viewConfig.spreads.forEach(function (spread) {
      var wrap = document.createElement('section');
      wrap.className = 'llsb-spread' + (spread.layout === 'single' ? ' llsb-spread--single' : '');
      wrap.hidden = true;
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-roledescription', 'page');
      wrap.setAttribute('aria-label', spread.label);
      wrap.setAttribute('data-llsb-spread-id', spread.id);
      wrap.setAttribute('data-llsb-spread-layout', spread.layout || 'spread');
      var frame = document.createElement('div');
      frame.className = 'llsb-spread__frame';
      var bg = document.createElement('img');
      bg.className = 'llsb-spread__bg';
      bg.loading = 'lazy';
      bg.decoding = 'async';
      var bgSrcset = shopifyImageSrcset(spread.bg, spread.layout === 'single' ? SINGLE_IMAGE_WIDTHS : SPREAD_IMAGE_WIDTHS);
      if (bgSrcset) {
        bg.sizes = (bookViewport.clientWidth ? Math.round(bookViewport.clientWidth * (spread.layout === 'single' ? 0.6 : 1)) + 'px' : '100vw');
        bg.srcset = bgSrcset;
      }
      bg.src = spread.bg;
      bg.alt = spread.description || spread.label;
      /* The frame's aspect-ratio fixes its size before any art arrives, so a
         listed page needs no refit when its art loads; only the paged reader
         refits the page it has just turned to. */
      bg.addEventListener('load', function () {
        if (!wrap.hidden && !isScrollingBookReader()) refreshText();
      });
      frame.appendChild(bg);

      /* Luna's checked-in fallback calls this page "dedication". The
         published Rainbow and Dino preview metafields use
         "dedication-page" for the same page. Keep the decoration keyed to
         both canonical IDs so every US story receives the framed photo and
         three-cover range. */
      if (spread.id === 'dedication' || spread.id === 'dedication-page') {
        var dedicationPhoto = document.createElement('div');
        dedicationPhoto.className = 'llsb-dedication-photo';
        dedicationPhoto.hidden = true;
        dedicationPhoto.setAttribute('data-llsb-dedication-photo', '');
        dedicationPhoto.setAttribute('aria-hidden', 'true');
        var dedicationPhotoImage = document.createElement('img');
        dedicationPhotoImage.alt = '';
        dedicationPhotoImage.decoding = 'async';
        dedicationPhoto.appendChild(dedicationPhotoImage);
        /* The print compositor draws a Black Oak frame around the dedication
           photo (compose_dedication_photo, build_storybook_canva_box_proof.py),
           sized to that photo's own shape. This layer holds the same frame as
           SVG (storybookBlackOakFrameSvg), drawn for each photo's box by
           syncDedicationPhotoPreview below and stacked after the photo <img>
           so it paints on top, filling the box whose padding is the rail.
           Decorative and never interactive (CSS: pointer-events: none). */
        var dedicationPhotoFrame = document.createElement('span');
        dedicationPhotoFrame.className = 'llsb-dedication-photo__frame';
        dedicationPhotoFrame.setAttribute('aria-hidden', 'true');
        dedicationPhoto.appendChild(dedicationPhotoFrame);
        frame.appendChild(dedicationPhoto);
      }

      var rangeBooks = introRangeBooks(spread, viewConfig.region, viewConfig.introRange, viewConfig.storyId, viewConfig.introRangeQr);
      var rangeQr = introRangeQrCard(spread, viewConfig.region, viewConfig.introRange, viewConfig.storyId, viewConfig.introRangeQr);
      if (rangeBooks.length) {
        var range = document.createElement('div');
        range.className = 'llsb-intro-range';
        range.setAttribute('data-llsb-intro-range', '');
        /* A book or the QR card that cannot load takes the row with it: a
           short row or a broken image is not the printed page. */
        var dropRange = function () {
          if (!range.parentNode) return;
          range.remove();
          syncDedicationPhotoPreview();
        };
        rangeBooks.forEach(function (book) {
          var bookImage = document.createElement('img');
          bookImage.src = book.src;
          bookImage.alt = book.title + ' hardcover';
          bookImage.loading = 'lazy';
          bookImage.decoding = 'async';
          bookImage.setAttribute('data-llsb-intro-range-story', book.storyId);
          bookImage.style.left = book.left + '%';
          bookImage.style.top = book.top + '%';
          bookImage.style.width = book.width + '%';
          bookImage.addEventListener('error', dropRange);
          range.appendChild(bookImage);
        });
        if (rangeQr) {
          var qrImage = document.createElement('img');
          qrImage.src = rangeQr.src;
          qrImage.alt = rangeQr.alt;
          qrImage.loading = 'lazy';
          qrImage.decoding = 'async';
          qrImage.setAttribute('data-llsb-intro-range-qr', '');
          qrImage.style.left = rangeQr.left + '%';
          qrImage.style.top = rangeQr.top + '%';
          qrImage.style.width = rangeQr.width + '%';
          qrImage.addEventListener('error', dropRange);
          range.appendChild(qrImage);
        }
        frame.appendChild(range);
      }

      (spread.texts || []).forEach(function (t) {
        var st = viewConfig.styles[t.style] || {};
        var textBox = storybookTextBoxGeometry(t, st);
        var el = document.createElement('div');
        el.className = 'llsb-text' + (isCoverDisplayFace(st) ? ' is-script' : '');
        el.style.left = textBox.x + '%';
        el.style.top = textBox.y + '%';
        el.style.width = textBox.w + '%';
        var isFittedDedication = t.kind === 'dedication_name' || t.kind === 'dedication_greeting';
        // Print's own layout for the moved dedication (arrange_dedication_text_around_photo).
        if (isFittedDedication && typeof t.dedicationLayout === 'string') el.setAttribute('data-llsb-dedication-layout', t.dedicationLayout);
        var isFittedSingleDialogue = spread.layout === 'single' && t.kind === 'dialogue';
        /* The cover name occupies the authored Canva title region immediately
           above the fixed title lines. The compositor shrinks long names to the
           published floor, so the preview has to apply the same fit contract. */
        var isFittedCoverName = t.kind === 'cover_child_name';
        var isCreatorFittedText = viewConfig.storybookPreviewActive && st.min != null;
        /* Activity-page name lines (Luna's Roar Practice page) shrink to their
           style's floor like print's activity_title, so a long name stays in
           the gap the art leaves for it. */
        var isFittedActivityLine = t.kind === 'activity_name_line';
        var isFittedText = isCreatorFittedText || isFittedDedication || isFittedSingleDialogue || isFittedCoverName || isFittedActivityLine;
        if (isFittedText && t.h != null) {
          el.style.height = textBox.h + '%';
          el.style.overflow = 'hidden';
        }
        el.style.textAlign = t.align || 'left';
        /* The compositor anchors bottom-aligned boxes (the cover name) at the
           box floor, so a shrunk name hugs the fixed title beneath it instead
           of drifting upward — and the first line's ascenders gain headroom
           instead of being clipped by the box's top edge. refreshText slides
           an inner span down after fitting; flexbox can't be used here because
           justify-content:flex-end breaks the scrollHeight overflow probe the
           fit loop depends on. */
        if (t.valign === 'bottom' || t.valign === 'middle' || isFittedDedication) {
          el.setAttribute('data-llsb-valign', t.valign || 'top');
          var inner = document.createElement('span');
          inner.style.display = 'block';
          el.appendChild(inner);
        }
        /* Bubblegum Sans deliberately uses tight authored leading on the
           Rainbow cover-title runs. Its glyph ink is taller than that line box, just
           as it is in the print compositor, so browser scrollHeight reports a
           few pixels of harmless overhang. Mark those single-line runs so the
           fit loop tolerates the ink and reveals it instead of clipping the
           tops/bottoms of the title. */
        if (
          viewConfig.storyId === 'rainbow_kingdom'
          && (String(t.kind || '').indexOf('rainbow_cover_title_') === 0
            || t.kind === 'cover_title_ampersand' || t.kind === 'cover_title_story')
        ) {
          el.setAttribute('data-llsb-ink-overhang', 'true');
        }
        el.style.color = t.fill || st.fill || '#000';
        el.style.fontWeight = st.weight || 400;
        el.style.fontFamily = styleFontFamily(st);
        el.style.fontStyle = st.italic ? 'italic' : 'normal';
        el.style.letterSpacing = String(st.tracking || 0) + 'em';
        el.style.lineHeight = String(st.leading || 1.05);
        el.style.whiteSpace = st.preserveLineBreaks === true ? 'pre' : 'pre-line';
        if (t.rot) {
          el.style.transform = 'rotate(' + t.rot + 'deg)';
          el.style.transformOrigin = '50% 50%';
        }
        var strokeColour = styleStrokeColour(st);
        if (strokeColour) el.style.webkitTextStroke = String(st.strokeWidth == null ? 0.028 : st.strokeWidth) + 'em ' + strokeColour;
        el.setAttribute('data-llsb-tpl', t.template);
        if (t.runs) el.storybookRuns = t.runs;
        el.setAttribute('data-llsb-kind', t.kind || 'dialogue');
        el.setAttribute('data-llsb-style', t.style || '');
        el.setAttribute('data-llsb-original-left', String(textBox.x));
        el.setAttribute('data-llsb-original-top', String(textBox.y));
        el.setAttribute('data-llsb-original-width', String(textBox.w));
        el.setAttribute('data-llsb-original-height', String(textBox.h));
        el.setAttribute('data-llsb-original-align', t.align || 'left');
        el.setAttribute('data-llsb-original-valign', t.valign || 'top');
        if (isFittedText) el.setAttribute('data-llsb-fit', 'true');
        /* canva font sizes are fractions of the 779-unit page height */
        el.setAttribute('data-llsb-size', String(((st.size || 26) / 779) * 100));
        /* Styles that carry the compositor's min_canva_size shrink to that same
           floor; the rest keep the gentler proportional floor refreshText picks. */
        if (st.min != null) el.setAttribute('data-llsb-min', String((st.min / 779) * 100));
        if (st.fullSizeMaxChars != null) {
          el.setAttribute('data-llsb-full-size-max-chars', String(st.fullSizeMaxChars));
        }
        frame.appendChild(el);
      });

      if (spread.char) {
        var zone = document.createElement('div');
        zone.className = 'llsb-char-zone';
        zone.style.left = spread.char.x + '%';
        zone.style.top = spread.char.y + '%';
        zone.style.width = spread.char.w + '%';
        zone.style.height = spread.char.h + '%';
        zone.setAttribute('data-llsb-zone', String(spread.char.pose));
        if (spread.char.rot) zone.style.setProperty('--llsb-rot', spread.char.rot + 'deg');
        var backdrop = document.createElement('div');
        backdrop.className = 'llsb-char-zone__backdrop';
        backdrop.style.background = spread.char.backdrop || '#e8e2d2';
        zone.appendChild(backdrop);
        var idle = document.createElement('div');
        idle.className = 'llsb-char-zone__idle';
        zone.classList.add('is-idle');
        zone.appendChild(idle);
        setZoneIdleText(idle, zoneIdleText(spread.id === 'cover'));
        frame.appendChild(zone);
      }

      if (spread.layout === 'single') wrap.appendChild(frame);
      else appendSpreadPages(wrap, frame);
      bookViewport.appendChild(wrap);
      spreadEls.push(wrap);
    });
    showBookPage(0);
    armSwipeHint();
  }

  /* Once per device (2026-10-20): the first two-page spread a phone shows
     whole-screen pages for nudges sideways and says the other page is a
     swipe away. A swipe, or a few seconds, puts it away for good. */
  var SWIPE_HINT_KEY = 'll:swipe-hint-seen';
  var swipeHintWatch = null;
  function swipeHintSeen() {
    try { return window.localStorage.getItem(SWIPE_HINT_KEY) === '1'; } catch (error) { return false; }
  }
  function armSwipeHint() {
    if (swipeHintWatch) { swipeHintWatch.disconnect(); swipeHintWatch = null; }
    if (!window.IntersectionObserver || swipeHintSeen()) return;
    var spreads = spreadEls.filter(function (wrap) { return wrap.querySelector('.llsb-spread__pager'); });
    if (!spreads.length) return;
    swipeHintWatch = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var pager = entry.target;
        if (!swipeHintWatch || !entry.isIntersecting || entry.intersectionRatio < 0.6) return;
        if (!pager.clientWidth || pager.scrollWidth <= pager.clientWidth + 1 || swipeHintSeen()) return;
        swipeHintWatch.disconnect(); swipeHintWatch = null;
        showSwipeHint(pager);
      });
    }, { threshold: [0.6] });
    // The first two-page spread only: the hint never waits for a later one.
    swipeHintWatch.observe(spreads[0].querySelector('.llsb-spread__pager'));
  }
  function showSwipeHint(pager) {
    try { window.localStorage.setItem(SWIPE_HINT_KEY, '1'); } catch (error) { /* shown this visit only */ }
    var wrap = pager.parentNode, frame = pager.querySelector('.llsb-spread__frame');
    var copy = config.journeyCopy || {};
    var hint = document.createElement('p');
    hint.className = 'llsb-swipe-hint';
    hint.setAttribute('role', 'status');
    hint.textContent = copy.swipe_hint || 'Swipe for the other page';
    wrap.appendChild(hint);
    // Peek at the other page: left when the spread opened on its left page.
    var toLeft = pager.scrollLeft < pager.clientWidth / 2;
    if (frame && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) frame.classList.add(toLeft ? 'is-peek-left' : 'is-peek-right');
    var timer = 0;
    function done() {
      clearTimeout(timer);
      pager.removeEventListener('scroll', done);
      if (frame) frame.classList.remove('is-peek-left', 'is-peek-right');
      hint.classList.add('is-leaving');
      setTimeout(function () { hint.remove(); }, 300);
    }
    pager.addEventListener('scroll', done, { passive: true });
    timer = setTimeout(done, 4500);
  }

  /* Small phones list a spread as its two pages, each the screen's width
     (owner, 2026-10-19): the spread sits twice as wide in a sideways
     scroller that snaps to its left or right page, so its words are twice
     the size, and two dots under it show which page is in view. Each opens
     on the child's page, where most spreads also set the verse, so reading
     down the book shows the child on every spread. Wider screens lay the
     scroller out as if it were not there and see the whole spread
     (ll-storybook.css). */
  function spreadOpeningPage(frame) {
    var centre = function (node) { return (parseFloat(node.style.left) || 0) + (parseFloat(node.style.width) || 0) / 2; };
    var zone = frame.querySelector('.llsb-char-zone');
    if (zone) return centre(zone) >= 50 ? 1 : 0;
    // No child on the spread (the dedication): the page with its words.
    var right = 0;
    frame.querySelectorAll('[data-llsb-tpl]').forEach(function (node) { right += centre(node) >= 50 ? 1 : -1; });
    return right > 0 ? 1 : 0;
  }
  function syncSpreadPages() {
    spreadEls.forEach(function (spread) { if (spread.llsbSyncPages) spread.llsbSyncPages(); });
  }
  function appendSpreadPages(wrap, frame) {
    var pager = document.createElement('div');
    pager.className = 'llsb-spread__pager';
    pager.appendChild(frame);
    var dots = document.createElement('div');
    dots.className = 'llsb-spread__pages';
    ['Left page', 'Right page'].forEach(function (label, index) {
      var stop = document.createElement('span');
      stop.className = 'llsb-spread__stop';
      stop.setAttribute('aria-hidden', 'true');
      pager.appendChild(stop);
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'llsb-spread__page-dot';
      dot.setAttribute('aria-label', label);
      dot.setAttribute('aria-pressed', 'false');
      dot.addEventListener('click', function () {
        turned = true;
        pager.scrollTo({ left: index * pager.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      });
      dots.appendChild(dot);
    });
    // Until the shopper turns it, a spread stays on its opening page.
    var turned = false;
    ['pointerdown', 'wheel', 'keydown'].forEach(function (type) {
      pager.addEventListener(type, function () { turned = true; }, { passive: true });
    });
    wrap.llsbSyncPages = function () {
      var paged = pager.clientWidth && pager.scrollWidth > pager.clientWidth + 1;
      var opening = spreadOpeningPage(frame);
      if (paged && !turned && Math.abs(pager.scrollLeft - opening * pager.clientWidth) > 1) pager.scrollLeft = opening * pager.clientWidth;
      var page = paged ? (pager.scrollLeft > pager.clientWidth / 2 ? 1 : 0) : opening;
      Array.prototype.forEach.call(dots.children, function (dot, index) {
        var pressed = index === page ? 'true' : 'false';
        if (dot.getAttribute('aria-pressed') !== pressed) dot.setAttribute('aria-pressed', pressed);
      });
    };
    pager.addEventListener('scroll', wrap.llsbSyncPages, { passive: true });
    wrap.appendChild(pager);
    wrap.appendChild(dots);
    wrap.llsbSyncPages();
  }

  function fitStorybookTextSize(maximum, minimum, overflows, applySize) {
    var steps = Math.ceil(Math.max(0, maximum - minimum) * 2);
    if (!steps || !overflows()) return maximum;
    var low = 1;
    var high = steps;
    while (low < high) {
      var middle = Math.floor((low + high) / 2);
      applySize(Math.max(minimum, maximum - middle * 0.5));
      if (overflows()) low = middle + 1;
      else high = middle;
    }
    var size = Math.max(minimum, maximum - low * 0.5);
    applySize(size);
    return size;
  }

  function refreshText() {
    refreshCharacterAltText();
    refreshZoneIdleText();
    preview.querySelectorAll('[data-llsb-tpl]').forEach(function (el) {
      /* Bottom/middle-aligned boxes carry an inner span (see renderSpreads);
         the text lands there so the span can slide inside the fixed clip box. */
      var inner = el.getAttribute('data-llsb-valign') ? el.firstElementChild : null;
      /* As in print, the shopper's own dedication replaces the greeting. */
      var ownDedication = el.getAttribute('data-llsb-kind') === 'dedication_greeting' ? activeDedication() : '';
      var renderedText = ownDedication || styleText(el.getAttribute('data-llsb-tpl'), el.classList.contains('is-script'));
      if (el.storybookRuns && !ownDedication) {
        renderStorybookRuns(inner || el, el.storybookRuns, viewConfig.spanStyles, function (text) {
          return styleText(text, el.classList.contains('is-script'));
        });
      } else (inner || el).textContent = renderedText;
      if (inner) inner.style.transform = '';
      var frame = el.closest('.llsb-spread__frame');
      var sizePct = parseFloat(el.getAttribute('data-llsb-size')) || 3;
      var baseSize = frame.clientHeight * sizePct / 100;
      /* Only the literal empty-state placeholder ("Your child") should read
         like a name that has already shrunk to fit. Creator-authored sample
         names such as Dino Herd's "Noah" are real cover copy even before the
         customer types, so they keep the full authored size. A typed name is
         also unaffected. The box is already left-aligned/valign bottom, so
         anchorInner() below re-anchors the smaller placeholder automatically. */
      baseSize *= storybookCoverNameInitialScale(
        el.getAttribute('data-llsb-kind'),
        previewChild().name,
        defaultChildName()
      );
      /* Preserve the approved 80-unit size through ten visible characters.
         Every additional character reduces the ceiling proportionally before
         the existing box-overflow loop runs, keeping Luna's name inside the
         watercolour canopy even when narrow letters would not overflow yet. */
      baseSize *= storybookCoverNameLengthScale(
        el.getAttribute('data-llsb-kind'),
        renderedText,
        el.getAttribute('data-llsb-full-size-max-chars')
      );
      el.style.fontSize = baseSize + 'px';
      /* Every pass re-arms the clip on a fitted box: the fit loop below tells
         a wrapped second line from a fit by scrollHeight, and it then lifts
         the clip again for faces whose ink overhangs the line box (cover
         names, Rainbow's title runs). */
      if (el.getAttribute('data-llsb-fit') === 'true' && el.style.height) el.style.overflow = 'hidden';
      function anchorInner() {
        if (!inner) return;
        var slack = el.clientHeight - inner.offsetHeight;
        var verticalAlign = el.getAttribute('data-llsb-valign');
        inner.style.transform = '';
        if (slack > 0 && verticalAlign !== 'top') {
          inner.style.transform = 'translateY('
            + (verticalAlign === 'middle' ? slack / 2 : slack)
            + 'px)';
        }
      }
      if (el.getAttribute('data-llsb-fit') !== 'true' || !frame.clientHeight) return anchorInner();

      /* Keep short salutations and messages at the same authored size. Longer
         customer text may shrink only as much as its own box needs, so a long
         dedication fits without making "To Riu," artificially tiny. Styles that
         publish the compositor's own floor (data-llsb-min) use it instead, so
         the preview bottoms out exactly where the printed page does. */
      var minPct = parseFloat(el.getAttribute('data-llsb-min'));
      var minimumSize = (isFinite(minPct) && minPct > 0)
        ? frame.clientHeight * minPct / 100
        : baseSize * 0.55;
      /* The combined US dedication page intentionally gives the dedication
         less vertical room so the framed photo and three-story range can
         share one square page. Its scoped proportional floor may be lower
         than the generic fitted-text floor, while every other Creator box
         continues to honour its authored minimum exactly. */
      var minimumScale = parseFloat(el.getAttribute('data-llsb-min-scale'));
      if (isFinite(minimumScale) && minimumScale > 0 && minimumScale <= 1) {
        minimumSize = Math.min(minimumSize, baseSize * minimumScale);
      }
      var fittedSize = baseSize;
      var allowsInkOverhang = el.getAttribute('data-llsb-ink-overhang') === 'true';
      /* The cover name box is height-locked with overflow:hidden purely so
         the scrollHeight probe below can tell a wrapped second line from a
         fit. Its display faces (Bubblegum Sans at 0.88 leading, the script
         face) draw ink outside their own line box, and the run sits on the
         box floor (valign bottom) — so with the clip left on, descenders
         (y, g, j) vanished below the edge (owner report 2026-09-02, "Joyng"
         on the live Dino Herd cover). The print compositor never clips
         glyph ink either. Once fitted, reveal it. */
      var isCoverName = el.getAttribute('data-llsb-kind') === 'cover_child_name';
      function textBoxOverflows() {
        /* The compositor fits Rainbow's one-line cover runs by authored
           line-height, not by the display face's ink bounds. A proportional
           allowance distinguishes that small glyph overhang from a genuine
           second line while preserving the normal strict check everywhere
           else. */
        var verticalAllowance = allowsInkOverhang ? fittedSize * 0.2 : 1;
        return el.scrollHeight > el.clientHeight + verticalAllowance
          || el.scrollWidth > el.clientWidth + 1;
      }
      fitStorybookTextSize(baseSize, minimumSize, textBoxOverflows, function (size) {
        fittedSize = size;
        el.style.fontSize = fittedSize + 'px';
      });
      if (allowsInkOverhang || isCoverName) el.style.overflow = 'visible';
      anchorInner();
    });
    // A photo moves the dedication's words to the other page.
    syncSpreadPages();
  }

  function syncDedicationPhotoPreview() {
    var spread = preview.querySelector(
      '[data-llsb-spread-id="dedication"], [data-llsb-spread-id="dedication-page"]'
    );
    if (!spread) return;
    var photo = spread.querySelector('[data-llsb-dedication-photo]');
    var image = photo && photo.querySelector('img');
    var frameArt = photo && photo.querySelector('.llsb-dedication-photo__frame');
    /* The demo sample's photo stands in for the customer's until they upload
       one, so the framed dedication page demonstrates itself (badged, below). */
    var sample = previewChild().photoFile ? null : demoSample();
    var previewPhotoSrc = previewChild().photoDataUrl || (sample && (sample.photo || sample.thumb));
    var previewPhotoWidth = previewChild().photoWidth || (sample && sample.photoWidth);
    var previewPhotoHeight = previewChild().photoHeight || (sample && sample.photoHeight);
    var active = Boolean(
      photo
      && image
      && previewPhotoSrc
      && previewPhotoWidth
      && previewPhotoHeight
    );
    var dedicationFrame = spread.querySelector('.llsb-spread__frame');
    var staleDedicationBadge = dedicationFrame && dedicationFrame.querySelector('.llsb-char-zone__badge');
    if (staleDedicationBadge) staleDedicationBadge.remove();
    var singlePage = spread.getAttribute('data-llsb-spread-layout') === 'single';
    var withStoryRange = Boolean(
      singlePage
      && spread.querySelector('[data-llsb-intro-range]')
    );
    if (active) {
      var frame = storybookDedicationPhotoLayout(
        singlePage ? 'single' : 'spread',
        previewPhotoWidth,
        previewPhotoHeight,
        withStoryRange
      );
      photo.style.left = frame.x + '%';
      photo.style.top = frame.y + '%';
      photo.style.width = frame.w + '%';
      photo.style.height = frame.h + '%';
      photo.style.padding = frame.rail + '%';
      image.src = previewPhotoSrc;
      image.alt = previewChild().photoDataUrl
        ? (previewChild().name || defaultChildName()) + ' original uploaded photo'
        : 'Sample child photo in the dedication frame';
      /* The frame for this box, as dedication_photo_geometry gives print:
         its rails overhang the photo by the box's padding, one rail on every
         side, whatever the photo's shape. Redrawn only when the box changes;
         photo.hidden below hides it with the rest of the box. */
      var frameKey = [frame.frame.width, frame.frame.height, frame.frame.rail].map(function (value) { return value.toFixed(3); }).join(' ');
      if (frameArt && frameArt.getAttribute('data-llsb-frame-box') !== frameKey) {
        frameArt.innerHTML = storybookBlackOakFrameSvg(frame.frame.width, frame.frame.height, frame.frame.rail);
        frameArt.setAttribute('data-llsb-frame-box', frameKey);
      }
      photo.hidden = false;
      photo.setAttribute('aria-hidden', 'false');
      if (!previewChild().photoDataUrl && dedicationFrame) {
        var dedicationBadge = document.createElement('span');
        dedicationBadge.className = 'llsb-char-zone__badge' + (!singlePage && frame.x + frame.w / 2 >= 50 ? ' llsb-char-zone__badge--right' : '');
        dedicationBadge.textContent = 'Sample preview';
        dedicationFrame.appendChild(dedicationBadge);
      }
    } else if (photo && image) {
      photo.hidden = true;
      photo.setAttribute('aria-hidden', 'true');
      image.removeAttribute('src');
      image.alt = '';
    }

    var textNodes = Array.prototype.slice.call(
      spread.querySelectorAll('[data-llsb-kind="dedication_name"], [data-llsb-kind="dedication_greeting"]')
    );
    var hasName = textNodes.some(function (node) {
      return node.getAttribute('data-llsb-kind') === 'dedication_name';
    });
    textNodes.forEach(function (node) {
      if (active && singlePage && withStoryRange) {
        node.setAttribute('data-llsb-min-scale', '0.45');
      } else {
        node.removeAttribute('data-llsb-min-scale');
      }
      if (!active) {
        node.style.left = node.getAttribute('data-llsb-original-left') + '%';
        node.style.top = node.getAttribute('data-llsb-original-top') + '%';
        node.style.width = node.getAttribute('data-llsb-original-width') + '%';
        node.style.height = node.getAttribute('data-llsb-original-height') + '%';
        node.style.textAlign = node.getAttribute('data-llsb-original-align') || 'left';
        node.setAttribute('data-llsb-valign', node.getAttribute('data-llsb-original-valign') || 'top');
        var originalInner = node.firstElementChild;
        if (originalInner) originalInner.style.transform = '';
        return;
      }
      var kind = node.getAttribute('data-llsb-kind');
      var box = singlePage && withStoryRange
        ? kind === 'dedication_name'
          ? { x: 10, y: 41, w: 80, h: 6 }
          : { x: 10, y: hasName ? 47 : 41, w: 80, h: hasName ? 22 : 28 }
        : singlePage
        ? kind === 'dedication_name'
          ? { x: 10, y: 61, w: 80, h: 7 }
          : { x: 10, y: hasName ? 69 : 65, w: 80, h: hasName ? 25 : 29 }
        : kind === 'dedication_name'
          ? { x: 7, y: 27, w: 36, h: 8 }
          // Dragon Roar's dragon rises into the copy area: print raises the
          // message above her head (raised-over-illustration), so does this.
          : !hasName && node.getAttribute('data-llsb-dedication-layout') === 'raised-over-illustration'
          ? { x: 7, y: 12, w: 36, h: 28 }
          : { x: 7, y: hasName ? 37 : 30, w: 36, h: hasName ? 31 : 34 };
      var style = viewConfig.styles[node.getAttribute('data-llsb-style')] || {};
      var textBox = storybookTextBoxGeometry(box, style);
      node.style.left = textBox.x + '%';
      node.style.top = textBox.y + '%';
      node.style.width = textBox.w + '%';
      node.style.height = textBox.h + '%';
      node.style.textAlign = 'center';
      node.setAttribute('data-llsb-valign', 'middle');
    });
    refreshText();
  }

  window.addEventListener('resize', syncBookViewportHeight);
  window.addEventListener('resize', refreshText);

  /* The first per-story sample set (viewConfig.samples[0]: a photo plus one
     pose-transferred cutout per spread) is the preview's default demo
     character. It is a demonstration, never a selection: nothing persists
     it, and addToBasket() only ever sends state.photoFile. */
  function demoSample() {
    return (viewConfig.samples && viewConfig.samples.length) ? viewConfig.samples[0] : null;
  }

  var zoneRenderVersion = 0;

  /* The empty character zone names the child once a name is typed ("Ava
     appears here, created from your photo") — a promise about their book rather than
     a note about an absent asset. Pages without the guided copy keep the
     original placeholder. */
  /* The cover and the first story page explain the spot ("appears here");
     every later spot just names the child, so the line never repeats. */
  function zoneIdleText(isCover, explain) {
    var copy = config.journeyCopy || {};
    var name = (previewChild().name || '').trim();
    if (!isCover) {
      var callout = '';
      if (explain) callout = name && copy.zone_idle ? copy.zone_idle.replace('[NAME]', name) : copy.zone_idle_anon || 'Their character will appear here';
      return { label: name || defaultChildName(), note: '', callout: callout };
    }
    if (name && copy.zone_idle) {
      /* "[NAME] appears here, created from your photo": the name leads the spot
         and the rest of the line sits under it. */
      var line = copy.zone_idle;
      /* The preview's own subtitle already says the character is created from
         their photo, so the cover tag keeps to the short note ("appears here"). */
      if (line.indexOf('[NAME]') === 0) return { label: name, note: copy.zone_idle_note || line.slice(6).trim() };
      return { label: line.replace('[NAME]', name), note: '' };
    }
    return { label: copy.zone_idle_anon || 'Their character will appear here', note: '' };
  }
  function idleZoneParts(node) {
    var isCover = node.closest('[data-llsb-spread-id]').getAttribute('data-llsb-spread-id') === 'cover';
    var firstStoryZone = preview.querySelector('[data-llsb-spread-id]:not([data-llsb-spread-id="cover"]) .llsb-char-zone');
    return zoneIdleText(isCover, Boolean(firstStoryZone) && firstStoryZone.contains(node));
  }
  function refreshZoneIdleText() {
    preview.querySelectorAll('.llsb-char-zone__idle').forEach(function (node) { setZoneIdleText(node, idleZoneParts(node)); });
  }

  /* Pose boxes run from a third of the page down to a few pixels wide on a
     phone, so the label steps down from its 11px design size to an 8px
     floor, then keeps the longest start that fits behind an ellipsis, and
     leaves the box empty when not even one letter fits. It never spills
     onto the art. A box with room (most desktop poses) is a fuller spot: a
     crop of the shopper's photo (or a person mark), the name in the display
     face (16-24px) and, on the cover and first story page when wide enough,
     a quiet note under it ("appears here"). A label on a hidden spread has no size yet;
     it is fitted when showBookPage reveals it (refreshText). */
  var ZONE_IDLE_MAX_PX = 11;
  var ZONE_IDLE_MIN_PX = 8;
  var ZONE_IDLE_ROOMY_WIDTH = 64;
  var ZONE_IDLE_ROOMY_HEIGHT = 96;
  var ZONE_IDLE_NOTE_WIDTH = 104;
  var ZONE_IDLE_MARK_MIN = 40;
  var ZONE_IDLE_MARK_MAX = 120;
  var ZONE_IDLE_PERSON = '<svg viewBox="0 0 24 24" focusable="false"><circle cx="12" cy="8" r="3.6" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M5 20c.6-3.8 3.4-6 7-6s6.4 2.2 7 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
  /* With room, the spot carries a crop of the shopper's own photo (held in
     this browser only, as on the dedication page): their child, never
     another's. Without a photo it shows a plain person mark. */
  function syncZoneIdleMark(mark) {
    if (!mark) return;
    var photo = previewChild().photoDataUrl || '';
    if (mark.llsbPhoto === photo && mark.firstChild) return;
    mark.llsbPhoto = photo;
    mark.classList.toggle('has-photo', Boolean(photo));
    mark.textContent = '';
    if (!photo) { mark.innerHTML = ZONE_IDLE_PERSON; return; }
    var image = document.createElement('img');
    image.alt = '';
    image.decoding = 'async';
    image.src = photo;
    mark.appendChild(image);
  }
  function setZoneIdleText(node, parts) {
    node.setAttribute('data-llsb-idle-text', parts.label);
    node.setAttribute('data-llsb-idle-note', parts.note || '');
    syncZoneCallout(node, parts.callout || '');
    fitZoneIdleText(node);
  }
  function syncZoneCallout(node, text) {
    var zone = node.parentNode;
    if (!zone) return;
    var callout = zone.querySelector('.llsb-char-zone__callout');
    if (!text) { if (callout) callout.remove(); return; }
    if (!callout) {
      callout = document.createElement('span');
      callout.className = 'llsb-char-zone__callout';
      callout.setAttribute('aria-hidden', 'true');
      zone.appendChild(callout);
    }
    callout.textContent = text;
    /* Away from the gutter, on the tag's own page, unless that edge is near. */
    var left = parseFloat(zone.style.left) || 0, width = parseFloat(zone.style.width) || 0;
    var onLeftPage = left + width / 2 < 50;
    callout.setAttribute('data-side', onLeftPage ? (left > 22 ? 'left' : 'right') : (left + width < 78 ? 'right' : 'left'));
  }
  function fitZoneIdleText(node) {
    var full = node.getAttribute('data-llsb-idle-text') || '';
    var label = node.querySelector('.llsb-char-zone__idle-text');
    if (!label) {
      node.textContent = '';
      var mark = document.createElement('span');
      mark.className = 'llsb-char-zone__idle-mark';
      mark.setAttribute('aria-hidden', 'true');
      node.appendChild(mark);
      label = document.createElement('span');
      label.className = 'llsb-char-zone__idle-text';
      node.appendChild(label);
      var noteNode = document.createElement('span');
      noteNode.className = 'llsb-char-zone__idle-note';
      node.appendChild(noteNode);
    }
    var note = node.querySelector('.llsb-char-zone__idle-note');
    label.textContent = full;
    node.style.fontSize = '';
    var zone = node.parentNode;
    var roomy = Boolean(zone) && zone.clientWidth >= ZONE_IDLE_ROOMY_WIDTH && zone.clientHeight >= ZONE_IDLE_ROOMY_HEIGHT;
    node.classList.toggle('is-roomy', roomy);
    /* The note needs a line of its own: narrow spots show the name alone. */
    note.textContent = roomy && zone.clientWidth >= ZONE_IDLE_NOTE_WIDTH ? node.getAttribute('data-llsb-idle-note') || '' : '';
    node.classList.toggle('is-sentence', /\s/.test(full) && full.length > 14);
    var mark = node.querySelector('.llsb-char-zone__idle-mark');
    syncZoneIdleMark(mark);
    /* The shopper's photo fills the whole tag, a rectangle sized from the
       pose box, whatever the photo's own shape (owner, 2026-10-18). On the
       cover, with room, the name and "appears here" sit on a band above the
       photo; inside the story the photo stands alone (CSS .is-photo). */
    var photoTag = mark.classList.contains('has-photo');
    var spreadNode = node.closest('[data-llsb-spread-id]');
    var captioned = photoTag && roomy && Boolean(spreadNode) && spreadNode.getAttribute('data-llsb-spread-id') === 'cover';
    node.classList.toggle('is-photo', photoTag);
    node.classList.toggle('is-captioned', captioned);
    if (photoTag && !captioned) { mark.style.width = mark.style.height = ''; label.textContent = ''; note.textContent = ''; return; }
    /* The tag hugs its contents; the mark scales with the pose box. With
       room, the photo is the tag's largest part (owner, 2026-10-15): it
       starts near the box's width and steps down below until the name and
       note fit under it. */
    var zoneWidth = zone ? zone.clientWidth : 0;
    var markSize = roomy
      ? Math.max(ZONE_IDLE_MARK_MIN, Math.min(ZONE_IDLE_MARK_MAX, Math.round(zoneWidth * 0.72)))
      : Math.max(14, Math.min(30, Math.round(zoneWidth - 8)));
    mark.style.width = mark.style.height = photoTag ? '' : markSize + 'px';
    function fits() {
      return label.scrollWidth <= label.clientWidth && label.scrollHeight <= label.clientHeight;
    }
    var largest = roomy ? Math.min(24, Math.max(16, Math.floor(zone.clientWidth * 0.22))) : ZONE_IDLE_MAX_PX;
    /* Returns false when only part of the name (or none) fits. */
    function fitLabel() {
      label.textContent = full;
      node.style.fontSize = '';
      if (!label.clientWidth || !label.clientHeight) return true;
      node.style.fontSize = largest + 'px';
      fitStorybookTextSize(largest, ZONE_IDLE_MIN_PX, function () { return !fits(); }, function (size) {
        node.style.fontSize = size + 'px';
      });
      if (fits()) return true;
      var chars = Array.from(full);
      function shortened(count) { return chars.slice(0, count).join('').replace(/\s+$/, '') + '…'; }
      var low = 0;
      var high = chars.length - 1;
      while (low < high) {
        var mid = Math.ceil((low + high) / 2);
        label.textContent = shortened(mid);
        if (fits()) low = mid;
        else high = mid - 1;
      }
      label.textContent = low ? shortened(low) : '';
      return false;
    }
    /* The name comes first: in a short box the note gives way to it. */
    if (!fitLabel() && note.textContent) { note.textContent = ''; fitLabel(); }
    if (!roomy || photoTag) return;
    /* A tag taller than its box would clip the name: the photo steps down,
       then the note goes. */
    function overflows() { return node.scrollHeight > node.clientHeight + 1; }
    while (overflows() && markSize > ZONE_IDLE_MARK_MIN) {
      markSize = Math.max(ZONE_IDLE_MARK_MIN, markSize - 4);
      mark.style.width = mark.style.height = markSize + 'px';
    }
    if (overflows() && note.textContent) { note.textContent = ''; fitLabel(); }
  }

  /* renders a sample's per-pose cutouts into every spread's character zone
     (llp-sb-sample-{n}-cut-p{NN}.webp), or clears every zone back to its
     idle "their character will appear here" placeholder. The "Sample
     preview" badge is pinned to the top-left corner of each spread's image
     frame (never the character zone itself, which sits mid-image and would
     otherwise straddle the character) on every frame where a sample cutout
     actually rendered. */
  function setZones(stateName, cuts) {
    var renderVersion = ++zoneRenderVersion;
    var zones = Array.prototype.slice.call(preview.querySelectorAll('[data-llsb-zone]'));

    function idleZone(zone) {
      var idle = document.createElement('div');
      idle.className = 'llsb-char-zone__idle';
      zone.appendChild(idle);
      zone.classList.add('is-idle');
      setZoneIdleText(idle, idleZoneParts(idle));
    }

    zones.forEach(function (zone) {
      var pose = zone.getAttribute('data-llsb-zone');
      var frame = zone.closest('.llsb-spread__frame');
      zone.classList.remove('is-ready', 'is-idle');
      zone.querySelectorAll('.llsb-char-zone__idle, img').forEach(function (n) { n.remove(); });
      if (frame) {
        var existingBadge = frame.querySelector('.llsb-char-zone__badge');
        if (existingBadge) existingBadge.remove();
      }
      if (stateName === 'sample' && cuts && cuts[pose]) {
        var img = document.createElement('img');
        img.alt = 'Sample preview';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.addEventListener('load', function () {
          if (renderVersion !== zoneRenderVersion) return;
          zone.classList.add('is-ready');
          if (frame) {
            var badge = document.createElement('span');
            // On the child's own page, which a small phone shows alone.
            var zoneLeft = parseFloat(zone.style.left) || 0, zoneWidth = parseFloat(zone.style.width) || 0;
            badge.className = 'llsb-char-zone__badge' + (zoneLeft + zoneWidth / 2 >= 50 ? ' llsb-char-zone__badge--right' : '');
            badge.textContent = 'Sample preview';
            frame.appendChild(badge);
          }
        });
        img.addEventListener('error', function () {
          if (renderVersion !== zoneRenderVersion) return;
          img.remove();
          idleZone(zone);
        });
        zone.appendChild(img);
        img.src = cuts[pose];
      } else {
        idleZone(zone);
      }
    });
  }

  var hintShown = false;
  function showScrollHint() {
    if (guidedEnabled) return;
    if (hintShown || !window.matchMedia('(max-width: 989px)').matches) return;
    var hint = root.querySelector('[data-llsb-scrollhint]');
    if (!hint) return;
    hintShown = true;
    hint.hidden = false;
    requestAnimationFrame(function () { hint.classList.add('is-visible'); });
    function dismiss() {
      hint.classList.remove('is-visible');
      window.removeEventListener('scroll', onScroll);
      setTimeout(function () { hint.hidden = true; }, 300);
    }
    var startY = null;
    function onScroll() {
      if (startY === null) return;
      if (Math.abs(window.scrollY - startY) > 120) dismiss();
    }
    /* arm after the collapse auto-scroll settles so it doesn't self-dismiss */
    setTimeout(function () {
      startY = window.scrollY;
      window.addEventListener('scroll', onScroll, { passive: true });
    }, 800);
    hint.addEventListener('click', function () {
      if (state.offer === 'single') showGallerySlide('live');
      var first = preview.querySelector('.llsb-spread');
      if (first) first.scrollIntoView({ behavior: 'smooth' });
      dismiss();
    });
  }

  function applyPreview() {
    syncDedicationPhotoPreview();
    /* A real upload shows the idle "their character will appear here" zones:
       generated poses are never shown before purchase, and another child's
       demo character must never sit in a book that now belongs to theirs. */
    if (previewChild().photoFile) { setZones('idle'); return; }
    var demo = demoSample();
    if (demo && demo.cuts) { setZones('sample', demo.cuts); return; }
    setZones('idle');
  }

  /* ================= character choice ================= */

  /* collapse step 1 once a photo (real upload or sample) is chosen, so the
     name field is reachable without scrolling past the whole photo step */
  var charStep = root.querySelector('[data-llsb-step="character"]');
  var chosenBtn = root.querySelector('[data-llsb-chosen]');
  var chosenImg = root.querySelector('[data-llsb-chosen-img]');
  var chosenTitle = root.querySelector('[data-llsb-chosen-title]');

  function collapseCharacter(scrollToDetails) {
    if (guidedEnabled) return;
    if (!characterChosen()) return;
    chosenImg.src = state.photoDataUrl || '';
    chosenImg.removeAttribute('width');
    chosenImg.removeAttribute('height');
    chosenImg.classList.add('is-photo');
    chosenTitle.textContent = 'Photo added';
    charStep.classList.add('is-collapsed');
    /* Owner round 9: this used to call details.scrollIntoView({block:
       'start'}) unconditionally. On desktop the collapsed panel (heading +
       benefits + the now-tiny "Sample photo / Tap to change" summary + the
       name step + buy button) is short enough that the browser cannot
       satisfy "align details with the top of the viewport" — there simply
       isn't that much page left below it — so it clamped to its maximum
       scroll position instead, which dumped the reader at the very bottom
       of the document (the more-stories strip / footer beyond #llsb). That
       is the owner's screenshot: a photo/sample re-selection "scrolling
       way down" to what looked like the footer. Confirmed via a real
       measurement (round 9 repro): requested target ~695px, max scrollable
       ~157px on a 1440x900 fixture — the clamp, not a bug in the target
       element, produced the jump.
       Desktop's whole editor already fits on one screen once step 1
       collapses (that collapse just happened above), so no scroll is ever
       needed there — skip it outright rather than re-deriving a "safe"
       target. Mobile keeps a gentle assist, but retargeted at the NAME
       INPUT itself with block:'nearest' (a true no-op if it's already on
       screen) instead of top-aligning the whole details section — the same
       reduced-motion-aware smooth/auto choice already used elsewhere in
       this file (e.g. guideToMissing). */
    if (scrollToDetails && window.matchMedia('(max-width: 989px)').matches && nameInput) {
      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      nameInput.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
    }
  }

  function expandCharacter() {
    charStep.classList.remove('is-collapsed');
    /* Review finding, round 9 follow-up: charStep sits near the top of the
       panel today, so this scroll happened to measure safe — but that's a
       property of today's content above it, not a guarantee, and it is the
       same unconditional scrollIntoView({block:'start'}) shape that clamped
       to the document's bottom elsewhere on this page (see
       collapseCharacter and guideToMissing). Gating it the same
       way makes it robust rather than merely safe-by-measurement: desktop
       performs no scroll (the editor is one screen; charStep is already
       reachable without moving the page), mobile keeps this scroll
       unchanged. */
    if (window.matchMedia('(max-width: 989px)').matches) {
      charStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  if (chosenBtn) chosenBtn.addEventListener('click', expandCharacter);

  /* ================= photo upload ================= */
  var photoInput = root.querySelector('[data-llsb-photo]');
  var photoConsentInput = root.querySelector('[data-llsb-photo-consent]');
  var photoStatusEl = root.querySelector('[data-llsb-photo-status]');
  var nameStatusEl = root.querySelector('[data-llsb-name-status]');
  var uploadControl = root.querySelector('.llsb__upload');
  var consentControl = root.querySelector('.llsb__photo-consent');
  var photoStatusAnchor = document.createComment('photo status');
  if (photoStatusEl) photoStatusEl.after(photoStatusAnchor);
  var photoCheck = window.LLPhotoCheck || null;
  /* "Choose another photo": an offer under a photo warning (the photo still
     counts). It follows the status line wherever that line is placed. */
  var photoRetryEl = document.createElement('button');
  photoRetryEl.type = 'button';
  photoRetryEl.className = 'll-photo-retry';
  photoRetryEl.setAttribute('data-ll-photo-retry', '');
  photoRetryEl.hidden = true;
  photoRetryEl.textContent = 'Choose another photo';
  photoRetryEl.addEventListener('click', function () {
    photoInput.click();
    if (photoCheck) photoCheck.warmUp();
  });
  if (photoStatusEl) photoStatusEl.after(photoRetryEl);
  var photoCheckGeneration = 0;
  var checkingPhoto = false;
  /* The accepted photo's warning verdict (null for a clean photo), kept so a
     consent error that replaces its line can give the warning back. */
  var acceptedPhotoWarning = null;

  var PHOTO_MISSING_COPY = 'Please upload their photo first.';
  var CONSENT_MISSING_COPY = 'Please tick the box above to confirm you have authority to provide this photo.';
  var NAME_MISSING_COPY = 'Please enter the child’s first name.';
  var PHOTO_READY_COPY = 'Photo ready to use.';
  var CONSENT_SUFFIX = ' Tick the box above to continue.';

  function setStatus(node, message, kind) {
    if (!node) return;
    node.hidden = !message;
    node.className = 'llsb__upload-status' + (kind ? ' is-' + kind : '');
    node.textContent = message || '';
  }

  /* code: what the line is currently about ('photo' | 'consent' | 'checking'
     | a photo-check verdict code) so a later event can clear only its own
     message; level mirrors the class so collapse logic can read it. */
  function showPhotoStatus(message, kind, code) {
    if (!photoStatusEl) return;
    if (code === 'consent' && consentControl) consentControl.after(photoStatusEl);
    else photoStatusAnchor.before(photoStatusEl);
    var retry = photoRetryEl;
    photoStatusEl.after(retry);
    retry.hidden = !(kind === 'warn' && Boolean(message));
    if (photoConsentInput) {
      if (code === 'consent') photoConsentInput.setAttribute('aria-invalid', 'true');
      else photoConsentInput.removeAttribute('aria-invalid');
    }
    setStatus(photoStatusEl, message, kind);
    if (message && code) photoStatusEl.setAttribute('data-llsb-status-code', code);
    else photoStatusEl.removeAttribute('data-llsb-status-code');
    if (message && kind) photoStatusEl.setAttribute('data-llsb-status-level', kind);
    else photoStatusEl.removeAttribute('data-llsb-status-level');
  }

  function photoStatusCode() {
    return photoStatusEl ? photoStatusEl.getAttribute('data-llsb-status-code') : null;
  }

  /* In the guided journey a photo's verdict, and "Choose another photo" with
     a warning, scroll just into view above the bar (2026-10-28): the nearest
     scroll, never more, so the heading stays put when it is already in view. */
  function revealPhotoStatus() {
    if (!photoStatusEl || photoStatusEl.hidden || !photoStatusEl.closest('.llsj')) return;
    var target = !photoRetryEl.hidden ? photoRetryEl : photoStatusEl;
    var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { target.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' }); } catch (e) { /* older browsers: no scroll */ }
  }

  function photoStatusLevel() {
    return photoStatusEl ? photoStatusEl.getAttribute('data-llsb-status-level') : null;
  }

  /* Start fetching the detector the moment the shopper reaches for the
     upload control: the file picker takes seconds, which hides the download
     (the YuNet bundle is lazy loaded and only ever loads on this
     intent, never for a visitor who just reads the page). */
  if (uploadControl && photoCheck) {
    uploadControl.addEventListener('click', function () { photoCheck.warmUp(); });
  }

  if (photoConsentInput) {
    photoConsentInput.addEventListener('change', function () {
      if (consentControl) consentControl.classList.remove('is-missing');
      if (photoConsentInput.checked && photoStatusCode() === 'consent') {
        /* the consent warning replaced the photo's line; bring it back, the
           photo's own warning included (a photo is necessarily chosen for
           that warning to have shown) */
        if (state.photoFile && acceptedPhotoWarning) showPhotoStatus(acceptedPhotoWarning.message, 'warn', acceptedPhotoWarning.code);
        else if (state.photoFile) showPhotoStatus(PHOTO_READY_COPY, 'ok', 'ok');
        else showPhotoStatus('', '', null);
      }
      var code = photoStatusCode();
      if (state.photoFile && code && code !== 'consent' && code !== 'checking' && code !== 'photo') {
        /* keep the ready/warning line honest about whether consent is still owed */
        var level = photoStatusLevel() || 'ok';
        var base = photoStatusEl.textContent.replace(CONSENT_SUFFIX, '');
        showPhotoStatus(base + (photoConsentInput.checked ? '' : CONSENT_SUFFIX), level, code);
      }
      syncButtons();
      if (photoConsentInput.checked && state.photoFile && photoStatusLevel() !== 'warn') {
        collapseCharacter(true);
      }
    });
  }

  function resetPhoto(message) {
    checkingPhoto = false;
    acceptedPhotoWarning = null;
    state.photoFile = null;
    state.photoDataUrl = null;
    state.photoWidth = null;
    state.photoHeight = null;
    try { photoInput.value = ''; } catch (e) { /* ignore */ }
    var prev = root.querySelector('[data-llsb-photo-preview]');
    prev.removeAttribute('src');
    prev.hidden = true;
    root.querySelector('[data-llsb-photo-cta]').textContent = 'Upload their photo';
    chosenImg.removeAttribute('src');
    charStep.classList.remove('is-collapsed');
    showPhotoStatus(message, 'bad', 'photo');
    syncButtons();
    applyPreview();
  }

  function acceptPhoto(file, dataUrl, width, height, verdict) {
    checkingPhoto = false;
    state.photoFile = file;
    state.photoDataUrl = dataUrl;
    state.photoWidth = width;
    state.photoHeight = height;
    var prev = root.querySelector('[data-llsb-photo-preview]');
    prev.src = dataUrl;
    prev.removeAttribute('width');
    prev.removeAttribute('height');
    prev.hidden = false;
    root.querySelector('[data-llsb-photo-cta]').textContent = 'Choose another photo';
    if (uploadControl) uploadControl.classList.remove('is-missing');
    var consentPending = Boolean(photoConsentInput && !photoConsentInput.checked);
    var warn = Boolean(verdict && verdict.level === 'warn');
    acceptedPhotoWarning = warn ? { message: verdict.message, code: verdict.code } : null;
    var message = (warn ? verdict.message : PHOTO_READY_COPY) + (consentPending ? CONSENT_SUFFIX : '');
    showPhotoStatus(message, warn ? 'warn' : 'ok', warn ? verdict.code : 'ok');
    syncButtons();
    applyPreview();
    /* A warning stays readable: step 1 only collapses on a clean pass with
       consent already given. */
    if (!consentPending && !warn) collapseCharacter(true);
  }

  photoInput.addEventListener('change', function () {
    var file = photoInput.files && photoInput.files[0];
    if (!file) return;
    /* A new photo answers a refused Add to basket; the plain product page
       has no journey step change to retire it. */
    var journeyCore = window.LLStorybookJourney && window.LLStorybookJourney.core;
    if (journeyCore && journeyCore.clearAddFailure) journeyCore.clearAddFailure(root.querySelector('[data-llsb-add-status]'));
    var generation = ++photoCheckGeneration;
    /* Type and size are checked before the file is read at all: an
       unreadable or huge file otherwise sat on "Checking photo…" forever,
       because neither FileReader nor <img> had an error handler to fall out
       of (2026-09-02 review). resetPhoto() puts the reader back on the
       upload control with the reason. */
    if (ALLOWED_PHOTO_TYPES.indexOf(file.type) === -1) {
      resetPhoto('Please choose a JPEG, PNG or WebP image.');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      resetPhoto('Please choose an image smaller than 10 MB.');
      return;
    }
    checkingPhoto = true;
    syncButtons();
    showPhotoStatus('Checking photo…', '', 'checking');
    var reader = new FileReader();
    reader.onerror = function () {
      if (generation === photoCheckGeneration) resetPhoto('We couldn’t read that image. Please choose another.');
    };
    reader.onload = function () {
      var probe = new Image();
      probe.onerror = function () {
        if (generation === photoCheckGeneration) resetPhoto('We couldn’t read that image. Please choose another.');
      };
      probe.onload = function () {
        if (generation !== photoCheckGeneration) return;
        if (probe.naturalWidth < 500 || probe.naturalHeight < 500) {
          resetPhoto('That photo is quite small (' + probe.naturalWidth + '×' + probe.naturalHeight + 'px). A larger, clearer photo gives a much better character.');
          return;
        }
        /* The face gate runs entirely in the browser (see ll-photo-check.js)
           and never rejects: an unavailable detector resolves to an ok
           verdict, so this path degrades to today's size-only check. With no
           checker at all (ll-photo-check.js failed to load), or one that
           throws or rejects anyway, the photo goes ahead unchecked and
           production QA checks it (owner, 2026-10-28). */
        var verdictPromise = new Promise(function (done) { done(photoCheck ? photoCheck.check(probe) : { level: 'ok', code: 'unchecked' }); })
          .catch(function () { return { level: 'ok', code: 'unchecked' }; });
        verdictPromise.then(function (verdict) {
          if (generation !== photoCheckGeneration) return;
          if (verdict && verdict.level === 'block') {
            resetPhoto(verdict.message);
            revealPhotoStatus();
            return;
          }
          var copy = booksOffers && booksOffers.inMemoryPhoto ? booksOffers.inMemoryPhoto(file, reader.result) : file;
          acceptPhoto(copy, reader.result, probe.naturalWidth, probe.naturalHeight, verdict);
          revealPhotoStatus();
        });
      };
      probe.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  /* ================= fields ================= */

  var nameInput = root.querySelector('[data-llsb-name]');

  function refreshCharacterAltText() {
    preview.querySelectorAll('.llsb-char-zone img').forEach(function (img) {
      if (img.alt === 'Sample preview') return;
      img.alt = (previewChild().name || defaultChildName()) + ' illustrated';
    });
    var dedicationPhoto = preview.querySelector(
      '[data-llsb-dedication-photo]:not([hidden]) img:not(.llsb-dedication-photo__frame)'
    );
    if (dedicationPhoto) {
      dedicationPhoto.alt = previewChild().photoDataUrl
        ? (previewChild().name || defaultChildName()) + ' original uploaded photo'
        : 'Sample child photo in the dedication frame';
    }
  }

  /* A prefilled name can be longer than the field would ever have let anyone
     type, so the cap needs a message of its own rather than only a silent
     readiness failure. */
  function storybookNameLengthError(name) {
    return name.length > MAX_NAME_LENGTH
      ? 'Please use ' + MAX_NAME_LENGTH + ' characters or fewer.'
      : '';
  }

  function personalisationGlyphError() {
    return state.name
      ? storybookNameLengthError(state.name) || storybookPersonalisationGlyphError(state.name, false)
      : '';
  }

  function syncPersonalisationGlyphValidity() {
    var nameError = state.name
      ? storybookNameLengthError(state.name) || storybookPersonalisationGlyphError(state.name, false)
      : '';
    nameInput.setCustomValidity(nameError);
    if (nameError) nameInput.setAttribute('aria-invalid', 'true');
    else nameInput.removeAttribute('aria-invalid');
    return nameError;
  }

  nameInput.addEventListener('input', function () {
    state.name = nameInput.value.trim();
    if (state.name) setStatus(nameStatusEl, '', '');
    refreshText();
    syncButtons();
    persistDraft();
  });

  function characterChosen() {
    return Boolean(state.photoFile);
  }

  /* Only the name survives a reload: the uploaded File cannot be
     serialised, and the demo character is not a choice worth restoring. */
  function persistDraft() {
    if (guidedEnabled) return;
    try {
      localStorage.setItem(storeKey, JSON.stringify({ name: state.name }));
    } catch (e) { /* ignore */ }
  }

  function isReady() {
    var permissionReady = Boolean(photoConsentInput && photoConsentInput.checked);
    /* The input's own maxlength caps typing, but not the homepage handoff
       below, which fills the field programmatically — an over-long name
       used to sail through to a book cover that cannot set it. */
    return Boolean(config.product.anyAvailable) && !checkingPhoto && characterChosen() && state.name.length > 0 && state.name.length <= MAX_NAME_LENGTH && !personalisationGlyphError() && permissionReady;
  }

  /* The configurator reads this page's child for every book; when the
     child changes here (photo, consent, name) the set's tiles and rows are
     re-rendered. The signature stops the configurator's own onChange →
     syncButtons() call from rendering twice. */
  var lastPrimarySignature = '';
  function syncConfigurator() {
    if (state.offer === 'single' || !configurator) return;
    var signature = [state.name, state.photoFile ? (state.photoFile.name + ':' + state.photoFile.size) : '', photoConsentInput && photoConsentInput.checked ? 1 : 0].join('|');
    if (signature === lastPrimarySignature) return;
    lastPrimarySignature = signature;
    configurator.render();
  }

  var childSession;
  function syncButtons() {
    if (childSession) childSession.update();
    var previewEmail = root.querySelector('[data-llsb-preview-email]');
    if (previewEmail) previewEmail.hidden = !state.photoFile;
    syncConfigurator();
    var textError = syncPersonalisationGlyphValidity();
    /* Both buttons are the same control rendered twice — the inline one in
       the wizard and its twin in the fixed bar — and scrolling swaps which
       is on screen. They must therefore always look and behave identically:
       a grey inline button handing over to a live-looking sticky one (or the
       reverse) reads as the page breaking mid-scroll.
       Neither is disabled merely because the wizard is incomplete; pressing
       walks the reader to whatever is still missing (see onCtaPress). Only a
       genuinely unbuyable product or an add already in flight disables. */
    var ctaDisabled = addingNow
      || Boolean(pendingStorybookAdd)
      || !config.product.anyAvailable;
    var addBtn = root.querySelector('[data-llsb-add]');
    addBtn.disabled = ctaDisabled;
    /* document-scoped: the sticky bar lives outside root since it is
       reparented to document.body on init (task 11c item 1). */
    var sticky = document.querySelector('[data-llsb-add-sticky]');
    if (sticky) sticky.disabled = ctaDisabled;
    if (!ctaDisabled) {
      var ctaLabel = isReady() ? ctaLabelFor(currentBundle())
        : (config.ctaLabel || (config.region === 'US' ? 'Personalize your book' : 'Personalise your book'));
      addBtn.textContent = ctaLabel;
      if (sticky) sticky.textContent = ctaLabel;
    }
    var addStatus = root.querySelector('[data-llsb-add-status]');
    var addFlowState = root.getAttribute('data-llsb-add-state');
    if (pendingStorybookAdd && addFlowState === 'unknown') {
      showUnknownStorybookAdd(pendingStorybookAdd.token);
    } else if (pendingStorybookAdd && addFlowState === 'reconciling') {
      showStorybookAddChecking();
    } else if (addStatus && textError) {
      addStatus.hidden = false;
      addStatus.className = 'llsb__upload-status is-bad';
      addStatus.setAttribute('data-llsb-text-error', 'true');
      addStatus.textContent = textError;
    } else if (addStatus && (
      addStatus.getAttribute('data-llsb-cutout-error') === 'true'
      || addStatus.getAttribute('data-llsb-text-error') === 'true'
    )) {
      addStatus.hidden = true;
      addStatus.className = 'llsb__upload-status';
      addStatus.removeAttribute('data-llsb-cutout-error');
      addStatus.removeAttribute('data-llsb-text-error');
      addStatus.textContent = '';
    }
    persistDraft();
    if (journey) journey.sync();
  }

  /* ================= offer selector + inline bundles ================= */
  /* 2026-09-06: the price popup ("Choose your books") is gone. The three
     offers sit in one selector under the pitch (snippet
     ll-book-offer-summary.liquid, select mode); choosing a bundle builds it
     right here — steps 1+2 stay the child for every book, step 3 (one row
     per book) and the set's covers on the stage come from the bundle
     configurator module (ll-bundle-page.js mount), and the CTA posts the
     storybook-bundle-v2 line. Choosing the single again restores the live
     preview and the plain add. */
  var booksOffers = window.LLBooksOffers || null;

  /* One word, one owner: the shared offers module maps region -> "cart"/
     "basket" and both book PDPs read it from there. The inline fallback keeps
     the page correct if ll-books-offers.js has not loaded — it must never
     drift back to a hard-coded "basket", which is what US shoppers were
     reading in every post-CTA line on this page. */
  function basketWord(capitalised) {
    var word = (booksOffers && booksOffers.basketWord)
      ? booksOffers.basketWord(config.region)
      : (config.region === 'US' ? 'cart' : 'basket');
    return capitalised ? word.charAt(0).toUpperCase() + word.slice(1) : word;
  }

  var bundlePage = window.LLBundlePage || null;
  var booksStep = root.querySelector('[data-llbp-books]');
  var setStage = root.querySelector('[data-llsb-set-stage]');
  var thumbsRail = root.querySelector('[data-llsb-gthumbs]');
  var configurator = null;
  /* a story page sells the story double and Their Complete Collection; the
     colouring-only double belongs to the colouring pages */
  var pageBundles = (config.bundles || []).filter(function (bundle) { return bundle && bundle.kind !== 'colouring'; });
  function bundleById(id) { return pageBundles.filter(function (bundle) { return bundle.id === id && bundle.available; })[0] || null; }
  function currentBundle() { return state.offer === 'single' ? null : bundleById(state.offer); }
  function setWordFor(bundle) { return bundle && bundle.kind === 'mixed' ? 'set' : bundle && bundle.kind === 'library' ? 'collection' : 'bundle'; }
  function stageKindFor(bundle) { return { mixed: 'gift-set', library: 'complete-collection' }[bundle.kind] || 'two-stories'; }
  /* this page's child, as the configurator reads it for every book */
  function primaryChild() {
    return { id: 'primary', photoFile: state.photoFile, photoDataUrl: state.photoDataUrl || null, photoWidth: state.photoWidth, photoHeight: state.photoHeight, name: state.name, consent: Boolean(photoConsentInput && photoConsentInput.checked), checking: checkingPhoto, ready: Boolean(state.photoFile) && !checkingPhoto && !personalisationGlyphError() };
  }
  function mountConfigurator(bundle) {
    if (!bundlePage || !bundlePage.mount || !booksOffers || !booksStep || !setStage) return null;
    var storage = null;
    try { storage = window.localStorage; } catch (e) { storage = null; }
    return bundlePage.mount(root, {
      config: config, bundle: bundle, offers: booksOffers, fromStoryId: config.storyId || '',
      guided: guidedEnabled ? { children: function () { return journey ? journey.children() : [primaryChild()]; }, recipients: function () { return journey ? journey.recipients() : []; }, assign: function (index, id) { if (journey) journey.assign(index, id); },
        // Book 2's Change on Your books adds the pair's second child (owner, 2026-10-27).
        addChild: function (index) { if (journey) journey.addChildForBooks(index); } } : null,
      // Look inside a chosen book opens the journey's view of it (#make/look).
      lookInside: guidedEnabled ? function (slot) { return journey && journey.canLook() ? { open: function () { journey.look(slot.index - 1); } } : null; } : null,
      lookLabel: (config.journeyCopy || {}).look_inside,
      primary: { read: primaryChild, guide: function () { guideToMissing(); return true; } },
      stage: setStage.querySelector('[data-llbp-stage]'), legend: setStage.querySelector('[data-llbp-legend]'), slots: booksStep.querySelector('[data-llbp-slots]'),
      /* document-scoped sticky lookup: the bar is reparented to body at boot */
      cta: { buttons: [root.querySelector('[data-llsb-add]'), document.querySelector('[data-llsb-add-sticky]')], status: root.querySelector('[data-llsb-add-status]'), idleLabel: function () { return ctaLabelFor(currentBundle()); }, storageKey: 'llsb:' + config.product.handle + ':bundle-pending-add', storage: storage,
        // The journey's basket line sits above it, away from the bar: a missing book is said in the picker.
        guideInBar: !guidedEnabled },
      trackContext: { region: config.region, surface: 'storybook', offerId: bundle.id, placement: 'inline' },
      // A set's own dedication, once the worker prints it in every story book (settings.ll_set_dedication).
      extraProperties: function () { return config.setDedication && activeDedication() ? [['Dedication', activeDedication()]] : []; },
      onAdded: function () { if (journey) journey.clear(); },
      onChange: function () { syncButtons(); }
    });
  }
  function selectOffer(optionId) {
    if (journey && (addingNow || pendingStorybookAdd || (configurator && configurator.busy()))) {
      /* A cart request in flight keeps its package: put the radio back so
         the page never shows a choice it ignored. */
      if (offerSelector) offerSelector.select(state.offer);
      return;
    }
    var bundle = optionId === 'single' ? null : bundleById(optionId);
    state.offer = bundle ? optionId : 'single';
    if (offerSelector) offerSelector.select(state.offer);
    if (bundle) {
      if (!configurator) configurator = mountConfigurator(bundle);
      else if (configurator.bundle().id !== bundle.id) configurator.setBundle(bundle);
      var title = booksStep && booksStep.querySelector('[data-llbp-slots-title]');
      if (title) title.textContent = 'Books in this ' + setWordFor(bundle);
      /* the bundle page's tile caps key on its kind class; the set stage wears the same one */
      if (setStage) setStage.className = 'llsb__stage-set llbp llbp--' + stageKindFor(bundle);
    }
    var inline = Boolean(bundle && configurator);
    if (bundle && !inline) { /* module missing: the single is the only thing this page can sell */
      state.offer = 'single';
      if (offerSelector) offerSelector.select('single');
    }
    root.classList.toggle('llsb--bundle', inline);
    if (booksStep) booksStep.hidden = !inline;
    if (setStage) setStage.hidden = !inline;
    if (thumbsRail) thumbsRail.hidden = inline && !guidedEnabled;
    if (galleryStageLive) galleryStageLive.hidden = inline;
    syncGalleryControls();
    if (inline) configurator.render(); else syncButtons();
    if (journey) journey.sync();
  }
  var offerSelector = (booksOffers && booksOffers.bindOfferSelector)
    ? booksOffers.bindOfferSelector(root, { region: config.region, surface: 'storybook', placement: config.pdpOffers ? 'pdp' : 'selector', bundles: pageBundles, catalogue: config.catalogue, storyId: config.storyId, isReady: isReady, onSelect: function (optionId) { selectOffer(optionId); } })
    : null;

  /* the CTA's ready label: the single's price, or the bundle's */
  // plain: the journey's button names the action alone (owner, 2026-10-18).
  function ctaLabelFor(bundle, plain) {
    // The journey's pair reads as two books, not a "bundle" (owner, 2026-10-19).
    if (bundle && plain && bundle.kind === 'stories') return 'Add both books to ' + basketWord();
    if (bundle) return 'Add ' + setWordFor(bundle) + ' to ' + basketWord() + (plain ? '' : ' · ' + bundle.priceFormatted);
    var single = config.offers && config.offers[0];
    return 'Add to ' + basketWord() + (!plain && single && single.priceFormatted ? ' · ' + single.priceFormatted : '');
  }

  /* ================= add to basket ================= */

  /* `child` (optional) proofs another child's name on this cover — a bundle
     whose first book is this story, for someone other than the page's own
     primary child. Without it the page state is used as before. */
  function compositeCoverProof(child) {
    var proofName = child ? String(child.name || '').trim() : state.name;
    return new Promise(function (resolve) {
      try {
        var spread = config.spreads[0];
        var texts = spread.texts || [];
        /* Every authored cover line needs its own font before canvas draws:
           the child's name and the fixed story title can use different faces. */
        var fontReady = Promise.all(texts.map(function (text) {
          var style = config.styles[text.style] || {};
          var sizePx = 1000 * ((style.size || 84) / 779);
          return document.fonts && document.fonts.load
            ? document.fonts.load((style.italic ? 'italic ' : '') + (style.weight || 400) + ' ' + sizePx + 'px ' + styleFontFamily(style)).catch(function () {})
            : Promise.resolve();
        }));
        fontReady.then(function () { start(); });

        function start() {
        var bg = new Image();
        bg.crossOrigin = 'anonymous';
        bg.onload = function () {
          try {
            var W = 1000;
            var H = Math.round(W * (bg.naturalHeight / bg.naturalWidth));
            var cv = document.createElement('canvas');
            cv.width = W;
            cv.height = H;
            var ctx = cv.getContext('2d');
            ctx.drawImage(bg, 0, 0, W, H);
            texts.forEach(function (t) {
              var st = (t && config.styles[t.style]) || {};
              ctx.fillStyle = (t && t.fill) || st.fill || '#fffffa';
              ctx.textAlign = (t && t.align) || 'left';
              ctx.textBaseline = 'top';
              if (t) {
                var proofBox = storybookTextBoxGeometry(t, st);
                var proofX = (proofBox.x / 100) * W;
                if (ctx.textAlign === 'center') proofX += (proofBox.w / 200) * W;
                if (ctx.textAlign === 'right') proofX += (proofBox.w / 100) * W;
                var proofBoxH = t.h == null ? Infinity : (proofBox.h / 100) * H;
                /* Same placeholder cap as the live DOM preview (refreshText
                   above): an empty state.name draws "Your child" here too, via
                   styleText -> fillTemplate's defaultChildName() fallback, so
                   shrink this fit's ceiling the same way for consistency.
                   isReady() currently keeps this canvas path from running
                   before a name is typed, so this is defence in depth rather
                   than a change anyone can see today. */
                var priorViewChild = viewChild;
                if (child) viewChild = { name: proofName };
                var proofText = styleText(t.template, isCoverDisplayFace(st));
                viewChild = priorViewChild;
                var proofScale = (!proofName && t.kind === 'cover_child_name') ? 0.55 : 1;
                proofScale *= storybookCoverNameLengthScale(
                  t.kind,
                  proofText,
                  st.fullSizeMaxChars
                );
                var fitStyle = proofScale === 1
                  ? st
                  : cloneStyleWithSize(st, (st.size || 84) * proofScale);
                var fitted = fitCanvasText(ctx, proofText, fitStyle, (proofBox.w / 100) * W, proofBoxH, H);
                var proofY = (proofBox.y / 100) * H;
                if (isFinite(proofBoxH)) {
                  var remainingHeight = Math.max(0, proofBoxH - fitted.lines.length * fitted.lineHeightPx);
                  if (t.valign === 'bottom') proofY += remainingHeight;
                  else if (t.valign === 'middle') proofY += remainingHeight / 2;
                }
                for (var lineIndex = 0; lineIndex < fitted.lines.length; lineIndex++) {
                  ctx.fillText(fitted.lines[lineIndex], proofX, proofY);
                  proofY += fitted.lineHeightPx;
                }
              }
            });
            /* The illustrated child is generated after purchase. The basket
               proof includes the background and all authored cover text. */
            var finish = function () { cv.toBlob(function (b) { resolve(b); }, 'image/jpeg', 0.85); };
            finish();
          } catch (e) { resolve(null); }
        };
        bg.onerror = function () { resolve(null); };
        bg.src = spread.bg;
        }
      } catch (e) { resolve(null); }
    });
  }

  var addingNow = false;
  /* Deliberately no age-based expiry: elapsed time cannot prove that Shopify
     did not commit the POST, so expiring this record could mint a duplicate. */
  var pendingStorybookAddKey = storeKey + ':pending-add';
  var storybookAddStorage = null;
  try { storybookAddStorage = window.localStorage; } catch (error) { /* fail closed before POST */ }
  var pendingStorybookAdd = readStorybookPendingAdd(
    storybookAddStorage,
    pendingStorybookAddKey
  );
  var pendingStorybookReconciliation = null;

  function createStorybookAddToken() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) { /* fall back to a local opaque token */ }
    return 'storybook-' + Date.now().toString(36) + '-' +
      Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  }

  function cartContainsStorybookAddToken(addToken) {
    return fetch('/cart.js', { cache: 'no-store' }).then(function (res) {
      if (!res || !res.ok) throw new Error('Cart reconciliation failed.');
      return res.json();
    }).then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) {
        throw new Error('Cart reconciliation returned an invalid response.');
      }
      return cart.items.some(function (item) {
        return Boolean(
          item &&
          item.properties &&
          item.properties['_Storybook add token'] === addToken
        );
      });
    });
  }

  function rememberStorybookPendingAdd(addToken, addState) {
    var record = {
      token: addToken,
      state: addState,
      createdAt: pendingStorybookAdd && pendingStorybookAdd.token === addToken
        ? pendingStorybookAdd.createdAt
        : Date.now()
    };
    if (!persistStorybookPendingAdd(
      storybookAddStorage,
      pendingStorybookAddKey,
      record
    )) return false;
    pendingStorybookAdd = record;
    return true;
  }

  function forgetStorybookPendingAdd() {
    clearStorybookPendingAdd(storybookAddStorage, pendingStorybookAddKey);
    pendingStorybookAdd = null;
    root.removeAttribute('data-llsb-add-state');
  }

  function storybookAddControls() {
    return {
      button: root.querySelector('[data-llsb-add]'),
      /* document-scoped: reparented to document.body on init, task 11c item 1 */
      sticky: document.querySelector('[data-llsb-add-sticky]'),
      status: root.querySelector('[data-llsb-add-status]')
    };
  }

  function showStorybookAddChecking() {
    var controls = storybookAddControls();
    root.setAttribute('data-llsb-add-state', 'reconciling');
    controls.button.disabled = true;
    controls.button.textContent = 'Checking ' + basketWord() + '…';
    if (controls.sticky) {
      controls.sticky.disabled = true;
      controls.sticky.textContent = 'Checking ' + basketWord() + '…';
    }
    if (controls.status) {
      controls.status.hidden = false;
      controls.status.className = 'llsb__upload-status';
      controls.status.textContent = 'Checking whether your personalised book reached the ' + basketWord() + '…';
    }
  }

  function showUnknownStorybookAdd(addToken) {
    var controls = storybookAddControls();
    /* Keep the same token and safety lock. A reload or programmatic re-click
       may poll it again, but neither path is allowed to mint or POST a new
       logical add while Shopify's commit state is unknown. */
    var unknownCreatedAt = pendingStorybookAdd
      && pendingStorybookAdd.token === addToken
      ? pendingStorybookAdd.createdAt
      : Date.now();
    if (!rememberStorybookPendingAdd(addToken, 'unknown')) {
      pendingStorybookAdd = {
        token: addToken,
        state: 'unknown',
        createdAt: unknownCreatedAt
      };
    }
    addingNow = true;
    root.setAttribute('data-llsb-add-state', 'unknown');
    controls.button.disabled = true;
    controls.button.textContent = 'Check ' + basketWord();
    if (controls.sticky) {
      controls.sticky.disabled = true;
      controls.sticky.textContent = 'Check ' + basketWord();
    }
    if (controls.status) {
      controls.status.hidden = false;
      controls.status.className = 'llsb__upload-status is-bad';
      controls.status.textContent = 'We couldn’t verify whether this was added. Check your ' + basketWord() + ' before returning to this page. ';
      var cartLink = document.createElement('a');
      cartLink.href = config.cartUrl;
      cartLink.textContent = 'Open your ' + basketWord();
      controls.status.appendChild(cartLink);
      controls.status.appendChild(document.createTextNode(' · '));
      var retryButton = document.createElement('button');
      retryButton.type = 'button';
      retryButton.className = 'llsb__status-action';
      retryButton.textContent = 'Not there? Try again';
      retryButton.addEventListener('click', function () {
        retryUnknownStorybookAdd(addToken, retryButton);
      });
      controls.status.appendChild(retryButton);
    }
  }

  /* The ambiguous-POST lock must never expire or replay itself: elapsed time
     cannot prove that Shopify did not commit the line. It also must not trap
     this browser forever when the basket is genuinely empty. The customer
     therefore gets one explicit recovery action. It performs one fresh,
     token-specific basket read; a found line still wins and opens the basket,
     a confirmed absence only clears the safety lock and restores the idle
     editor, and a failed read remains fail-closed. Crucially, this action
     never calls addToBasket() or POSTs a replacement line — retrying the
     purchase remains a separate customer click. */
  function retryUnknownStorybookAdd(addToken, retryButton) {
    if (!pendingStorybookAdd || pendingStorybookAdd.token !== addToken) {
      return Promise.resolve(false);
    }
    retryButton.disabled = true;
    retryButton.textContent = 'Checking…';
    return cartContainsStorybookAddToken(addToken).then(function (found) {
      if (found) {
        forgetStorybookPendingAdd();
        window.location.href = config.cartUrl;
        return true;
      }
      forgetStorybookPendingAdd();
      addingNow = false;
      idleAddLabels.forEach(function (entry) {
        entry.button.textContent = entry.label;
      });
      syncButtons();
      var controls = storybookAddControls();
      if (controls.status) {
        controls.status.hidden = false;
        controls.status.className = 'llsb__upload-status is-ok';
        controls.status.textContent = basketWord(true) + ' checked — you can try again.';
      }
      return false;
    }).catch(function () {
      showUnknownStorybookAdd(addToken);
      return false;
    });
  }

  function reconcilePendingStorybookAdd() {
    if (!pendingStorybookAdd) return Promise.resolve(false);
    if (pendingStorybookReconciliation) return pendingStorybookReconciliation;
    var addToken = pendingStorybookAdd.token;
    addingNow = true;
    showStorybookAddChecking();
    pendingStorybookReconciliation = pollForStorybookAddToken(
      function () { return cartContainsStorybookAddToken(addToken); },
      function (delay) {
        return new Promise(function (resolve) { window.setTimeout(resolve, delay); });
      },
      STORYBOOK_ADD_RECONCILE_DELAYS_MS
    ).then(function (found) {
      pendingStorybookReconciliation = null;
      if (found) {
        forgetStorybookPendingAdd();
        window.location.href = config.cartUrl;
        return true;
      }
      /* A record from a much earlier visit: the poll's absences may include a
         swallowed read failure, so confirm with one fresh, unswallowed read
         before releasing the lock. A fresh record keeps the explicit retry. */
      if (settledStorybookPendingAdd(pendingStorybookAdd, Date.now())) return releaseSettledStorybookAdd(addToken);
      showUnknownStorybookAdd(addToken);
      return false;
    }).catch(function () {
      pendingStorybookReconciliation = null;
      showUnknownStorybookAdd(addToken);
      return false;
    });
    return pendingStorybookReconciliation;
  }

  /* Release path for a settled record: a found line still wins and opens the
     basket; a confirmed absence clears the lock and restores the idle editor;
     a failed read stays fail-closed in "unknown". Never POSTs anything. */
  function releaseSettledStorybookAdd(addToken) {
    return cartContainsStorybookAddToken(addToken).then(function (found) {
      if (found) {
        forgetStorybookPendingAdd();
        window.location.href = config.cartUrl;
        return true;
      }
      forgetStorybookPendingAdd();
      addingNow = false;
      idleAddLabels.forEach(function (entry) { entry.button.textContent = entry.label; });
      syncButtons();
      var controls = storybookAddControls();
      if (controls.status) { controls.status.hidden = true; controls.status.textContent = ''; }
      return false;
    }).catch(function () {
      showUnknownStorybookAdd(addToken);
      return false;
    });
  }

  /* addToBasket() adds the single hardcover; token, reconcile, redirect and
     failure handling live here too. */
  function addToBasket() {
    if (!config.product.available || !isReady()) return;
    if (pendingStorybookAdd) {
      reconcilePendingStorybookAdd();
      return;
    }
    if (addingNow) return;
    /* the single hardcover offer (config.offers[0]) is what this posts */
    var single = config.offers && config.offers[0];
    if (single && single.variantId) { config.product.variantId = single.variantId; config.product.available = true; }
    if (!photoConsentInput || !photoConsentInput.checked) {
      /* last line of defence on the POST path — guideToMissing() normally
         turns this away before the offer popup ever opens */
      charStep.classList.remove('is-collapsed');
      if (consentControl) consentControl.classList.add('is-missing');
      showPhotoStatus(CONSENT_MISSING_COPY, 'bad', 'consent');
      return;
    }
    var btn = root.querySelector('[data-llsb-add]');
    /* document-scoped: reparented to document.body on init, task 11c item 1 */
    var sticky = document.querySelector('[data-llsb-add-sticky]');
    if (!isShopify) {
      window.location.href = config.cartUrl;
      return;
    }
    addingNow = true;
    var originalBtnText = btn.textContent;
    var originalStickyText = sticky ? sticky.textContent : null;
    var addToken = createStorybookAddToken();
    var postStarted = false;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    if (sticky) {
      sticky.disabled = true;
      sticky.textContent = 'Adding…';
    }
    if (journey) journey.sync();
    var basketProof = null;
    compositeCoverProof().then(function (proof) {
      basketProof = proof;
      var fd = new FormData();
      fd.append('id', String(config.product.variantId));
      fd.append('quantity', '1');
      fd.append('properties[_Storybook add token]', addToken);
      if (config.storyId) fd.append('properties[_Story ID]', config.storyId);
      fd.append('properties[Child name]', state.name);
      if (activeDedication()) fd.append('properties[Dedication]', activeDedication());
      fd.append('properties[Personalisation]', 'From your photo');
      if (state.photoFile) {
        fd.append('properties[_Customer photo]', state.photoFile, state.photoFile.name);
        fd.append('properties[_Photo consent]', 'adult-authority-v2026-07-21');
      }
      if (proof) fd.append('properties[_Personalised artwork]', proof, 'storybook-cover.jpg');
      return booksOffers.preparePhotoForm(fd);
    }).then(function (fd) {
      /* The basket shows their photo in the child's spot on this device
         only; the uploaded cover above stays photo-free (ll-books-offers.js). */
      var pose = config.spreads && config.spreads[0] && config.spreads[0].char;
      if (!booksOffers.rememberBasketPhotoCover || !basketProof || !state.photoFile || !pose) return fd;
      return booksOffers.rememberBasketPhotoCover(booksOffers.formProperties(fd), basketProof, state.photoFile, pose).then(function () { return fd; });
    }).then(function (fd) {
      if (!rememberStorybookPendingAdd(addToken, 'posting')) {
        throw new Error('We couldn’t safely start this ' + basketWord() + ' update. Please refresh and try again.');
      }
      postStarted = true;
      return fetch('/cart/add.js', { method: 'POST', body: fd });
    }).then(function (res) {
      if (res.ok) {
        forgetStorybookPendingAdd();
        if (journey) journey.clear();
        if (booksOffers && booksOffers.trackOffer) booksOffers.trackOffer('confirmed', { region: config.region, surface: 'storybook', offerId: 'single', placement: 'inline' });
        window.location.href = config.cartUrl;
        return;
      }
      if (res.status >= 400 && res.status < 500) {
        /* A 4xx response is a definite rejection. Surface Shopify's own 422
           text (for example sold-out inventory) and let the customer correct
           the request. */
        return res.json().catch(function () { return null; }).then(function (body) {
          var definiteFailure = new Error((body && (body.description || body.message)) || '');
          definiteFailure.storybookDefiniteFailure = true;
          throw definiteFailure;
        });
      }
      /* A transport failure or 5xx can arrive after Shopify committed the
         line. Never replay the POST; the private token lets us reconcile the
         exact logical click against the cart instead. */
      throw new Error('');
    }).catch(function (err) {
      var addStatus = root.querySelector('[data-llsb-add-status]');

      function unlockAfterDefiniteFailure(message) {
        forgetStorybookPendingAdd();
        btn.textContent = originalBtnText;
        if (sticky) sticky.textContent = originalStickyText;
        addingNow = false;
        syncButtons();
        if (addStatus) {
          addStatus.hidden = false;
          addStatus.className = 'llsb__upload-status is-bad';
          addStatus.textContent = message || ('Sorry — we couldn’t add that to your ' + basketWord() + '. Please try again.');
          /* the journey retires it on the next step (clearAddFailure) */
          addStatus.setAttribute('data-llsb-add-failure', addStatus.textContent);
          /* Round 6: the in-flow .llsb__buy block (this status line's own
             parent) is visible on <990px now, but the sticky bar is a
             legitimate second way to have reached this failure — and by
             design (observeInlineBuyBlockForCtaBar) the bar is only ever
             on screen while the in-flow block is NOT, so whenever the bar
             was the control just pressed, this status line is still off
             the bottom of the viewport and a phone would never show the
             rejection without bringing it on screen. Harmless, still
             correct, when the in-flow button was the one pressed instead —
             the line is already at/near centre, so this is a no-op scroll. */
          if (window.matchMedia('(max-width: 989px)').matches) {
            var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            addStatus.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
          }
        }
      }

      if (!postStarted || (err && err.storybookDefiniteFailure)) {
        unlockAfterDefiniteFailure(err && err.message);
        return;
      }

      reconcilePendingStorybookAdd();
    });
  }

  /* The CTA is now the first thing a phone shows, before the wizard has been
     touched. Rather than presenting a dead disabled button there, send an
     incomplete order to whatever it is still missing — and SAY what that is:
     a red status line on the step, a red edge on the control, and focus on
     it (2026-09-02; the same shape the colouring PDP uses). */
  function revealConsentError() {
    if (!consentControl || !photoStatusEl) return;
    var top = consentControl.getBoundingClientRect().top;
    var bottom = photoStatusEl.getBoundingClientRect().bottom;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    photoConsentInput.focus({ preventScroll: true });
    window.scrollTo({
      top: Math.max(0, window.scrollY + (top + bottom) / 2 - window.innerHeight / 2),
      behavior: reducedMotion ? 'auto' : 'smooth'
    });
  }

  function guideToMissing() {
    if (guidedEnabled) {
      var wantsName = !state.name || personalisationGlyphError();
      if (wantsName) { setStatus(nameStatusEl, personalisationGlyphError() || NAME_MISSING_COPY, 'bad'); nameInput.focus(); }
      else if (checkingPhoto) { showPhotoStatus('Checking photo…', '', 'checking'); photoInput.focus(); }
      else if (!state.photoFile) { showPhotoStatus(PHOTO_MISSING_COPY, 'bad', 'photo'); photoInput.focus(); }
      else if (!photoConsentInput.checked) { showPhotoStatus(CONSENT_MISSING_COPY, 'bad', 'consent'); revealConsentError(); }
      return !isReady();
    }
    var target;
    var focusTarget;
    var missing = null;
    if (!characterChosen()) {
      missing = 'photo';
      charStep.classList.remove('is-collapsed');
      target = charStep;
      focusTarget = photoInput;
      if (uploadControl) uploadControl.classList.add('is-missing');
      showPhotoStatus(PHOTO_MISSING_COPY, 'bad', 'photo');
    } else if (photoConsentInput && !photoConsentInput.checked) {
      missing = 'consent';
      charStep.classList.remove('is-collapsed');
      target = charStep;
      focusTarget = photoConsentInput;
      if (consentControl) consentControl.classList.add('is-missing');
      showPhotoStatus(CONSENT_MISSING_COPY, 'bad', 'consent');
    } else if (!state.name.length) {
      missing = 'name';
      target = root.querySelector('[data-llsb-step="details"]');
      focusTarget = nameInput;
      setStatus(nameStatusEl, NAME_MISSING_COPY, 'bad');
    } else if (personalisationGlyphError()) {
      /* syncButtons() already shows the glyph error under the CTA */
      missing = 'name';
      target = root.querySelector('[data-llsb-step="details"]');
      focusTarget = nameInput;
    } else {
      target = root.querySelector('.llsb__buy');
    }
    if (missing === 'consent') {
      revealConsentError();
      return true;
    }
    if (!target) return Boolean(missing);
    /* Round 6 (owner): top-aligning the target (block: 'start', desktop's
       behaviour in the else branch below) used to strand mobile readers
       looking at dead space below it — the reserved sticky-bar clearance,
       the hidden "more stories" strip, even a peek of the footer beyond
       #llsb — because so little real content used to follow an incomplete
       step (the in-flow CTA was display:none; the fixed bar was the only
       clickable copy of it). That was the owner's "moves down" complaint:
       the walk itself picked the right step, but where it landed exposed
       blank space instead of the form. The in-flow CTA is back now (see
       .llsb__buy above) and the bar's reserved clearance is gone (see
       .llsb__panel's padding-bottom), which already closes most of that
       gap, but the walk still should not scroll further than it has to.
       Clamp so the viewport's bottom edge never passes the buy block's own
       bottom (plus a small breathing gap) — the reader lands with the
       form, never blank space, filling the screen below the target. */
    if (window.matchMedia('(max-width: 989px)').matches) {
      var buyBlock = root.querySelector('.llsb__buy');
      var boundary = buyBlock || root.querySelector('.llsb__panel') || target;
      var boundaryBottomDocY = window.scrollY + boundary.getBoundingClientRect().bottom;
      var desiredTopDocY = window.scrollY + target.getBoundingClientRect().top;
      var maxTopDocY = Math.max(0, boundaryBottomDocY + 16 - window.innerHeight);
      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: Math.max(0, Math.min(desiredTopDocY, maxTopDocY)),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }
    /* Review finding, round 9 follow-up: this branch used to be an
       unconditional `else { target.scrollIntoView({block:'start'}) }` here —
       the exact sibling of collapseCharacter's own pre-fix bug above, same
       clamp mechanism, different trigger. Reviewer-verified repro: consent
       checked + a photo (or sample) chosen + the name left empty, then press
       the CTA at 1440x900 — target becomes the details section, `block:
       'start'` asks the browser to align it with the viewport top, the
       collapsed desktop panel is short enough that the document can't
       satisfy that, so it clamps to its maximum scroll position and dumps
       the reader at the very bottom of the page. Pressing the CTA before
       typing a name is an entirely ordinary flow, not an edge case. Desktop
       now performs no scroll at all here, matching collapseCharacter's own
       fix: the editor already fits on one screen, so there is nothing to
       walk the reader to — only the focus() call below still runs, moving
       keyboard/screen-reader focus to whatever's incomplete without ever
       moving the page under it (`preventScroll: true`). Mobile keeps its
       existing round-6 clamped walk above, unchanged. */
    if (focusTarget) {
      /* focus after the smooth scroll so the browser does not jump past it */
      window.setTimeout(function () { focusTarget.focus({ preventScroll: true }); }, 400);
    }
    return Boolean(missing);
  }

  /* One press behaviour shared by both renderings of the CTA, so whichever
     one happens to be on screen does exactly the same thing. */
  function onCtaPress() {
    if (journey && !journey.canSubmit()) { journey.next(); return; }
    if (!isReady()) {
      guideToMissing();
      return;
    }
    var bundle = currentBundle();
    if (booksOffers && booksOffers.trackOffer) booksOffers.trackOffer('completed', { region: config.region, surface: 'storybook', offerId: bundle ? bundle.id : 'single', placement: 'builder' });
    if (bundle && configurator) {
      /* the bundle's own rows may still want a photo or a name */
      if (!configurator.guideToMissing()) configurator.add();
      return;
    }
    addToBasket();
  }

  root.querySelector('[data-llsb-add]').addEventListener('click', onCtaPress);
  /* root-scoped is safe here specifically: this runs once, synchronously,
     before the reparent below moves [data-llsb-ctabar] out to document.body
     (task 11c item 1) — every OTHER lookup of [data-llsb-add-sticky] in this
     file runs from inside a function that can also fire later, after the
     move, so those are document-scoped instead. The listener itself keeps
     working regardless of where the node lives afterwards; only *finding*
     it is scope-sensitive. */
  var stickyAdd = root.querySelector('[data-llsb-add-sticky]');
  if (stickyAdd) stickyAdd.addEventListener('click', onCtaPress);

  /* The bar's own reparent-to-body + [hidden] init happens unconditionally
     here, regardless of whether anything below ends up showing or hiding
     it again (round 6, further down) — CSS hides it outright on desktop
     either way. Round 3 made it permanently visible on <990px specifically
     because it was, at the time, the page's ONLY buy control: it used to
     slide in and out on an IntersectionObserver as the inline button
     crossed the fold, but a handoff between the sole interactive copy of
     the control and a dead disabled duplicate above it read as the page
     re-rendering mid-scroll. Round 6 restores the inline copy as a fully
     live, always-functional control in its own right (see
     observeInlineBuyBlockForCtaBar below) — with two genuinely equivalent
     copies instead of one live and one decorative, showing only the one
     that's actually on screen is no longer a "handoff," just an overflow
     affordance, so the slide-in/out behaviour comes back. The [hidden]
     attribute stays as the no-JS default so a broken script never shows a
     dead button.

     Task 11c item 1 (owner screenshot: the bar rendered in normal flow at
     the page-content bottom instead of pinned to the viewport). Root cause
     could not be pinned to one live ancestor — a full computed-style walk
     from this node up to <html> on the real draft (Chromium AND WebKit,
     mobile viewport, real dispatched touch-drag scroll, both with and
     without the consent banner clearing) found transform/filter/
     backdrop-filter/will-change/contain/perspective all "none"/"auto" the
     whole way up, and the bar stayed correctly pinned throughout — see the
     task-11c report for the full probe output. But per spec, ANY ancestor
     ever picking up one of those properties (Dawn's animations_reveal_on_
     scroll wrappers — confirmed ON for this theme, just inert on this
     template today — a future section wrapper, theme-editor instrumentation)
     silently turns this position:fixed into position:absolute, and that
     class of bug is exactly what a screenshot like the owner's looks like.
     Rather than keep chasing a specific ancestor, remove the possibility
     outright: move the node itself to document.body, unconditionally, on
     every load, so it always resolves position:fixed against the real
     viewport no matter what grows above #llsb later. Before the [hidden]
     un-hide so nothing ever paints mid-flight in the pre-move position. */
  var ctabar = root.querySelector('[data-llsb-ctabar]');
  if (ctabar) {
    document.body.appendChild(ctabar);
    ctabar.hidden = false;
  }

  /* Round 6 (owner): the bar is no longer the ONLY way to buy — the
     wizard's own .llsb__buy block is back in flow, directly under the name
     step (CSS, mobile only). Two live copies of the same control on screen
     at once is exactly the "handoff" problem the always-visible bar above
     was built to avoid, so instead the bar now shows only while the in-flow
     copy is actually scrolled out of view: an overflow affordance, not a
     permanent fixture. IntersectionObserver on the in-flow block itself,
     requiring at least VISIBLE_RATIO of ITS OWN height on screen before
     counting as "visible" (so a single pixel of the button peeking over the
     fold doesn't count) plus a short debounce on the toggle itself (so a
     scroll that bounces right on that boundary — momentum scrolling, a
     keyboard opening/closing — cancels and reschedules instead of
     flickering the bar in and out). The ratio is relative to the block's
     OWN size rather than a fraction of viewport height deliberately: an
     earlier version used a rootMargin inset of 10% of the viewport instead,
     which never cleared on a short product page — .llsb__buy sits close to
     the natural end of the document, and the page can run out of scroll
     room before the block clears a viewport-relative exclusion zone that
     size. A threshold on the target's own (much smaller) height has no
     such dependency on how much page is left below it. Desktop needs none
     of this: .llsb__ctabar is display:none there unconditionally (CSS,
     min-width: 990px) regardless of the [hidden] attribute this sets. If
     IntersectionObserver is unavailable the bar simply stays in whatever
     state the reparent above left it — always visible, the round-3
     behaviour, a safe fallback either way. */
  (function observeInlineBuyBlockForCtaBar() {
    if (guidedEnabled || !ctabar || typeof IntersectionObserver !== 'function') return;
    var buyBlock = root.querySelector('.llsb__buy');
    if (!buyBlock) return;
    var VISIBLE_RATIO = 0.4;
    var appliedHidden = false; // matches ctabar.hidden = false set above
    var toggleTimer = null;
    var observer = new IntersectionObserver(function (entries) {
      var inlineCtaOnScreen = entries[0].intersectionRatio >= VISIBLE_RATIO;
      if (toggleTimer) { window.clearTimeout(toggleTimer); toggleTimer = null; }
      /* A bounce back to the already-applied state within the debounce
         window below needs nothing scheduled — cancelling the pending
         timer above already undoes the stale in-flight toggle. */
      if (inlineCtaOnScreen === appliedHidden) return;
      toggleTimer = window.setTimeout(function () {
        appliedHidden = inlineCtaOnScreen;
        ctabar.hidden = appliedHidden;
        toggleTimer = null;
      }, 120);
    }, {
      threshold: [0, VISIBLE_RATIO]
    });
    observer.observe(buyBlock);
  }());

  /* Shopify's cookie-consent banner is fixed over the bottom of the viewport
     at z-index 2000000 — six orders of magnitude above anything a theme
     stylesheet can answer. Until it is dismissed, a tap aimed at the CTA bar
     lands on a consent button instead (Accept, at some widths): a lost sale
     AND a consent choice the customer never meant to make. The banner is not
     ours to restyle, so the bar yields: while a banner is pinned over the
     bottom edge, --llsb-consent-clearance lifts the bar (and everything
     sized against it — panel padding, focus scroll margins, the scroll
     hint) to sit flush on top of it.
     A light poll rather than observers: the banner script arrives late,
     animates in and out, and its markup is Shopify's to change — measuring
     the one rectangle four times a second covers insertion, dismissal,
     resize and rotation with no coupling to how any of that happens. */
  (function keepCtaClearOfConsentBanner() {
    if (!ctabar) return;
    var appliedClearance = -1;
    function measure() {
      var banner = document.getElementById('shopify-pc__banner');
      var clearance = 0;
      if (banner) {
        var style = window.getComputedStyle(banner);
        var rect = banner.getBoundingClientRect();
        var visible = style.display !== 'none'
          && style.visibility !== 'hidden'
          && parseFloat(style.opacity || '1') > 0.1
          && rect.height > 0;
        /* only a banner actually pinned across the bottom edge needs
           clearing; a centred modal variant would not */
        if (visible && rect.bottom > window.innerHeight - 8 && rect.top < window.innerHeight) {
          clearance = Math.ceil(window.innerHeight - rect.top);
        }
      }
      if (clearance !== appliedClearance) {
        appliedClearance = clearance;
        document.documentElement.style.setProperty('--llsb-consent-clearance', clearance + 'px');
      }
    }
    measure();
    window.setInterval(measure, 250);
  }());

  /* Delivery estimate — the date alone. Behind it: 3–4 working days to make
     and dispatch plus 4–5 business days in the post (UK and US alike),
     quoted as the buffered worst case of 9 working days.
     Computed here rather than in Liquid because storefront pages are cached:
     a server-rendered date would freeze at whatever day the cache was cut.
     If Intl is unavailable the Liquid fallback copy ("Arrives within 9
     working days") simply stays. */
  (function renderArrivalEstimate() {
    /* document-scoped, not root-scoped: this runs AFTER the ctabar reparent
       above (task 11c item 1), so its [data-llsb-arrival] span is already
       outside root's subtree by now — root.querySelectorAll would silently
       find only the wizard's copy and leave the sticky bar's date frozen at
       the Liquid fallback ("Arrives within 9 working days"). */
    var nodes = document.querySelectorAll('[data-llsb-arrival]');
    if (!nodes.length) return;
    var WORKING_DAYS = 9;
    var date = new Date();
    var added = 0;
    while (added < WORKING_DAYS) {
      date.setDate(date.getDate() + 1);
      var weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) added++;
    }
    var text;
    try {
      text = new Intl.DateTimeFormat(
        config.region === 'US' ? 'en-US' : 'en-GB',
        { weekday: 'short', day: 'numeric', month: 'short' }
      ).format(date);
    } catch (error) { return; }
    nodes.forEach(function (node) { node.textContent = 'Arrives by ' + text; });
  }());

  var previewConfigs = {};
  previewConfigs[config.storyId] = Promise.resolve(config);
  var previewRequestVersion = 0;
  function showJourneyPreview(book, child) {
    journeyPreviewShown = true;
    var version = ++previewRequestVersion;
    if (!previewConfigs[book.storyId]) {
      var url;
      try {
        url = new URL(book.url, window.location.href);
        if (url.origin !== window.location.origin || !/\/products\/[^/]+$/.test(url.pathname) || url.search || url.hash) throw new Error('Invalid story URL');
      } catch (error) { return Promise.reject(error); }
      var controller = new AbortController();
      var timer = setTimeout(function () { controller.abort(); }, 12000);
      previewConfigs[book.storyId] = fetch(url.href, { credentials: 'same-origin', signal: controller.signal }).then(function (response) {
        if (!response.ok) throw new Error('Story unavailable');
        return response.text();
      }).then(function (html) {
        var documentCopy = new DOMParser().parseFromString(html, 'text/html');
        var node = documentCopy.querySelector('[data-llsb-config]');
        if (!node) throw new Error('Preview unavailable');
        var raw = JSON.parse(node.textContent);
        if (raw.storyId !== book.storyId || raw.region !== config.region || raw.product.handle !== book.handle) throw new Error('Preview does not match the selected book');
        var result = applyStorybookPreview(raw, raw.storybookPreview);
        if (result.storybookPreviewUnavailable) throw new Error('Preview unavailable');
        result.spreads = storybookViewerSpreads(result.spreads);
        clearTimeout(timer); return result;
      }).catch(function (error) { clearTimeout(timer); delete previewConfigs[book.storyId]; throw error; });
    }
    return previewConfigs[book.storyId].then(function (nextConfig) {
      if (version !== previewRequestVersion) throw new Error('Preview superseded');
      viewConfig = nextConfig; viewChild = child;
      preview.textContent = ''; spreadEls = []; currentSpreadIndex = 0;
      renderSpreads(); applyPreview(); syncBookViewportHeight(); refreshText();
      if (bookViewportObserver) { bookViewportObserver.disconnect(); bookViewportObserver.observe(bookViewport); }
      if (document.fonts && document.fonts.ready) return document.fonts.ready.then(function () { if (version === previewRequestVersion) { syncBookViewportHeight(); refreshText(); } });
    });
  }

  /* ================= boot ================= */

  renderSpreads();
  syncBookViewportHeight();
  /* A placeholder label fitted in a fallback face is refitted in its own. */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refreshZoneIdleText);

  if (bookViewport && typeof ResizeObserver !== 'undefined') {
    var lastBookTrackWidth = 0;
    var bookViewportObserver = new ResizeObserver(function () {
      var trackWidth = bookViewport.clientWidth;
      if (!trackWidth || trackWidth === lastBookTrackWidth) return;
      lastBookTrackWidth = trackWidth;
      syncBookViewportHeight();
      refreshText();
      // The viewer just appeared (or resized): warm the page on show and the next one.
      warmSpread(currentSpreadIndex);
      warmSpread(currentSpreadIndex + 1);
    });
    bookViewportObserver.observe(bookViewport);
  }

  try {
    var saved = JSON.parse(localStorage.getItem(storeKey) || 'null');
    /* Only the name is restored. The uploaded File never survives a reload
       (not JSON-serialisable) and the demo character is not a choice, so
       older drafts (the retired selection / sample fields, or the
       pre-photo-only mode record) contribute their name and nothing else;
       step 1 reopens on the upload control. */
    if (saved && typeof saved.name === 'string' && saved.name) {
      state.name = saved.name;
      nameInput.value = state.name;
      refreshText();
    }
  } catch (e) { /* fresh start */ }
  /* demo character (or idle zones when this story ships no sample set) */
  applyPreview();
  showGallerySlide(selectedPhotoId || 'live');

  /* Homepage hero handoff: the hero's "Who's the story for?" form arrives as
     ?child=<name> (the no-JS path) and, when its script ran, the same name in
     sessionStorage. The stored copy is fresh intent typed seconds ago, so it
     wins even over a draft's saved name — and is consumed here so a
     deliberate clear on this page never resurrects it on reload. The query
     param survives reloads in the address bar, so it only fills silence: a
     draft name always beats it. Placed above syncButtons() so the
     unconditional call below validates and persists the prefill exactly like
     a typed name. */
  try {
    var heroName = '';
    var heroNameFresh = false;
    try {
      heroName = window.sessionStorage.getItem('ll:heroChildName') || '';
      if (heroName) {
        heroNameFresh = true;
        window.sessionStorage.removeItem('ll:heroChildName');
      }
    } catch (e) { /* storage unavailable; fall through to the query param */ }
    if (!heroName) {
      heroName = new URLSearchParams(window.location.search).get('child') || '';
    }
    heroName = heroName.trim().slice(0, MAX_NAME_LENGTH);
    if (heroName && (heroNameFresh || !state.name)) {
      state.name = heroName;
      nameInput.value = heroName;
      refreshText();
    }
  } catch (e) { /* the handoff never blocks boot */ }

  /* Arriving from a bundle page or the Books shelf: ?bundle= preselects that
     option, and a fresh handoff record (a bundle page's "one hardcover
     story" card, sent once the child was ready there) refills the child —
     photo marked ready without re-running the gate, it passed seconds ago —
     unless the shopper has already started here. */
  var wantedOffer = (booksOffers && booksOffers.requestedBundleId) ? booksOffers.requestedBundleId() : '';
  if (wantedOffer && bundleById(wantedOffer)) selectOffer(wantedOffer);
  if (booksOffers && booksOffers.createHandoffStore && booksOffers.indexedDbHandoffDriver) {
    try {
      booksOffers.createHandoffStore(booksOffers.indexedDbHandoffDriver()).take().then(function (record) {
        if (!record || !record.bundleId || !booksOffers.validHandoff(record, record.bundleId)) return;
        if (state.photoFile) return;
        if (record.name && !state.name) { state.name = record.name; nameInput.value = record.name; refreshText(); }
        if (photoConsentInput && record.consent === true) photoConsentInput.checked = true;
        if (!wantedOffer && bundleById(record.bundleId)) selectOffer(record.bundleId);
        if (!record.photo) { syncButtons(); return; }
        var reader = new FileReader();
        reader.onload = function () {
          var probe = new Image();
          probe.onload = function () {
            if (state.photoFile) return;
            acceptPhoto(record.photo, String(reader.result || ''), probe.naturalWidth, probe.naturalHeight, null);
            syncButtons();
          };
          probe.src = String(reader.result || '');
        };
        reader.readAsDataURL(record.photo);
      }).catch(function () {});
    } catch (e) { /* no IndexedDB: the page starts empty */ }
  }

  if (booksOffers && booksOffers.bindChildSession) {
    childSession = booksOffers.bindChildSession({
      inputs: [nameInput, photoInput, photoConsentInput], read: primaryChild, restoreAllowed: !heroName && !wantedOffer,
      restore: function (record, data, width, height) {
        state.name = record.name; nameInput.value = record.name;
        photoConsentInput.checked = true;
        acceptPhoto(record.photo, data, width, height, null);
        refreshText(); syncButtons();
      }
    });
  }

  if (guidedEnabled) {
    journey = window.LLStorybookJourney.mount(root, {
      config: config, offer: function () { return state.offer; }, select: selectOffer,
      bundle: function () { return configurator; }, primary: primaryChild, guidePrimary: guideToMissing,
      busy: function () { return addingNow || Boolean(pendingStorybookAdd) || Boolean(configurator && configurator.busy()); },
      addLabel: function (plain) { return ctaLabelFor(currentBundle(), plain); },
      buttons: function () { return [root.querySelector('[data-llsb-add]'), document.querySelector('[data-llsb-add-sticky]')].filter(Boolean); },
      // The journey's package sheet presses the bar's button for the row chosen.
      press: onCtaPress,
      showPreview: showJourneyPreview,
      updatePreviewChild: function (child) { viewChild = child; refreshText(); },
      dedication: {
        supported: function () { return CUSTOM_DEDICATION_STORIES.indexOf(config.storyId) !== -1 && Boolean(ownGreeting()); },
        value: function () { return state.dedication; },
        // The story's own greeting with the child's name, for the editor's placeholder.
        standard: standardDedication,
        error: dedicationError,
        lines: dedicationLines,
        sets: Boolean(config.setDedication),
        maxChars: DEDICATION_MAX_CHARS,
        maxLines: DEDICATION_MAX_LINES,
        // Saving our own words unchanged keeps ours: nothing extra is sent.
        set: function (value) {
          if (dedicationError(value)) return state.dedication;
          var text = normaliseDedication(value);
          state.dedication = text === normaliseDedication(standardDedication()) ? '' : text;
          refreshText();
          return state.dedication;
        },
        active: function (on) {
          if (dedicationActive === Boolean(on)) return;
          dedicationActive = Boolean(on);
          refreshText();
        }
      },
      cancelPreview: function () { previewRequestVersion++; },
      previewVisibility: function (colouring) { galleryStageLive.hidden = colouring; galleryStageLive.inert = colouring; galleryStageLive.setAttribute('aria-hidden', colouring ? 'true' : 'false'); },
    });
    if (journey) journey.start();
  }
  if (!journey) {
    // The section renders the guided layout for the first paint; without the
    // journey the page falls back to the plain product layout.
    root.classList.remove('llsb--guided', 'llsb--pdp-offers', 'llsj-lead-pending');
    var serverLead = root.querySelector('[data-llsj-lead]');
    if (serverLead) serverLead.remove();
  }

  syncButtons();

  /* A successful add navigates to the basket without restoring the button,
     because the page is on its way out. Pressing Back then hands the phone a
     bfcache copy of this page frozen mid-add: "Adding…", permanently
     disabled, because nothing re-runs on a bfcache restore. Reset the
     transient add state whenever we come back that way. The labels come from
     the buttons' own boot-time text, so region wording and the sold-out case
     restore themselves without being duplicated here. Captured here, above
     the reconcile call below, so a pending add's "Checking basket…" text is
     never mistaken for the idle label. */
  var idleAddLabels = [];
  /* document-scoped: this runs at boot, after the ctabar reparent above, so
     [data-llsb-add-sticky] is already outside root (task 11c item 1). */
  document.querySelectorAll('[data-llsb-add], [data-llsb-add-sticky]').forEach(function (button) {
    idleAddLabels.push({ button: button, label: button.textContent });
  });

  if (pendingStorybookAdd) reconcilePendingStorybookAdd();

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    addingNow = false;
    idleAddLabels.forEach(function (entry) { entry.button.textContent = entry.label; });
    root.removeAttribute('data-llsb-add-state');
    pendingStorybookAdd = readStorybookPendingAdd(storybookAddStorage, pendingStorybookAddKey);
    syncButtons();
    if (pendingStorybookAdd) reconcilePendingStorybookAdd();
  });
})();
